import CompanyOptionLabel from "../CompanyOptionLabel";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hasGmailScope } from "../../utils/gmailScopes";
import DOMPurify from "dompurify";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AltRouteRoundedIcon from "@mui/icons-material/AltRouteRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { DeleteOutlineRounded as DeleteOutlineRoundedIcon } from "../icons/FigmaControlIcons";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import FormatAlignLeftRoundedIcon from "@mui/icons-material/FormatAlignLeftRounded";
import FormatAlignCenterRoundedIcon from "@mui/icons-material/FormatAlignCenterRounded";
import FormatAlignRightRoundedIcon from "@mui/icons-material/FormatAlignRightRounded";
import FormatAlignJustifyRoundedIcon from "@mui/icons-material/FormatAlignJustifyRounded";
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatColorTextRoundedIcon from "@mui/icons-material/FormatColorTextRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { SearchRounded as SearchRoundedIcon } from "../icons/FigmaControlIcons";
import TitleRoundedIcon from "@mui/icons-material/TitleRounded";
import { SequenceActionIcon } from "../ActionButtonIcons";
import {
  addSequenceEnrollments,
  createDefaultSequenceSteps,
  createSequence,
  fetchSequences,
  getSequenceEntityLabel,
  getSequenceCampaignType,
  LEGACY_CANDIDATE_SEQUENCE_BODY,
  normalizeSequenceEntityType,
  normalizeSequenceItem,
  normalizeSequenceString,
  normalizeSequenceText,
  sendSequenceTestEmail,
  STRUCTURED_RECRUITING_SEQUENCE_BODY,
  updateSequence,
} from "../../utils/sequences";
import {
  getDistinctJobHiringManagerName,
  sanitizeJobCompanyContactFields,
} from "../../utils/recruiterOwnership";
import { fetchCompanyContactOptions } from "../../utils/hiringManagers";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";

const PURPLE = "#7C3AED";
const BLUE = "#3451F4";
const BORDER = "#E5E7EB";
const MUTED = "#6B7280";
const DEFAULT_STEP_TIME = "09:00 AM";
const DEFAULT_STEP_TIMEZONE = "+00:00 Western European Time - Lisbon, London";
const SCHEDULE_TIME_SLOTS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 ? "30" : "00";
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 || 12;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${period}`;
});
const SEQUENCE_ACTION_BUTTON_HEIGHT = 40;
const RICH_TEXT_COLORS = ["#1F2937", "#3451F4", "#7C3AED", "#DC2626", "#D97706", "#059669"];
const SEQUENCE_OUTLINE_HOVER_SX = {
  color: "#9C4FFF !important",
  background: "#FFFFFF !important",
  backgroundColor: "#FFFFFF !important",
  border: "1px solid #9C4FFF !important",
  borderColor: "#9C4FFF !important",
  boxShadow: "none !important",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: `${PURPLE} !important`,
  },
  "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg, & .MuiButton-startIcon svg path, & .MuiButton-endIcon svg path": {
    color: `${PURPLE} !important`,
    fill: "currentColor !important",
  },
};
const GMAIL_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
const API_BASE_RAW = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const API_BASE_ROOT = API_BASE_RAW.endsWith("/api")
  ? API_BASE_RAW.replace(/\/api\/?$/, "")
  : API_BASE_RAW;
const API_ROOT = API_BASE_RAW.endsWith("/api")
  ? API_BASE_RAW
  : `${API_BASE_RAW}/api`;

const INITIAL_AUDIENCE_PREPARATION_STATE = {
  status: "idle",
  message: "",
  totalFields: 0,
  resolvedFields: 0,
  remainingFields: [],
  collectedFields: [],
};

function shouldStartWithBlankCompany(entityType = "candidate") {
  return normalizeSequenceEntityType(entityType) === "candidate";
}

function shouldStartWithBlankJob(entityType = "candidate") {
  const normalized = normalizeSequenceEntityType(entityType);
  return normalized === "candidate" || normalized === "lead";
}

function buildInitialCampaignSteps(entityType = "candidate", defaults = {}) {
  const template = createDefaultSequenceSteps(entityType)[0] || {};

  return [
    {
      ...template,
      id: "step-1",
      channel: "email",
      title: "Step 1",
      delayValue: 0,
      delayUnit: "minutes",
      scheduleType: "immediately",
      scheduledDate: "",
      scheduledTime: DEFAULT_STEP_TIME,
      timezoneLabel: DEFAULT_STEP_TIMEZONE,
      senderName: normalizeSequenceString(defaults.fromName || template.senderName),
      senderEmail: normalizeSequenceString(defaults.fromEmail || template.senderEmail),
    },
  ];
}

function buildFollowUpSubject(subject = "") {
  const normalized = normalizeSequenceString(subject);
  if (!normalized) return "Following up";
  if (/^(following up|follow-?up|reminder)\s*[-:]/i.test(normalized)) return normalized;
  return `Following up - ${normalized}`;
}

function buildDefaultFollowUpBody(entityType = "candidate") {
  const normalized = normalizeSequenceEntityType(entityType);

  if (normalized === "candidate") {
    return `Hi {{first_name}},

I wanted to follow up on my earlier note about {{job_title}} at {{company_name}}. Your background stood out to me, and I thought it may still be worth a quick conversation.

Would you be open to a brief chat this week?

Best,
{{sender_name}}`;
  }

  if (normalized === "job") {
    return `Hi {{first_name}},

Following up on my earlier email regarding {{job_title}}. I wanted to check whether this role is still a priority and whether you are the right person to connect with about recruiting support.

If helpful, I can share how we could assist on the search.

Best,
{{sender_name}}`;
  }

  if (normalized === "lead") {
    return `Hi {{first_name}},

Following up on my earlier note. I wanted to reconnect and see whether this is something you would be open to discussing further.

If you are not the right contact, I would appreciate being pointed to the best person on your team.

Best,
{{sender_name}}`;
  }

  if (normalized === "client") {
    return `Hi {{first_name}},

Following up on my earlier email. I wanted to check whether it makes sense to continue the conversation around supporting {{company_name}} with recruiting help.

If there is someone else on your team I should reach out to, I would appreciate the direction.

Best,
{{sender_name}}`;
  }

  return `Hi {{first_name}},

Following up on my earlier note in case this is still relevant.

Would you be open to a quick conversation?

Best,
{{sender_name}}`;
}

function createFollowUpStep(previousStep = {}, nextIndex = 1, defaults = {}) {
  return {
    ...previousStep,
    channel: "email",
    title: `Step ${nextIndex + 1}`,
    subject: buildFollowUpSubject(previousStep.subject),
    body: buildDefaultFollowUpBody(defaults.entityType),
    delayValue: 1,
    delayUnit: "days",
    scheduleType: "next-business-day",
    scheduledDate: previousStep.scheduledDate || "",
    scheduledTime: previousStep.scheduledTime || DEFAULT_STEP_TIME,
    timezoneLabel: previousStep.timezoneLabel || DEFAULT_STEP_TIMEZONE,
    senderName: normalizeSequenceString(previousStep.senderName || defaults.fromName),
    senderEmail: normalizeSequenceString(previousStep.senderEmail || defaults.fromEmail),
  };
}

function getSequenceAiRecipientLabel(entityType = "candidate") {
  const normalized = normalizeSequenceEntityType(entityType);
  if (normalized === "candidate") return "candidate";
  if (normalized === "job") return "hiring manager or job stakeholder";
  if (normalized === "lead") return "lead contact";
  if (normalized === "client") return "company contact or decision maker";
  return "contact";
}

function getSequenceAiFollowUpInstructions(entityType = "candidate") {
  const normalized = normalizeSequenceEntityType(entityType);

  if (normalized === "candidate") {
    return `Write a genuine follow-up email about the opportunity. Briefly reference the earlier outreach, restate one concrete reason the role may fit, and end with a light CTA for a quick reply or short chat.`;
  }

  if (normalized === "job") {
    return `Write a concise follow-up email about the open role and recruiting support. Briefly reference the earlier outreach, keep the value clear, and ask whether they are the right owner for the role or open to a quick conversation.`;
  }

  if (normalized === "lead") {
    return `Write a concise follow-up email for a lead. Briefly reference the earlier outreach, keep it consultative, and ask whether they are the right contact or open to a short discussion.`;
  }

  if (normalized === "client") {
    return `Write a concise follow-up email for a company contact or decision maker. Briefly reference the earlier outreach, keep it helpful and credible, and ask for a quick reply or the best person to speak with.`;
  }

  return `Write a concise follow-up email that references the earlier outreach and ends with a simple next-step CTA.`;
}

function truncateAiComposeContext(value = "", maxLength = 1600) {
  const normalized = String(value || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  if (!normalized) return "";
  if (!Number.isFinite(maxLength) || maxLength <= 0 || normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

function buildDefaultRelatedSelections(items = []) {
  const seen = new Set();
  const selections = [];

  const pushSelection = (label, kind) => {
    const normalized = normalizeSequenceString(label, 120);
    if (!normalized) return;
    const key = `${kind}:${normalized.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    selections.push({ id: key, label: normalized, kind });
  };

  (Array.isArray(items) ? items : []).forEach((item) => {
    pushSelection(item.company, "company");
    pushSelection(item.title, "job");
    pushSelection(item.name, "lead");
    pushSelection(item.owner, "team");
  });

  return selections.slice(0, 6);
}

function getRelatedChipSx(kind = "custom") {
  const tones = {
    company: { color: "#F97316", backgroundColor: "#FFF7ED" },
    job: { color: "#3451F4", backgroundColor: "#EFF4FF" },
    lead: { color: "#0F766E", backgroundColor: "#ECFDF5" },
    team: { color: "#7C3AED", backgroundColor: "#F5F3FF" },
    custom: { color: "#475467", backgroundColor: "#F8FAFC" },
  };
  const tone = tones[kind] || tones.custom;

  return {
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: 12,
    color: tone.color,
    backgroundColor: tone.backgroundColor,
    "& .MuiChip-deleteIcon": {
      color: tone.color,
      fontSize: 16,
    },
  };
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#D0D5DD" },
    "&.Mui-focused fieldset": { borderColor: "#A78BFA" },
  },
};

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    ""
  );
}

function authHeaders(includeJson = false) {
  const token = getToken();
  return {
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "x-access-token": token } : {}),
  };
}

function apiUrl(path) {
  return `${API_ROOT}${path.startsWith("/") ? path : `/${path}`}`;
}

function rootApiUrl(path) {
  return `${API_BASE_ROOT}${path.startsWith("/") ? path : `/${path}`}`;
}

async function readResponseError(response, fallbackMessage) {
  const text = await response.text().catch(() => "");
  const normalizedText = normalizeSequenceString(text);
  if (!normalizedText) return fallbackMessage;

  try {
    const payload = JSON.parse(normalizedText);
    const payloadMessage =
      payload && typeof payload === "object"
        ? normalizeSequenceString(payload.error || payload.message)
        : "";
    return payloadMessage || normalizedText;
  } catch {
    return normalizedText;
  }
}

function renderGmailPopupStatus(popup, title, message) {
  if (!popup || popup.closed) return;

  try {
    popup.document.title = title;
    popup.document.body.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 24px; line-height: 1.5;">
        <h2 style="margin: 0 0 12px; font-size: 18px;">${title}</h2>
        <p style="margin: 0; white-space: pre-line;">${String(message || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>
      </div>
    `;
  } catch {
    // Ignore popup rendering errors.
  }
}

function safeDecodeJwt(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function senderDefaults(user) {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    "";
  const decoded = safeDecodeJwt(token);
  return {
    fromName:
      user?.name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
      decoded?.name ||
      [decoded?.first_name, decoded?.last_name].filter(Boolean).join(" ").trim() ||
      "",
    fromEmail: user?.email || decoded?.email || "",
  };
}

function matchesNormalizedValue(currentValue, expectedValue) {
  return (
    normalizeSequenceString(currentValue, 255).toLowerCase() ===
    normalizeSequenceString(expectedValue, 255).toLowerCase()
  );
}

function normalizeLookupValue(value = "") {
  return normalizeSequenceString(value, 255).toLowerCase();
}

function buildCompanyLookupOption(raw = {}) {
  const safeRaw = raw && typeof raw === "object" ? raw : {};
  const rawJobsCount = safeRaw.jobs_count ?? safeRaw.jobsCount;
  const jobsCount = rawJobsCount == null || rawJobsCount === "" ? null : Number(rawJobsCount);
  const companyId = Number(safeRaw.company_id ?? safeRaw.companyId ?? safeRaw.id);
  const companyName = normalizeSequenceString(
    safeRaw.company_name ?? safeRaw.companyName ?? safeRaw.name ?? safeRaw.company,
    160
  );
  if (!companyName) return null;

  return {
    company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
    company_name: companyName,
    ...(jobsCount != null && Number.isFinite(jobsCount) && jobsCount >= 0
      ? { jobs_count: jobsCount }
      : {}),
    company_logo: normalizeSequenceString(
      safeRaw.company_logo ?? safeRaw.companyLogo ?? safeRaw.logo,
      2048
    ),
    website: normalizeSequenceString(safeRaw.company_website ?? safeRaw.companyWebsite ?? safeRaw.website, 255),
    industry: normalizeSequenceString(safeRaw.industry, 160),
    size_range: normalizeSequenceString(safeRaw.size_range ?? safeRaw.company_size ?? safeRaw.companySize, 120),
    location: normalizeSequenceString(safeRaw.location, 160),
    status: normalizeSequenceString(safeRaw.status, 80),
    source: normalizeSequenceString(safeRaw.source, 80),
  };
}

function buildJobLookupOption(raw = {}) {
  const safeRaw = raw && typeof raw === "object" ? raw : {};
  const jobId = Number(safeRaw.job_id ?? safeRaw.jobId ?? safeRaw.id);
  const companyId = Number(safeRaw.company_id ?? safeRaw.companyId);
  const jobTitle = normalizeSequenceString(
    safeRaw.job_title ?? safeRaw.jobTitle ?? safeRaw.title ?? safeRaw.name,
    160
  );
  if (!jobTitle) return null;

  return {
    id: Number.isFinite(jobId) && jobId > 0 ? jobId : null,
    job_title: jobTitle,
    company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
    company_name: normalizeSequenceString(
      safeRaw.company_name ??
        safeRaw.companyName ??
        safeRaw.linked_company_name ??
        safeRaw.linkedCompanyName ??
        safeRaw.company,
      160
    ),
    company_logo: normalizeSequenceString(
      safeRaw.company_logo ?? safeRaw.companyLogo ?? safeRaw.logo,
      2048
    ),
    location: normalizeSequenceString(safeRaw.location, 160),
    employment: normalizeSequenceString(safeRaw.employment ?? safeRaw.employment_type, 120),
    work_arrangement: normalizeSequenceString(
      safeRaw.work_arrangement ?? safeRaw.workArrangement,
      120
    ),
    status: normalizeSequenceString(safeRaw.status, 80),
    source: normalizeSequenceString(safeRaw.source, 80),
    owner: normalizeSequenceString(safeRaw.owner ?? safeRaw.owner_name ?? safeRaw.ownerName, 160),
  };
}

function getCompanyLookupKey(option = {}) {
  if (option?.company_id != null) return `company:${option.company_id}`;
  return `company-name:${normalizeLookupValue(option?.company_name || "")}`;
}

function getJobLookupKey(option = {}) {
  if (option?.id != null) return `job:${option.id}`;
  return `job-title:${normalizeLookupValue(option?.job_title || "")}::company:${normalizeLookupValue(
    option?.company_name || ""
  )}`;
}

function jobBelongsToCompany(job = {}, company = {}) {
  const companyId = Number(company?.company_id ?? company?.id ?? 0);
  const jobCompanyId = Number(job?.company_id ?? job?.companyId ?? 0);
  const companyName = normalizeLookupValue(company?.company_name || company?.name || "");
  const matchesCompanyId =
    Number.isFinite(companyId) && companyId > 0 &&
    Number.isFinite(jobCompanyId) && jobCompanyId === companyId;
  const matchesCompanyName =
    Boolean(companyName) &&
    normalizeLookupValue(job?.company_name || job?.company || "") === companyName;

  // Imported records can retain an older company_id even when the linked
  // company name is correct. Accept either stable identifier, or an exact
  // company-name fallback, while the API performs the primary ID filter.
  return matchesCompanyId || matchesCompanyName;
}

function mergeLookupOptions(options = [], nextOptions = [], getKey = () => "") {
  const merged = new Map();

  [...(Array.isArray(options) ? options : []), ...(Array.isArray(nextOptions) ? nextOptions : [])]
    .filter(Boolean)
    .forEach((option) => {
      const key = String(getKey(option) || "").trim();
      if (!key) return;
      merged.set(key, {
        ...(merged.get(key) || {}),
        ...option,
      });
    });

  return Array.from(merged.values()).sort((a, b) =>
    String(a?.company_name || a?.job_title || "").localeCompare(
      String(b?.company_name || b?.job_title || "")
    )
  );
}

function findLookupOptionByLabel(options = [], label = "", key = "") {
  const normalizedLabel = normalizeLookupValue(label);
  if (!normalizedLabel || !key) return null;

  const exactMatch = options.find(
    (option) => normalizeLookupValue(option?.[key] || "") === normalizedLabel
  );
  if (exactMatch) return exactMatch;

  return (
    options.find((option) => {
      const optionValue = normalizeLookupValue(option?.[key] || "");
      return optionValue && optionValue.includes(normalizedLabel);
    }) || null
  );
}

const STRUCTURED_TEMPLATE_SECTION_HEADINGS = new Set([
  "Who We Are",
  "Responsibilities:",
  "Qualifications:",
  "Technical Expertise:",
  "Company Snapshot:",
  "Key Products / Services:",
  "Role Overview:",
  "Role Details:",
  "Why Join Us?",
]);

function cleanupStructuredRecruitingTemplateOutput(value = "") {
  const lines = String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");

  const cleanedLines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = String(line || "").trim();

    if (STRUCTURED_TEMPLATE_SECTION_HEADINGS.has(trimmedLine)) {
      let nextContentIndex = index + 1;
      while (nextContentIndex < lines.length && !String(lines[nextContentIndex] || "").trim()) {
        nextContentIndex += 1;
      }

      if (
        nextContentIndex >= lines.length ||
        STRUCTURED_TEMPLATE_SECTION_HEADINGS.has(
          String(lines[nextContentIndex] || "").trim()
        )
      ) {
        index = nextContentIndex - 1;
        continue;
      }
    }

    cleanedLines.push(line);
  }

  return cleanedLines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function applyPreviewTemplateTokens(value, context = {}) {
  const template = String(value || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!template.trim()) return "";

  const rendered = template.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_, token) => {
    const resolved = context[String(token || "").toLowerCase()];
    return resolved == null ? "" : String(resolved);
  });

  return cleanupStructuredRecruitingTemplateOutput(rendered);
}

function buildPreviewTemplateContext(sequence = {}, recipient = {}, step = {}, extraVars = {}) {
  const companyDetails =
    sequence.companyDetails && typeof sequence.companyDetails === "object"
      ? sequence.companyDetails
      : {};
  const leadInfo =
    sequence.leadDetails && typeof sequence.leadDetails === "object"
      ? sequence.leadDetails
      : {};
  const jobInfo =
    sequence.jobDetails && typeof sequence.jobDetails === "object"
      ? sequence.jobDetails
      : {};

  // For non-candidate entity types the item "name" holds the entity name
  // (company or job title) while the PERSON name is in "title".
  const entityType = String(recipient.entityType || sequence.entityType || "candidate").toLowerCase();
  const isPersonName = entityType === "candidate";

  // Person name: for candidates -> name; for jobs/clients/leads -> title (contact / hiring manager)
  const personFullName = normalizeSequenceString(
    isPersonName
      ? (recipient.name || recipient.displayName || recipient.full_name)
      : (recipient.title || recipient.displayName || recipient.name),
    255
  );
  const personParts = personFullName.split(/\s+/).filter(Boolean);
  const personFirst = personParts[0] || "";
  const personLast = personParts.slice(1).join(" ");

  const senderName = normalizeSequenceString(
    step.senderName || sequence.fromName || sequence.ownerName || recipient.owner,
    255
  );
  const senderEmail = normalizeSequenceString(
    step.senderEmail || sequence.fromEmail,
    255
  );
  const companyName = normalizeSequenceString(
    sequence.companyName || companyDetails.company || recipient.company,
    255
  );
  const jobTitle = normalizeSequenceString(
    sequence.selectedJobName || companyDetails.job || (isPersonName ? recipient.title : recipient.name),
    255
  );
  const contactName = normalizeSequenceString(companyDetails.contact, 255) || personFullName || senderName;

  return {
    first_name: personFirst,
    last_name: personLast,
    full_name: personFullName,
    candidate_name: personFullName,
    title: normalizeSequenceString(recipient.title, 255),
    job_title: jobTitle,
    company_name: companyName,
    company: companyName,
    location: normalizeSequenceString(recipient.location, 255),
    sender_name: senderName,
    sender_email: senderEmail,
    owner_name: normalizeSequenceString(sequence.ownerName, 255),
    contact_name: contactName,
    sequence_name: normalizeSequenceString(sequence.name, 255),
    email: normalizeSequenceString(recipient.email, 255),
    to_email: normalizeSequenceString(recipient.email, 255),
    company_website: normalizeSequenceString(
      companyDetails.website || leadInfo.company_website || jobInfo.companyWebsite,
      255
    ),
    company_industry: normalizeSequenceString(companyDetails.industry || leadInfo.industry_sector, 160),
    company_location: normalizeSequenceString(companyDetails.location || leadInfo.location, 255),
    company_status: normalizeSequenceString(companyDetails.status, 80),
    company_size: normalizeSequenceString(
      companyDetails.sizeRange || leadInfo.company_size || jobInfo.companySize,
      120
    ),
    company_description: normalizeSequenceText(leadInfo.full_company_description, 8000),
    company_founded_year: normalizeSequenceString(leadInfo.founded_year, 80),
    company_ownership: normalizeSequenceString(leadInfo.ownership_type, 255),
    company_career_website: normalizeSequenceString(
      leadInfo.career_site || jobInfo.careerWebsite,
      255
    ),
    company_mission_vision: normalizeSequenceText(leadInfo.mission_and_vision, 8000),
    company_key_products: normalizeSequenceText(leadInfo.key_products_services, 8000),
    job_location: normalizeSequenceString(companyDetails.jobLocation || jobInfo.location, 255),
    job_source: normalizeSequenceString(companyDetails.source, 80),
    job_status: normalizeSequenceString(companyDetails.jobStatus, 80),
    job_employment: normalizeSequenceString(companyDetails.employment || jobInfo.employmentType, 120),
    job_work_arrangement: normalizeSequenceString(
      companyDetails.workArrangement || jobInfo.workArrangement,
      120
    ),
    job_description: normalizeSequenceText(jobInfo.jobDescription, 8000),
    job_role_overview: normalizeSequenceText(buildRoleOverviewText(jobInfo), 1200),
    job_post_link: normalizeUrlValue(
      jobInfo.jobDetailUrl || jobInfo.job_detail_url || jobInfo.jobLink
    ),
    job_detail_link: normalizeUrlValue(
      jobInfo.jobDetailUrl || jobInfo.job_detail_url || jobInfo.jobLink
    ),
    job_responsibilities: normalizeSequenceText(jobInfo.responsibilities, 8000),
    job_qualifications: normalizeSequenceText(jobInfo.qualifications, 8000),
    job_skills: normalizeSequenceString(
      [].concat(jobInfo.hardSkills || [], jobInfo.softSkills || []).filter(Boolean).join(", "), 2000
    ),
    job_salary: normalizeSequenceString(jobInfo.salaryRange, 255),
    job_industry: normalizeSequenceString(jobInfo.industry || companyDetails.industry, 160),
    ...extraVars,
  };
}

function normalizeTextArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeSequenceString(item, 255))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => normalizeSequenceString(item, 255))
          .filter(Boolean);
      }
    } catch {}

    return trimmed
      .split(/\r?\n|,/)
      .map((item) => normalizeSequenceString(item, 255))
      .filter(Boolean);
  }

  return [];
}

function truncateSequenceText(value = "", maxLength = 300) {
  const normalized = normalizeSequenceString(value, 20000);
  const limit = Number(maxLength || 0);
  if (!normalized || !Number.isFinite(limit) || limit <= 0) return "";
  if (normalized.length <= limit) return normalized;
  if (limit <= 3) return normalized.slice(0, limit);
  return `${normalized.slice(0, limit - 3).trimEnd()}...`;
}

function normalizeUrlValue(value = "") {
  return normalizeSequenceString(value, 2048);
}

function buildInternalJobDetailUrl(jobId, tab = "details") {
  const normalizedJobId = Number(jobId || 0);
  if (!Number.isFinite(normalizedJobId) || normalizedJobId <= 0) return "";
  if (typeof window === "undefined" || !window.location?.origin) return "";

  const nextUrl = new URL("/jobs", window.location.origin);
  nextUrl.searchParams.set("jobId", String(normalizedJobId));
  nextUrl.searchParams.set("tab", normalizeSequenceString(tab, 40) || "details");
  return nextUrl.toString();
}

function buildJobEmailContextPersistencePayload(job = {}) {
  const safeJob = job && typeof job === "object" ? job : {};
  const hardSkills = collectUniqueSequenceTexts(
    safeJob.hard_skills,
    safeJob.hardSkills,
    safeJob.skills,
    safeJob.tech_stack,
    safeJob.techStack,
    safeJob.tech_stack_tokens
  );
  const softSkills = collectUniqueSequenceTexts(
    safeJob.soft_skills,
    safeJob.softSkills
  );
  const companyWebsite = normalizeUrlValue(
    safeJob.company_website || safeJob.companyWebsite || safeJob.website
  );
  const careerWebsite = normalizeUrlValue(
    safeJob.company_career_website ||
      safeJob.companyCareerWebsite ||
      safeJob.career_website ||
      safeJob.careerWebsite
  );
  const jobLink = normalizeUrlValue(
    safeJob.post_link || safeJob.postLink || safeJob.job_url || safeJob.jobUrl || safeJob.url
  );

  return {
    job_title: normalizeSequenceString(
      safeJob.job_title || safeJob.jobTitle || safeJob.title,
      160
    ),
    description: normalizeSequenceText(
      safeJob.description || safeJob.job_description || safeJob.jobDescription,
      8000
    ),
    job_description: normalizeSequenceText(
      safeJob.job_description || safeJob.jobDescription || safeJob.description,
      8000
    ),
    responsibilities: normalizeSequenceText(safeJob.responsibilities, 8000),
    qualifications: normalizeSequenceText(safeJob.qualifications, 8000),
    hard_skills: hardSkills,
    soft_skills: softSkills,
    skills: hardSkills,
    tech_stack: hardSkills,
    salary_range: normalizeSequenceString(
      safeJob.salary_range || safeJob.salaryRange || safeJob.compensation,
      255
    ),
    compensation: normalizeSequenceString(
      safeJob.compensation || safeJob.salary_range || safeJob.salaryRange,
      255
    ),
    employment: normalizeSequenceString(
      safeJob.employment || safeJob.employment_type || safeJob.employmentType,
      120
    ),
    employment_type: normalizeSequenceString(
      safeJob.employment_type || safeJob.employmentType || safeJob.employment,
      120
    ),
    work_arrangement: normalizeSequenceString(
      safeJob.work_arrangement || safeJob.workArrangement,
      120
    ),
    industry: normalizeSequenceString(
      safeJob.industry || safeJob.company_industry,
      160
    ),
    company_industry: normalizeSequenceString(
      safeJob.company_industry || safeJob.industry,
      160
    ),
    company_size: normalizeSequenceString(
      safeJob.company_size || safeJob.companySize,
      120
    ),
    location: normalizeSequenceString(safeJob.location, 255),
    website: companyWebsite,
    company_website: companyWebsite,
    career_website: careerWebsite,
    company_career_website: careerWebsite,
    post_link: jobLink,
    job_url: jobLink,
  };
}

function collectUniqueSequenceTexts(...values) {
  const seen = new Set();
  const items = [];

  values.forEach((value) => {
    normalizeTextArray(value).forEach((item) => {
      const normalized = normalizeSequenceString(item, 255);
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) return;
      seen.add(key);
      items.push(normalized);
    });
  });

  return items;
}

function toDisplayDomain(value = "") {
  const raw = normalizeSequenceString(value, 255);
  if (!raw) return "";

  try {
    const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./i, "");
  } catch {
    return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
  }
}

function normalizeDocumentList(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((doc) => {
      if (!doc || typeof doc !== "object") return "";

      const name = normalizeSequenceString(
        doc.name || doc.original_name || doc.filename || "Document",
        255
      );
      const url = normalizeSequenceString(doc.url, 2048);
      return url ? `${name} (${url})` : name;
    })
    .filter(Boolean);
}

function buildSequenceCompanyProfile({
  companyProfile = {},
  leadInfo = {},
  companyDetails = {},
  jobDetails = {},
} = {}) {
  const safeCompanyProfile =
    companyProfile && typeof companyProfile === "object" ? companyProfile : {};
  const safeLeadInfo = leadInfo && typeof leadInfo === "object" ? leadInfo : {};
  const safeCompanyDetails =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const safeJobDetails = jobDetails && typeof jobDetails === "object" ? jobDetails : {};

  const companyWebsite = normalizeUrlValue(
    safeLeadInfo.company_website ||
      safeLeadInfo.website ||
      safeCompanyProfile.company_website ||
      safeCompanyProfile.website ||
      safeJobDetails.companyWebsite ||
      safeCompanyDetails.website
  );
  const careerSite = normalizeUrlValue(
    safeLeadInfo.career_site_url ||
      safeLeadInfo.career_site ||
      safeLeadInfo.career_website ||
      safeCompanyProfile.career_site ||
      safeCompanyProfile.career_website ||
      safeCompanyProfile.company_career_website ||
      safeJobDetails.careerWebsite
  );

  return {
    company_name: normalizeSequenceString(
      safeLeadInfo.company_name ||
        safeCompanyProfile.company_name ||
        safeCompanyProfile.name ||
        safeCompanyDetails.company ||
        safeJobDetails.companyName,
      160
    ),
    location: normalizeSequenceString(
      safeLeadInfo.location || safeCompanyProfile.location || safeCompanyDetails.location,
      255
    ),
    industry_sector: normalizeSequenceString(
      safeLeadInfo.industry_sector ||
        safeLeadInfo.industry ||
        safeCompanyProfile.industry_sector ||
        safeCompanyProfile.industry ||
        safeCompanyDetails.industry ||
        safeJobDetails.industry,
      160
    ),
    founded_year: normalizeSequenceString(
      safeLeadInfo.founded_year || safeCompanyProfile.founded_year,
      80
    ),
    work_policies: normalizeSequenceString(
      safeLeadInfo.work_policies || safeCompanyProfile.work_policies,
      255
    ),
    full_company_description: normalizeSequenceText(
      safeLeadInfo.full_company_description ||
        safeLeadInfo.company_description ||
        safeCompanyProfile.full_company_description ||
        safeCompanyProfile.company_description,
      8000
    ),
    mission_and_vision: normalizeSequenceText(
      safeLeadInfo.mission_and_vision || safeCompanyProfile.mission_and_vision,
      8000
    ),
    key_products_services: normalizeSequenceText(
      safeLeadInfo.key_products_services || safeCompanyProfile.key_products_services,
      8000
    ),
    unique_selling_points: normalizeSequenceText(
      safeLeadInfo.unique_selling_points || safeCompanyProfile.unique_selling_points,
      8000
    ),
    employee_perks: normalizeSequenceText(
      safeLeadInfo.employee_perks || safeCompanyProfile.employee_perks,
      8000
    ),
    achievements_awards: normalizeSequenceText(
      safeLeadInfo.achievements_awards || safeCompanyProfile.achievements_awards,
      8000
    ),
    recruitment_tools: collectUniqueSequenceTexts(
      safeLeadInfo.recruitment_tools,
      safeLeadInfo.technology_stack,
      safeCompanyProfile.recruitment_tools
    ),
    job_boards: collectUniqueSequenceTexts(
      safeLeadInfo.job_boards,
      safeCompanyProfile.job_boards
    ),
    company_website: companyWebsite,
    career_site: careerSite,
    company_link: normalizeUrlValue(
      safeLeadInfo.company_link || safeCompanyProfile.company_link || companyWebsite
    ),
    company_size: normalizeSequenceString(
      safeLeadInfo.company_size ||
        safeLeadInfo.size_range ||
        safeCompanyProfile.company_size ||
        safeCompanyProfile.size_range ||
        safeCompanyDetails.sizeRange ||
        safeJobDetails.companySize,
      120
    ),
    ownership_type: normalizeSequenceString(
      safeLeadInfo.ownership_type || safeCompanyProfile.ownership_type,
      255
    ),
    parent_company: normalizeSequenceString(
      safeLeadInfo.parent_company ||
        safeLeadInfo.parent_company_subsidiaries ||
        safeCompanyProfile.parent_company ||
        safeCompanyProfile.parent_company_subsidiaries,
      255
    ),
    job_board_rating: normalizeSequenceString(
      safeLeadInfo.job_board_rating ||
        safeLeadInfo.glassdoor_rating ||
        safeCompanyProfile.job_board_rating ||
        safeCompanyProfile.glassdoor_rating,
      50
    ),
    company_email: normalizeSequenceString(
      safeLeadInfo.company_email || safeCompanyProfile.company_email || safeCompanyProfile.email,
      255
    ),
    company_phone: normalizeSequenceString(
      safeLeadInfo.company_phone || safeCompanyProfile.company_phone || safeCompanyProfile.phone,
      50
    ),
    elevator_pitch: normalizeSequenceText(
      safeLeadInfo.elevator_pitch || safeCompanyProfile.elevator_pitch,
      2000
    ),
    status: normalizeSequenceString(
      safeCompanyProfile.status || safeCompanyDetails.status,
      80
    ),
    source: normalizeSequenceString(
      safeCompanyProfile.source || safeCompanyDetails.source,
      80
    ),
  };
}

function buildRoleOverviewText(jobDetails = {}) {
  const safeJobDetails = jobDetails && typeof jobDetails === "object" ? jobDetails : {};
  const baseOverview = truncateSequenceText(
    safeJobDetails.jobDescription ||
      safeJobDetails.roleOverview ||
      safeJobDetails.responsibilities,
    300
  );
  const readMoreUrl = normalizeUrlValue(
    safeJobDetails.jobDetailUrl ||
      safeJobDetails.job_detail_url ||
      safeJobDetails.jobLink ||
      safeJobDetails.postLink ||
      safeJobDetails.careerWebsite ||
      safeJobDetails.companyWebsite
  );

  if (!baseOverview) {
    return readMoreUrl ? `Read more: ${readMoreUrl}` : "";
  }

  return readMoreUrl ? `${baseOverview} Read more: ${readMoreUrl}` : baseOverview;
}

function buildCompanyEnrichmentPayload({
  companyDetails = {},
  companyProfile = {},
  jobDetails = {},
} = {}) {
  const safeCompanyDetails =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const safeCompanyProfile =
    companyProfile && typeof companyProfile === "object" ? companyProfile : {};
  const safeJobDetails = jobDetails && typeof jobDetails === "object" ? jobDetails : {};
  const companyName = normalizeSequenceString(
    safeCompanyDetails.company || safeCompanyProfile.company_name || safeJobDetails.companyName,
    160
  );
  const urls = Array.from(
    new Set(
      [
        safeCompanyProfile.company_website,
        safeCompanyProfile.company_link,
        safeCompanyProfile.career_site,
        safeCompanyDetails.website,
        safeJobDetails.companyWebsite,
        safeJobDetails.careerWebsite,
      ]
        .map((value) => normalizeUrlValue(value))
        .filter(Boolean)
    )
  ).slice(0, 3);

  return {
    company_name: companyName,
    urls,
    company_website: normalizeUrlValue(
      safeCompanyProfile.company_website || safeCompanyDetails.website || safeJobDetails.companyWebsite
    ),
    company_link: normalizeUrlValue(
      safeCompanyProfile.company_link || safeCompanyProfile.company_website || safeCompanyDetails.website
    ),
    career_site: normalizeUrlValue(
      safeCompanyProfile.career_site || safeJobDetails.careerWebsite
    ),
    company_location: normalizeSequenceString(
      safeCompanyProfile.location || safeCompanyDetails.location,
      255
    ),
    industry_sector: normalizeSequenceString(
      safeCompanyProfile.industry_sector || safeCompanyDetails.industry || safeJobDetails.industry,
      160
    ),
    company_size: normalizeSequenceString(
      safeCompanyProfile.company_size || safeCompanyDetails.sizeRange || safeJobDetails.companySize,
      120
    ),
    founded_year: normalizeSequenceString(safeCompanyProfile.founded_year, 80),
    ownership_type: normalizeSequenceString(safeCompanyProfile.ownership_type, 255),
    parent_company: normalizeSequenceString(safeCompanyProfile.parent_company, 255),
    work_policies: normalizeSequenceString(safeCompanyProfile.work_policies, 255),
    recruitment_tools: collectUniqueSequenceTexts(safeCompanyProfile.recruitment_tools),
    job_boards: collectUniqueSequenceTexts(safeCompanyProfile.job_boards),
    job_board_rating: normalizeSequenceString(safeCompanyProfile.job_board_rating, 50),
    company_email: normalizeSequenceString(safeCompanyProfile.company_email, 255),
    company_phone: normalizeSequenceString(safeCompanyProfile.company_phone, 50),
    elevator_pitch: normalizeSequenceText(safeCompanyProfile.elevator_pitch, 2000),
    full_company_description: normalizeSequenceText(
      safeCompanyProfile.full_company_description,
      8000
    ),
    mission_and_vision: normalizeSequenceText(
      safeCompanyProfile.mission_and_vision,
      8000
    ),
    key_products_services: normalizeSequenceText(
      safeCompanyProfile.key_products_services,
      8000
    ),
    unique_selling_points: normalizeSequenceText(
      safeCompanyProfile.unique_selling_points,
      8000
    ),
    employee_perks: normalizeSequenceText(safeCompanyProfile.employee_perks, 8000),
    achievements_awards: normalizeSequenceText(
      safeCompanyProfile.achievements_awards,
      8000
    ),
  };
}

function extractJsonObjectFromText(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const candidates = [];
  const fencedMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fencedMatch?.[1]) {
    candidates.push(fencedMatch[1].trim());
  }
  candidates.push(raw);

  const firstBrace = raw.indexOf("{");
  const lastBrace = raw.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.push(raw.slice(firstBrace, lastBrace + 1).trim());
  }

  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Ignore invalid JSON candidates.
    }
  }

  return null;
}

function buildJobEnrichmentPatch(parsed = {}) {
  const safeParsed = parsed && typeof parsed === "object" ? parsed : {};

  return {
    job_title: normalizeSequenceString(
      safeParsed.job_title || safeParsed.jobTitle || safeParsed.title,
      160
    ),
    description: normalizeSequenceText(
      safeParsed.job_description || safeParsed.jobDescription || safeParsed.description,
      8000
    ),
    job_description: normalizeSequenceText(
      safeParsed.job_description || safeParsed.jobDescription || safeParsed.description,
      8000
    ),
    responsibilities: normalizeSequenceText(safeParsed.responsibilities, 8000),
    qualifications: normalizeSequenceText(safeParsed.qualifications, 8000),
    hard_skills: collectUniqueSequenceTexts(
      safeParsed.hard_skills,
      safeParsed.hardSkills,
      safeParsed.skills,
      safeParsed.tech_stack,
      safeParsed.tech_stack_tokens
    ),
    soft_skills: collectUniqueSequenceTexts(
      safeParsed.soft_skills,
      safeParsed.softSkills
    ),
    salary_range: normalizeSequenceString(
      safeParsed.salary_range || safeParsed.salaryRange || safeParsed.compensation,
      255
    ),
    compensation: normalizeSequenceString(
      safeParsed.compensation || safeParsed.salary_range || safeParsed.salaryRange,
      255
    ),
    employment_type: normalizeSequenceString(
      safeParsed.employment_type || safeParsed.employmentType || safeParsed.employment,
      120
    ),
    employment: normalizeSequenceString(
      safeParsed.employment || safeParsed.employment_type || safeParsed.employmentType,
      120
    ),
    work_arrangement: normalizeSequenceString(
      safeParsed.work_arrangement || safeParsed.workArrangement,
      120
    ),
    industry: normalizeSequenceString(
      safeParsed.industry || safeParsed.company_industry,
      160
    ),
    company_size: normalizeSequenceString(
      safeParsed.company_size || safeParsed.companySize,
      120
    ),
    location: normalizeSequenceString(safeParsed.location, 255),
    company_website: normalizeUrlValue(
      safeParsed.company_website || safeParsed.companyWebsite || safeParsed.website
    ),
    company_career_website: normalizeUrlValue(
      safeParsed.company_career_website || safeParsed.companyCareerWebsite || safeParsed.career_website || safeParsed.careerWebsite
    ),
    career_website: normalizeUrlValue(
      safeParsed.company_career_website || safeParsed.companyCareerWebsite || safeParsed.career_website || safeParsed.careerWebsite
    ),
    post_link: normalizeUrlValue(
      safeParsed.post_link || safeParsed.postLink || safeParsed.job_url || safeParsed.jobUrl || safeParsed.url
    ),
    job_url: normalizeUrlValue(
      safeParsed.job_url || safeParsed.jobUrl || safeParsed.post_link || safeParsed.postLink || safeParsed.url
    ),
  };
}

function buildCompanyEnrichmentPatch(parsed = {}) {
  const safeParsed = parsed && typeof parsed === "object" ? parsed : {};

  return {
    company_name: normalizeSequenceString(
      safeParsed.company_name || safeParsed.companyName || safeParsed.name,
      160
    ),
    location: normalizeSequenceString(safeParsed.location || safeParsed.company_location, 255),
    industry_sector: normalizeSequenceString(
      safeParsed.industry_sector || safeParsed.industry,
      160
    ),
    founded_year: normalizeSequenceString(
      safeParsed.founded_year || safeParsed.founded,
      80
    ),
    work_policies: normalizeSequenceString(safeParsed.work_policies, 255),
    full_company_description: normalizeSequenceText(
      safeParsed.full_company_description || safeParsed.company_description,
      8000
    ),
    mission_and_vision: normalizeSequenceText(safeParsed.mission_and_vision, 8000),
    key_products_services: normalizeSequenceText(safeParsed.key_products_services, 8000),
    unique_selling_points: normalizeSequenceText(safeParsed.unique_selling_points, 8000),
    employee_perks: normalizeSequenceText(safeParsed.employee_perks, 8000),
    achievements_awards: normalizeSequenceText(safeParsed.achievements_awards, 8000),
    recruitment_tools: collectUniqueSequenceTexts(
      safeParsed.recruitment_tools,
      safeParsed.recruitmentTools
    ),
    job_boards: collectUniqueSequenceTexts(safeParsed.job_boards, safeParsed.jobBoards),
    company_website: normalizeUrlValue(
      safeParsed.company_website ||
        safeParsed.companyWebsite ||
        safeParsed.website ||
        safeParsed.company_url ||
        safeParsed.companyUrl
    ),
    career_site: normalizeUrlValue(
      safeParsed.career_site ||
        safeParsed.career_website ||
        safeParsed.company_career_website ||
        safeParsed.companyCareerWebsite
    ),
    company_link: normalizeUrlValue(
      safeParsed.company_link ||
        safeParsed.company_website ||
        safeParsed.website ||
        safeParsed.company_url ||
        safeParsed.companyUrl
    ),
    company_size: normalizeSequenceString(
      safeParsed.company_size || safeParsed.size_range || safeParsed.companySize,
      120
    ),
    ownership_type: normalizeSequenceString(safeParsed.ownership_type, 255),
    parent_company: normalizeSequenceString(
      safeParsed.parent_company || safeParsed.parent_company_subsidiaries,
      255
    ),
    job_board_rating: normalizeSequenceString(
      safeParsed.job_board_rating || safeParsed.glassdoor_rating,
      50
    ),
    company_email: normalizeSequenceString(
      safeParsed.company_email || safeParsed.email,
      255
    ),
    company_phone: normalizeSequenceString(
      safeParsed.company_phone || safeParsed.phone,
      50
    ),
    elevator_pitch: normalizeSequenceText(safeParsed.elevator_pitch, 2000),
  };
}

function buildCompanyPersistencePayload({
  companyName = "",
  companyProfile = {},
  companyDetails = {},
  ownerName = "",
} = {}) {
  const safeCompanyDetails =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const safeCompanyProfile = buildSequenceCompanyProfile({
    companyProfile,
    companyDetails: safeCompanyDetails,
  });
  const resolvedCompanyName = normalizeSequenceString(
    companyName || safeCompanyProfile.company_name || safeCompanyDetails.company,
    160
  );
  const resolvedOwnerName = normalizeSequenceString(ownerName, 160);

  return {
    name: resolvedCompanyName,
    website: safeCompanyProfile.company_website,
    company_website: safeCompanyProfile.company_website,
    company_link: safeCompanyProfile.company_link || safeCompanyProfile.company_website,
    career_site: safeCompanyProfile.career_site,
    career_website: safeCompanyProfile.career_site,
    location: safeCompanyProfile.location,
    company_location: safeCompanyProfile.location,
    company_description: safeCompanyProfile.full_company_description,
    full_company_description: safeCompanyProfile.full_company_description,
    elevator_pitch: safeCompanyProfile.elevator_pitch,
    mission_and_vision: safeCompanyProfile.mission_and_vision,
    key_products_services: safeCompanyProfile.key_products_services,
    unique_selling_points: safeCompanyProfile.unique_selling_points,
    employee_perks: safeCompanyProfile.employee_perks,
    achievements_awards: safeCompanyProfile.achievements_awards,
    industry: safeCompanyProfile.industry_sector || safeCompanyDetails.industry,
    industry_sector: safeCompanyProfile.industry_sector || safeCompanyDetails.industry,
    size_range: safeCompanyProfile.company_size || safeCompanyDetails.sizeRange,
    company_size: safeCompanyProfile.company_size || safeCompanyDetails.sizeRange,
    founded_year: safeCompanyProfile.founded_year,
    ownership_type: safeCompanyProfile.ownership_type,
    parent_company: safeCompanyProfile.parent_company,
    parent_company_subsidiaries: safeCompanyProfile.parent_company,
    work_policies: safeCompanyProfile.work_policies,
    recruitment_tools: safeCompanyProfile.recruitment_tools,
    job_boards: safeCompanyProfile.job_boards,
    job_board_rating: safeCompanyProfile.job_board_rating,
    email: safeCompanyProfile.company_email,
    company_email: safeCompanyProfile.company_email,
    phone: safeCompanyProfile.company_phone,
    company_phone: safeCompanyProfile.company_phone,
    status:
      normalizeSequenceString(
        safeCompanyDetails.status || safeCompanyProfile.status || "Prospect",
        80
      ) || "Prospect",
    source:
      normalizeSequenceString(
        safeCompanyDetails.source || safeCompanyProfile.source || "Sequence AI",
        80
      ) || "Sequence AI",
    owner_name: resolvedOwnerName,
    owner: resolvedOwnerName,
    team_member: resolvedOwnerName,
  };
}

function buildLeadCompanyPersistencePayload({
  companyProfile = {},
  companyDetails = {},
} = {}) {
  const safeCompanyDetails =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const safeCompanyProfile = buildSequenceCompanyProfile({
    companyProfile,
    companyDetails: safeCompanyDetails,
  });

  return {
    location: safeCompanyProfile.location,
    ownership_type: safeCompanyProfile.ownership_type,
    parent_company: safeCompanyProfile.parent_company,
    career_site_url: safeCompanyProfile.career_site,
    industry_sector: safeCompanyProfile.industry_sector || safeCompanyDetails.industry,
    founded_year: safeCompanyProfile.founded_year,
    company_size: safeCompanyProfile.company_size || safeCompanyDetails.sizeRange,
    company_website: safeCompanyProfile.company_website || safeCompanyDetails.website,
    work_policies: safeCompanyProfile.work_policies,
    job_board_rating: safeCompanyProfile.job_board_rating,
    elevator_pitch: safeCompanyProfile.elevator_pitch,
    full_company_description: safeCompanyProfile.full_company_description,
    mission_and_vision: safeCompanyProfile.mission_and_vision,
    key_products_services: safeCompanyProfile.key_products_services,
    unique_selling_points: safeCompanyProfile.unique_selling_points,
    employee_perks: safeCompanyProfile.employee_perks,
    achievements_awards: safeCompanyProfile.achievements_awards,
    recruitment_tools: safeCompanyProfile.recruitment_tools,
    job_boards: safeCompanyProfile.job_boards,
  };
}

function hasMissingStructuredCompanyFields(companyProfile = {}, jobDetails = {}) {
  const safeCompanyProfile =
    companyProfile && typeof companyProfile === "object" ? companyProfile : {};
  const safeJobDetails = jobDetails && typeof jobDetails === "object" ? jobDetails : {};

  return [
    safeCompanyProfile.full_company_description,
    safeCompanyProfile.mission_and_vision,
    safeCompanyProfile.key_products_services,
    safeCompanyProfile.industry_sector,
    safeCompanyProfile.company_website || safeJobDetails.companyWebsite,
  ].some((value) => !normalizeSequenceString(value, 8000));
}

function listMissingStructuredEmailFields(companyProfile = {}, jobDetails = {}) {
  const safeCompanyProfile =
    companyProfile && typeof companyProfile === "object" ? companyProfile : {};
  const safeJobDetails = jobDetails && typeof jobDetails === "object" ? jobDetails : {};
  const missing = [];

  const requireField = (value, label) => {
    if (!normalizeSequenceString(value, 8000)) {
      missing.push(label);
    }
  };

  requireField(safeCompanyProfile.full_company_description, "company description");
  requireField(safeCompanyProfile.mission_and_vision, "company mission and values");
  requireField(safeCompanyProfile.key_products_services, "company key products/services");
  requireField(
    safeCompanyProfile.company_website || safeCompanyProfile.career_site,
    "company website or career page"
  );
  requireField(safeJobDetails.jobTitle, "job title");
  requireField(safeJobDetails.roleOverview || safeJobDetails.jobDescription, "role overview");
  requireField(safeJobDetails.jobLink || safeJobDetails.careerWebsite, "job link");
  requireField(safeJobDetails.responsibilities, "job responsibilities");
  requireField(safeJobDetails.qualifications, "job qualifications");
  requireField(
    (safeJobDetails.hardSkills || []).concat(safeJobDetails.softSkills || []).join(", "),
    "job skills"
  );

  return missing;
}

function buildJobEmailDetails(job = {}, companyDetails = {}) {
  const safeJob = job && typeof job === "object" ? job : {};
  const safeCompanyDetails =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const companyWebsite = normalizeSequenceString(
    safeJob.company_website || safeJob.companyWebsite || safeJob.website || safeCompanyDetails.website,
    255
  );
  const jobLink = normalizeUrlValue(
    safeJob.post_link ||
      safeJob.postLink ||
      safeJob.job_url ||
      safeJob.jobUrl ||
      safeJob.url ||
      safeJob.career_website ||
      safeJob.company_career_website ||
      safeJob.careerWebsite
  );
  const hardSkills = normalizeTextArray(
    safeJob.hard_skills || safeJob.hardSkills || safeJob.tech_stack_tokens || safeJob.tech_stack
  );
  const softSkills = normalizeTextArray(safeJob.soft_skills || safeJob.softSkills);
  const documents = normalizeDocumentList(safeJob.documents);

  const normalizedJob = sanitizeJobCompanyContactFields(safeJob);

  return {
    jobTitle: normalizeSequenceString(safeJob.job_title || safeJob.jobTitle || safeCompanyDetails.job, 160),
    companyName: normalizeSequenceString(
      safeJob.company_name || safeJob.companyName || safeCompanyDetails.company,
      160
    ),
    companyDomain: toDisplayDomain(companyWebsite),
    primaryContact: normalizeSequenceString(
      normalizedJob.primary_contact ||
        normalizedJob.contact_name ||
        getDistinctJobHiringManagerName(normalizedJob),
      160
    ),
    titleField: normalizeSequenceString(safeJob.contact_title || safeJob.titleField || "Hiring Manager", 160),
    salaryRange: normalizeSequenceString(
      safeJob.salary_range || safeJob.salaryRange || safeJob.compensation,
      255
    ),
    location: normalizeSequenceString(
      safeJob.location || safeCompanyDetails.jobLocation,
      255
    ),
    employmentType: normalizeSequenceString(
      safeJob.employment_type || safeJob.employmentType || safeJob.employment || safeCompanyDetails.employment,
      120
    ),
    workArrangement: normalizeSequenceString(
      safeJob.work_arrangement || safeJob.workArrangement || safeCompanyDetails.workArrangement,
      120
    ),
    companySize: normalizeSequenceString(
      safeJob.company_size || safeJob.companySize || safeCompanyDetails.sizeRange,
      120
    ),
    industry: normalizeSequenceString(
      safeJob.industry || safeJob.company_industry || safeCompanyDetails.industry,
      160
    ),
    companyWebsite,
    careerWebsite: normalizeSequenceString(
      safeJob.company_career_website || safeJob.careerWebsite || safeJob.career_website,
      255
    ),
    jobDetailUrl: normalizeUrlValue(safeJob.job_detail_url || safeJob.jobDetailUrl),
    jobLink,
    hardSkills,
    softSkills,
    jobDescription: normalizeSequenceText(
      safeJob.job_description || safeJob.jobDescription || safeJob.description,
      8000
    ),
    responsibilities: normalizeSequenceText(
      safeJob.responsibilities || safeJob.jobDescription || safeJob.description,
      8000
    ),
    qualifications: normalizeSequenceText(safeJob.qualifications, 8000),
    documents,
    source: normalizeSequenceString(safeJob.source || safeCompanyDetails.source, 80),
    status: normalizeSequenceString(safeJob.status || safeCompanyDetails.jobStatus, 80),
    roleOverview: buildRoleOverviewText({
      jobDescription:
        safeJob.job_description || safeJob.jobDescription || safeJob.description,
      responsibilities:
        safeJob.responsibilities || safeJob.jobDescription || safeJob.description,
      jobLink,
      jobDetailUrl: safeJob.job_detail_url || safeJob.jobDetailUrl,
      careerWebsite:
        safeJob.company_career_website || safeJob.careerWebsite || safeJob.career_website,
      companyWebsite,
    }),
  };
}

function buildEmailCampaignDetailsText({
  companyDetails = {},
  leadInfo = {},
  jobDetails = {},
} = {}) {
  const dash = "-";
  const companyName = normalizeSequenceString(
    companyDetails.company || leadInfo.company_name,
    160
  );
  const companyTechStack = normalizeTextArray(
    leadInfo.recruitment_tools || leadInfo.technology_stack
  );
  const jobHardSkills = normalizeTextArray(jobDetails.hardSkills);
  const jobSoftSkills = normalizeTextArray(jobDetails.softSkills);
  const jobDocuments = normalizeTextArray(jobDetails.documents);

  const sections = [
    "Company Detail:",
    `Company Name: ${companyName || dash}`,
    `Company Website: ${normalizeUrlValue(leadInfo.company_website || companyDetails.website) || dash}`,
    `Career Website: ${normalizeUrlValue(leadInfo.career_site || jobDetails.careerWebsite) || dash}`,
    `Full Company Description: ${normalizeSequenceText(leadInfo.full_company_description, 8000) || dash}`,
    `Mission and Vision: ${normalizeSequenceText(leadInfo.mission_and_vision, 8000) || dash}`,
    `Key Products/Services Offered: ${normalizeSequenceText(leadInfo.key_products_services, 8000) || dash}`,
    `Unique Selling Points: ${normalizeSequenceText(leadInfo.unique_selling_points, 8000) || dash}`,
    `Employee Perks / Benefits Highlights: ${normalizeSequenceText(leadInfo.employee_perks, 8000) || dash}`,
    `Achievements and Awards: ${normalizeSequenceText(leadInfo.achievements_awards, 8000) || dash}`,
    `Technology Stack: ${companyTechStack.length ? companyTechStack.join(", ") : dash}`,
    `Location: ${normalizeSequenceString(leadInfo.location || companyDetails.location, 255) || dash}`,
    `Industry/Sector: ${normalizeSequenceString(leadInfo.industry_sector || companyDetails.industry, 160) || dash}`,
    `Ownership Type: ${normalizeSequenceString(leadInfo.ownership_type, 255) || dash}`,
    `Company Size: ${normalizeSequenceString(leadInfo.company_size || companyDetails.sizeRange, 120) || dash}`,
    `Work Policies: ${normalizeSequenceString(leadInfo.work_policies, 255) || dash}`,
    `Founded Year: ${normalizeSequenceString(leadInfo.founded_year, 80) || dash}`,
    "",
    "Job Detail:",
    `Job Title: ${jobDetails.jobTitle || dash}`,
    `Company Name: ${jobDetails.companyName || companyName || dash}`,
    `Company Domain: ${jobDetails.companyDomain || dash}`,
    `Contact: ${jobDetails.primaryContact || dash}`,
    `Title: ${jobDetails.titleField || dash}`,
    `Salary Range: ${jobDetails.salaryRange || dash}`,
    `Location: ${jobDetails.location || dash}`,
    `Employment Type: ${jobDetails.employmentType || dash}`,
    `Work Arrangement: ${jobDetails.workArrangement || dash}`,
    `Company Website: ${jobDetails.companyWebsite || dash}`,
    `Company Career Website: ${jobDetails.careerWebsite || dash}`,
    `Job Link: ${jobDetails.jobLink || dash}`,
    `Company Size: ${jobDetails.companySize || dash}`,
    `Industry: ${jobDetails.industry || dash}`,
    `Hard Skills: ${jobHardSkills.length ? jobHardSkills.join(", ") : dash}`,
    `Soft Skills: ${jobSoftSkills.length ? jobSoftSkills.join(", ") : dash}`,
    `Role Overview Summary (max 300 chars): ${jobDetails.roleOverview || dash}`,
    `Job Description: ${jobDetails.jobDescription || dash}`,
    `Key Responsibilities: ${jobDetails.responsibilities || dash}`,
    `Qualifications: ${jobDetails.qualifications || dash}`,
    `Documents: ${jobDocuments.length ? jobDocuments.join(", ") : dash}`,
  ];

  return sections.join("\n").trim();
}

function uniqueItems(items, entityType) {
  const seen = new Set();
  return (Array.isArray(items) ? items : [])
    .map((item) => normalizeSequenceItem(item, entityType))
    .filter((item) => item.name || item.title || item.company)
    .filter((item) => {
      const key = [
        item.entityType,
        item.entityId ?? "",
        item.email?.toLowerCase() || "",
        item.linkedinUrl?.toLowerCase() || "",
        item.name?.toLowerCase() || item.title?.toLowerCase() || "",
      ].join(":");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function ItemList({ items, entityLabel, compact = false, onRemove }) {
  const limit = compact ? 4 : items.length;
  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
      <Typography sx={{ color: BLUE, fontSize: 13, fontWeight: 800, mb: 1.2 }}>
        Selected {entityLabel}
      </Typography>
      {items.length ? (
        <Stack spacing={1}>
          {items.slice(0, limit).map((item, index) => (
            <Box
              key={`${item.entityType}-${item.entityId ?? item.email ?? index}`}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: "#EEF2FF", color: BLUE }}>
                  {(item.name || item.title || item.company || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} noWrap>
                    {item.name || item.title || item.company}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>
                    {[item.title, item.company, item.email].filter(Boolean).join(" | ") || item.location || "-"}
                  </Typography>
                </Box>
              </Box>
              {!compact && onRemove ? (
                <IconButton size="small" onClick={() => onRemove(index)}>
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: MUTED }} />
                </IconButton>
              ) : null}
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          No {entityLabel.toLowerCase()} selected yet.
        </Typography>
      )}
      {compact && items.length > limit ? (
        <Typography sx={{ mt: 1, fontSize: 11.5, color: MUTED }}>
          +{items.length - limit} more
        </Typography>
      ) : null}
    </Box>
  );
}

function getSequenceItemKey(item = {}, index = 0) {
  return [
    item.entityType || "item",
    item.entityId ?? "",
    item.email || "",
    item.linkedinUrl || "",
    item.name || item.title || item.company || index,
  ].join(":");
}

function StartFlowCard({ title, subtitle, icon, onClick, highlighted = false }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        borderRadius: "10px",
        border: "1px solid #BB86FC",
        px: 1.75,
        py: 1.5,
        alignItems: "stretch",
        justifyContent: "stretch",
        textAlign: "left",
        backgroundColor: highlighted ? "#F6FAFF" : "#FFFFFF",
        transition: "all 160ms ease",
        "&:hover": {
          backgroundColor: highlighted ? "#EEF6FF" : "#FBF8FF",
          borderColor: "#A855F7",
        },
      }}
    >
      <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: PURPLE, fontSize: 12.5, fontWeight: 800, lineHeight: 1.2, mb: 0.65 }}>
            {title}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 10.5, lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "999px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: PURPLE,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </ButtonBase>
  );
}

function CampaignTypeCard({ label, selected, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        minWidth: 106,
        borderRadius: "12px",
        border: `1px solid ${selected ? "#C4B5FD" : "#D0D5DD"}`,
        px: 1.8,
        py: 1.5,
        justifyContent: "space-between",
        gap: 1.5,
        backgroundColor: "#fff",
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "#4B5563" }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: 1,
          border: `1.5px solid ${selected ? BLUE : "#D0D5DD"}`,
          backgroundColor: selected ? "#EEF2FF" : "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: BLUE,
          flexShrink: 0,
        }}
      >
        {selected ? <CheckRoundedIcon sx={{ fontSize: 14 }} /> : null}
      </Box>
    </ButtonBase>
  );
}

export default function AddToSequenceDialog({
  open,
  onClose,
  entityType = "candidate",
  items = [],
  recommendedItems = [],
  user = null,
  onCreated,
  editSequence = null,
  initialCampaignType = "recruiting",
  initialScreen = "start",
}) {
  const normalizedEntityType = normalizeSequenceEntityType(entityType);
  const normalizedInitialCampaignType =
    String(initialCampaignType).toLowerCase() === "sales" ? "sales" : "recruiting";
  const normalizedInitialScreen = ["details", "recipients", "audience"].includes(initialScreen)
    ? initialScreen
    : "start";
  const entityLabel = getSequenceEntityLabel(normalizedEntityType);
  const entityLabelPlural = getSequenceEntityLabel(normalizedEntityType, true);
  const incomingItems = useMemo(
    () => uniqueItems(items, normalizedEntityType),
    [items, normalizedEntityType]
  );
  const recommendedSequenceItems = useMemo(
    () => uniqueItems(recommendedItems, normalizedEntityType),
    [recommendedItems, normalizedEntityType]
  );
  const recipientSourceItems = useMemo(
    () => uniqueItems([...incomingItems, ...recommendedSequenceItems], normalizedEntityType),
    [incomingItems, recommendedSequenceItems, normalizedEntityType]
  );
  const [verifiedUser, setVerifiedUser] = useState(null);
  const effectiveUser = verifiedUser || user;
  const fallbackDefaults = useMemo(() => senderDefaults(user), [user]);
  const defaults = useMemo(() => senderDefaults(effectiveUser), [effectiveUser]);

  const [screen, setScreen] = useState("start");
  const [mode, setMode] = useState("new");
  const [sequenceItems, setSequenceItems] = useState(recipientSourceItems);
  const [existingSequences, setExistingSequences] = useState([]);
  const [existingSearch, setExistingSearch] = useState("");
  const [selectedExistingId, setSelectedExistingId] = useState(null);
  const [existingLoading, setExistingLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [saveAction, setSaveAction] = useState("");
  const [textColorAnchorEl, setTextColorAnchorEl] = useState(null);
  const [textAlignmentAnchorEl, setTextAlignmentAnchorEl] = useState(null);
  const [draftId, setDraftId] = useState(editSequence?.id || null);
  const [gmailStatus, setGmailStatus] = useState({
    configured: false,
    connected: false,
    connection: null,
  });
  const [gmailLoading, setGmailLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const [form, setForm] = useState({
    name: `${entityLabel} outreach`,
    goal: `Move selected ${entityLabelPlural.toLowerCase()} into a structured follow-up flow`,
    description: "",
    status: "active",
    campaignType: normalizedInitialCampaignType,
    ownerName: defaults.fromName || effectiveUser?.name || "",
    privacy: "Private to Owner",
    relatedTo: "Job Lead",
    companyName: shouldStartWithBlankCompany(normalizedEntityType) ? "" : incomingItems[0]?.company || "",
    contactName:
      (incomingItems[0]?.name && incomingItems[0]?.title
        ? `${incomingItems[0].name} (${incomingItems[0].title})`
        : incomingItems[0]?.name || incomingItems[0]?.owner || "") || "",
    selectedJobName: shouldStartWithBlankJob(normalizedEntityType) ? "" : incomingItems[0]?.title || "",
    jobSource: incomingItems[0]?.metadata?.source || "Job Board",
    bodyFormat: "Rich text",
    primaryReplyAction: "Finish sequence",
    secondaryReplyAction: "Finish sequence",
    fromName: defaults.fromName,
    fromEmail: defaults.fromEmail,
    testEmail: defaults.fromEmail,
  });
  const [steps, setSteps] = useState(() => buildInitialCampaignSteps(normalizedEntityType, defaults));
  const [activeStepId, setActiveStepId] = useState("step-1");
  const [pendingAutoComposeStepId, setPendingAutoComposeStepId] = useState("");
  const [relatedSearch, setRelatedSearch] = useState("");
  const [relatedSelections, setRelatedSelections] = useState(() =>
    buildDefaultRelatedSelections(incomingItems)
  );
  const [recipientSearch, setRecipientSearch] = useState("");
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);
  const [contactLookupLoading, setContactLookupLoading] = useState(false);
  const [platformContactOptions, setPlatformContactOptions] = useState([]);
  const [companyLookupOptions, setCompanyLookupOptions] = useState(() => {
    if (shouldStartWithBlankCompany(normalizedEntityType)) return [];
    const initialOption = buildCompanyLookupOption({
      company_name: incomingItems[0]?.company,
    });
    return initialOption ? [initialOption] : [];
  });
  const [selectedCompanyOption, setSelectedCompanyOption] = useState(() =>
    shouldStartWithBlankCompany(normalizedEntityType)
      ? null
      : buildCompanyLookupOption({
          company_name: incomingItems[0]?.company,
        })
  );
  const [selectedCompanyProfile, setSelectedCompanyProfile] = useState(null);
  const [selectedLeadMatch, setSelectedLeadMatch] = useState(null);
  const [selectedLeadInfo, setSelectedLeadInfo] = useState(null);
  const [companyInputValue, setCompanyInputValue] = useState(
    shouldStartWithBlankCompany(normalizedEntityType)
      ? ""
      : normalizeSequenceString(incomingItems[0]?.company, 160)
  );
  const [jobLookupLoading, setJobLookupLoading] = useState(false);
  const [jobLookupOptions, setJobLookupOptions] = useState(() => {
    if (shouldStartWithBlankJob(normalizedEntityType)) return [];
    const initialOption = buildJobLookupOption({
      job_title: incomingItems[0]?.title,
      company_name: incomingItems[0]?.company,
      source: incomingItems[0]?.metadata?.source,
    });
    return initialOption ? [initialOption] : [];
  });
  const [selectedJobOption, setSelectedJobOption] = useState(() =>
    shouldStartWithBlankJob(normalizedEntityType)
      ? null
      : buildJobLookupOption({
          job_title: incomingItems[0]?.title,
          company_name: incomingItems[0]?.company,
          source: incomingItems[0]?.metadata?.source,
        })
  );
  const [selectedJobFullDetails, setSelectedJobFullDetails] = useState(null);
  const [jobInputValue, setJobInputValue] = useState(
    shouldStartWithBlankJob(normalizedEntityType)
      ? ""
      : normalizeSequenceString(incomingItems[0]?.title, 160)
  );
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState([]);
  const [previewRecipientKey, setPreviewRecipientKey] = useState("");
  const hasInitializedForOpenRef = useRef(false);
  const previousEmailDetailsTextRef = useRef("");
  const richBodyEditorRef = useRef(null);
  const richBodyLastValueRef = useRef("");
  const richBodySelectionRef = useRef(null);
  const [variablesAnchorEl, setVariablesAnchorEl] = useState(null);
  const [customVarDialogOpen, setCustomVarDialogOpen] = useState(false);
  const [customVarName, setCustomVarName] = useState("");
  const [customVarValue, setCustomVarValue] = useState("");
  const [customVariables, setCustomVariables] = useState([]);
  const [aiComposing, setAiComposing] = useState(false);
  const [preparingAudience, setPreparingAudience] = useState(false);
  const [audiencePreparation, setAudiencePreparation] = useState(
    INITIAL_AUDIENCE_PREPARATION_STATE
  );
  const screenLabels = {
    details: "Details",
    recipients: "Recipients",
    steps: "Steps",
    audience: "Audience",
    review: "Review",
    preview: "Preview",
    existing: "Existing",
  };

  const loadGmailStatus = useCallback(async () => {
    try {
      const response = await fetch(apiUrl("/gmail/status"), {
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          await readResponseError(
            response,
            `Failed to load Gmail status (${response.status})`
          )
        );
      }

      const payload = await response.json().catch(() => ({}));
      setGmailStatus({
        configured: Boolean(payload?.configured),
        connected: Boolean(payload?.connected),
        connection:
          payload?.connection && typeof payload.connection === "object"
            ? payload.connection
            : null,
      });
    } catch (error) {
      setGmailStatus({
        configured: false,
        connected: false,
        connection: null,
      });
      setToast({
        open: true,
        type: "error",
        msg: error?.message || "Failed to load Gmail status",
      });
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadVerifiedUser = async () => {
      try {
        const response = await fetch(rootApiUrl("/verify"), {
          headers: authHeaders(),
        });

        if (!response.ok) return;

        const payload = await response.json().catch(() => ({}));
        const nextUser =
          payload?.user && typeof payload.user === "object" ? payload.user : null;

        if (active && nextUser?.id) {
          setVerifiedUser(nextUser);
        }
      } catch {
        // Keep using the existing frontend user fallback when verify is unavailable.
      }
    };

    loadVerifiedUser();

    return () => {
      active = false;
    };
  }, [open, user?.id]);

  useEffect(() => {
    if (!open) {
      hasInitializedForOpenRef.current = false;
      return;
    }
    if (hasInitializedForOpenRef.current) return;

    hasInitializedForOpenRef.current = true;
    const nextSteps = buildInitialCampaignSteps(normalizedEntityType, defaults);
    setScreen(normalizedInitialScreen);
    setMode("new");
    setDraftId(editSequence?.id || null);
    setSequenceItems(recipientSourceItems);
    setExistingSearch("");
    setSelectedExistingId(null);
    setForm({
      name: incomingItems.length ? `${entityLabel} outreach (${incomingItems.length} selected)` : `${entityLabel} outreach`,
      goal: `Move selected ${entityLabelPlural.toLowerCase()} into a structured follow-up flow`,
      description: "",
      status: "active",
      campaignType: normalizedInitialCampaignType,
      ownerName: defaults.fromName || effectiveUser?.name || "",
      privacy: "Private to Owner",
      relatedTo: "Job Lead",
      companyName: shouldStartWithBlankCompany(normalizedEntityType) ? "" : incomingItems[0]?.company || "",
      contactName:
        (incomingItems[0]?.name && incomingItems[0]?.title
          ? `${incomingItems[0].name} (${incomingItems[0].title})`
          : incomingItems[0]?.name || incomingItems[0]?.owner || "") || "",
      selectedJobName: shouldStartWithBlankJob(normalizedEntityType) ? "" : incomingItems[0]?.title || "",
      jobSource: incomingItems[0]?.metadata?.source || "Job Board",
      bodyFormat: "Rich text",
      primaryReplyAction: "Finish sequence",
      secondaryReplyAction: "Finish sequence",
      fromName: defaults.fromName,
      fromEmail: defaults.fromEmail,
      testEmail: defaults.fromEmail,
    });
    setSteps(nextSteps);
    setActiveStepId(nextSteps[0]?.id || "step-1");
    setRelatedSearch("");
    setRelatedSelections(buildDefaultRelatedSelections(incomingItems));
    setRecipientSearch("");
    const initialCompanyOption = shouldStartWithBlankCompany(normalizedEntityType)
      ? null
      : buildCompanyLookupOption({
          company_name: incomingItems[0]?.company,
        });
    const initialJobOption = shouldStartWithBlankJob(normalizedEntityType)
      ? null
      : buildJobLookupOption({
          job_title: incomingItems[0]?.title,
          company_name: incomingItems[0]?.company,
          source: incomingItems[0]?.metadata?.source,
        });
    setCompanyLookupLoading(false);
    setCompanyLookupOptions(initialCompanyOption ? [initialCompanyOption] : []);
    setSelectedCompanyOption(initialCompanyOption);
    setSelectedCompanyProfile(null);
    setSelectedLeadMatch(null);
    setSelectedLeadInfo(null);
    setCompanyInputValue(
      shouldStartWithBlankCompany(normalizedEntityType)
        ? ""
        : normalizeSequenceString(incomingItems[0]?.company, 160)
    );
    setJobLookupLoading(false);
    setJobLookupOptions(initialJobOption ? [initialJobOption] : []);
    setSelectedJobOption(initialJobOption);
    setSelectedJobFullDetails(null);
    setJobInputValue(
      shouldStartWithBlankJob(normalizedEntityType)
        ? ""
        : normalizeSequenceString(incomingItems[0]?.title, 160)
    );
    setSelectedRecipientKeys(
      incomingItems.map((item, index) => getSequenceItemKey(item, index))
    );
    setPreviewRecipientKey("");
    setSaveAction("");
    setPreparingAudience(false);
    setAudiencePreparation(INITIAL_AUDIENCE_PREPARATION_STATE);
    previousEmailDetailsTextRef.current = "";

    if (editSequence?.id) {
      const seq = editSequence;
      const settings = seq.settings_json
        ? typeof seq.settings_json === "string"
          ? (() => { try { return JSON.parse(seq.settings_json); } catch { return {}; } })()
          : seq.settings_json
        : seq.settings || {};
      const savedCompanyDetails =
        settings.companyDetails && typeof settings.companyDetails === "object"
          ? settings.companyDetails
          : {};
      const savedLeadDetails =
        settings.leadDetails && typeof settings.leadDetails === "object"
          ? settings.leadDetails
          : null;
      const savedJobDetails =
        settings.jobDetails && typeof settings.jobDetails === "object"
          ? settings.jobDetails
          : null;
      const savedCompanyName = normalizeSequenceString(
        savedCompanyDetails.company || savedLeadDetails?.company_name,
        160
      );
      const savedJobTitle = normalizeSequenceString(
        savedCompanyDetails.job || savedJobDetails?.jobTitle || savedJobDetails?.job_title,
        160
      );
      const savedJobSource = normalizeSequenceString(
        savedCompanyDetails.source || savedJobDetails?.source,
        80
      );
      const savedContactName = normalizeSequenceString(savedCompanyDetails.contact, 160);
      setDraftId(seq.id);
      // Saved sequences reopen in the final composer/review screen, rather
      // than restarting the creation flow.
      setScreen("review");
      setMode("new");
      setForm((prev) => ({
        ...prev,
        name: seq.name || prev.name,
        goal: seq.goal || prev.goal,
        description: seq.description || prev.description,
        status: seq.status || prev.status,
        campaignType: settings.campaignType || prev.campaignType,
        ownerName: seq.owner_name || prev.ownerName,
        privacy: settings.privacy || prev.privacy,
        fromName: seq.from_name || prev.fromName,
        fromEmail: seq.from_email || prev.fromEmail,
        testEmail: seq.from_email || prev.testEmail,
        companyName: savedCompanyName || prev.companyName,
        contactName: savedContactName || prev.contactName,
        selectedJobName: savedJobTitle || prev.selectedJobName,
        jobSource: savedJobSource || prev.jobSource,
        primaryReplyAction: settings.replyRules?.primary || prev.primaryReplyAction,
        secondaryReplyAction: settings.replyRules?.secondary || prev.secondaryReplyAction,
      }));
      setCompanyInputValue(savedCompanyName);
      setJobInputValue(savedJobTitle);
      setSelectedCompanyOption(
        buildCompanyLookupOption({
          ...savedCompanyDetails,
          company_name: savedCompanyName || savedCompanyDetails.company,
        })
      );
      setSelectedCompanyProfile(savedCompanyDetails);
      setSelectedJobOption(
        buildJobLookupOption({
          ...savedJobDetails,
          id: savedJobDetails?.id ?? savedCompanyDetails.jobId,
          job_title: savedJobTitle || savedJobDetails?.jobTitle || savedJobDetails?.job_title,
          company_name:
            savedCompanyName ||
            savedJobDetails?.companyName ||
            savedJobDetails?.company_name ||
            savedCompanyDetails.company,
          location: savedJobDetails?.location || savedCompanyDetails.jobLocation,
          employment: savedJobDetails?.employmentType || savedJobDetails?.employment || savedCompanyDetails.employment,
          work_arrangement:
            savedJobDetails?.workArrangement ||
            savedJobDetails?.work_arrangement ||
            savedCompanyDetails.workArrangement,
          source: savedJobDetails?.source || savedCompanyDetails.source,
          owner: savedCompanyDetails.owner,
        })
      );
      setSelectedLeadMatch(
        savedLeadDetails
          ? {
              company_name: normalizeSequenceString(
                savedLeadDetails.company_name || savedCompanyName,
                160
              ),
            }
          : null
      );
      setSelectedLeadInfo(savedLeadDetails);
      setSelectedJobFullDetails(savedJobDetails);
      if (Array.isArray(seq.steps) && seq.steps.length) {
        const loadedSteps = seq.steps.map((s, i) => {
          const stepMetadata = s.metadata_json
            ? typeof s.metadata_json === "string"
              ? (() => { try { return JSON.parse(s.metadata_json); } catch { return {}; } })()
              : s.metadata_json
            : s.metadata || {};
          return {
            id: `step-${i + 1}`,
            channel: s.channel || "email",
            title: s.title || "",
            subject: s.subject || "",
            body: s.body_text || s.body || "",
            delayValue: Number(s.delay_value) || 0,
            delayUnit: s.delay_unit || "days",
            scheduleType: stepMetadata.scheduleType || "immediately",
            scheduledDate: stepMetadata.scheduledDate || "",
            scheduledTime: stepMetadata.scheduledTime || DEFAULT_STEP_TIME,
            timezoneLabel: stepMetadata.timezoneLabel || DEFAULT_STEP_TIMEZONE,
            bodyFormat: "Rich text",
            senderName: s.sender_name || "",
            senderEmail: s.sender_email || "",
          };
        });
        setSteps(loadedSteps);
        setActiveStepId(loadedSteps[loadedSteps.length - 1]?.id || "step-1");
        setForm((prev) => ({ ...prev, bodyFormat: "Rich text" }));
      }
      if (Array.isArray(seq.enrollments) && seq.enrollments.length) {
        const loadedItems = seq.enrollments.map((e) =>
          normalizeSequenceItem(
            {
              entityType: e.entity_type,
              entityId: e.entity_id,
              name: e.full_name,
              title: e.title,
              company: e.company_name,
              email: e.email,
              phone: e.phone,
              linkedinUrl: e.linkedin_url,
              location: e.location,
              owner: e.owner_name,
            },
            normalizedEntityType
          )
        );
        setSequenceItems(loadedItems);
        setSelectedRecipientKeys(
          loadedItems.map((item, index) => getSequenceItemKey(item, index))
        );
      }
      if (Array.isArray(settings.relatedTo)) {
        setRelatedSelections(settings.relatedTo);
      }
    }
  }, [
    open,
    incomingItems,
    recipientSourceItems,
    defaults,
    entityLabel,
    entityLabelPlural,
    normalizedEntityType,
    effectiveUser?.name,
    editSequence,
    normalizedInitialCampaignType,
    normalizedInitialScreen,
  ]);

  useEffect(() => {
    if (!open || !verifiedUser) return;

    const nextDefaults = senderDefaults(verifiedUser);

    setForm((prev) => {
      let changed = false;
      const next = { ...prev };

      if (
        nextDefaults.fromName &&
        (!normalizeSequenceString(prev.ownerName) ||
          matchesNormalizedValue(prev.ownerName, fallbackDefaults.fromName))
      ) {
        next.ownerName = nextDefaults.fromName;
        changed = true;
      }

      if (
        nextDefaults.fromName &&
        (!normalizeSequenceString(prev.fromName) ||
          matchesNormalizedValue(prev.fromName, fallbackDefaults.fromName))
      ) {
        next.fromName = nextDefaults.fromName;
        changed = true;
      }

      if (
        nextDefaults.fromEmail &&
        (!normalizeSequenceString(prev.fromEmail) ||
          matchesNormalizedValue(prev.fromEmail, fallbackDefaults.fromEmail))
      ) {
        next.fromEmail = nextDefaults.fromEmail;
        changed = true;
      }

      if (
        nextDefaults.fromEmail &&
        (!normalizeSequenceString(prev.testEmail) ||
          matchesNormalizedValue(prev.testEmail, fallbackDefaults.fromEmail))
      ) {
        next.testEmail = nextDefaults.fromEmail;
        changed = true;
      }

      return changed ? next : prev;
    });

    setSteps((prev) => {
      let changed = false;

      const nextSteps = prev.map((step) => {
        const nextStep = { ...step };
        let stepChanged = false;

        if (
          nextDefaults.fromName &&
          (!normalizeSequenceString(step.senderName) ||
            matchesNormalizedValue(step.senderName, fallbackDefaults.fromName))
        ) {
          nextStep.senderName = nextDefaults.fromName;
          stepChanged = true;
        }

        if (
          nextDefaults.fromEmail &&
          (!normalizeSequenceString(step.senderEmail) ||
            matchesNormalizedValue(step.senderEmail, fallbackDefaults.fromEmail))
        ) {
          nextStep.senderEmail = nextDefaults.fromEmail;
          stepChanged = true;
        }

        if (stepChanged) changed = true;
        return stepChanged ? nextStep : step;
      });

      return changed ? nextSteps : prev;
    });
  }, [
    fallbackDefaults.fromEmail,
    fallbackDefaults.fromName,
    open,
    verifiedUser,
  ]);

  useEffect(() => {
    if (!open) return;
    loadGmailStatus();
  }, [open, loadGmailStatus]);

  const handleCompanyInputChange = useCallback(
    (_, nextInputValue) => {
      const normalizedValue = normalizeSequenceString(nextInputValue, 160);
      setCompanyInputValue(normalizedValue);
      setForm((prev) => ({ ...prev, companyName: normalizedValue }));

      if (
        selectedCompanyOption &&
        !matchesNormalizedValue(normalizedValue, selectedCompanyOption.company_name)
      ) {
        setSelectedCompanyOption(null);
        setSelectedCompanyProfile(null);
        setPlatformContactOptions([]);
        setSelectedJobOption(null);
        setSelectedJobFullDetails(null);
        setJobLookupOptions([]);
        setJobInputValue("");
        setForm((prev) => ({ ...prev, companyName: normalizedValue, selectedJobName: "", contactName: "" }));
      }
    },
    [selectedCompanyOption]
  );

  const handleCompanySelection = useCallback((_, nextValue) => {
    if (typeof nextValue === "string") {
      const normalizedValue = normalizeSequenceString(nextValue, 160);
      setSelectedCompanyOption(null);
      setSelectedCompanyProfile(null);
      setPlatformContactOptions([]);
      setSelectedJobOption(null);
      setSelectedJobFullDetails(null);
      setJobLookupOptions([]);
      setJobInputValue("");
      setCompanyInputValue(normalizedValue);
      setForm((prev) => ({ ...prev, companyName: normalizedValue, selectedJobName: "", contactName: "" }));
      return;
    }

    if (!nextValue) {
      setSelectedCompanyOption(null);
      setSelectedCompanyProfile(null);
      setPlatformContactOptions([]);
      setSelectedJobOption(null);
      setSelectedJobFullDetails(null);
      setJobLookupOptions([]);
      setJobInputValue("");
      setCompanyInputValue("");
      setForm((prev) => ({ ...prev, companyName: "", selectedJobName: "", contactName: "" }));
      return;
    }

    const nextOption = buildCompanyLookupOption(nextValue);
    const nextCompanyName = normalizeSequenceString(nextOption?.company_name, 160);

    setSelectedCompanyOption(nextOption);
    setSelectedCompanyProfile(null);
    setPlatformContactOptions([]);
    setSelectedJobOption(null);
    setSelectedJobFullDetails(null);
    setJobLookupOptions([]);
    setJobInputValue("");
    setCompanyInputValue(nextCompanyName);
    setForm((prev) => ({ ...prev, companyName: nextCompanyName, selectedJobName: "", contactName: "" }));

    if (nextOption) {
      setCompanyLookupOptions((prev) =>
        mergeLookupOptions(prev, [nextOption], getCompanyLookupKey)
      );
    }
  }, []);

  const handleJobInputChange = useCallback(
    (_, nextInputValue) => {
      const normalizedValue = normalizeSequenceString(nextInputValue, 160);
      setJobInputValue(normalizedValue);
      setForm((prev) => ({ ...prev, selectedJobName: normalizedValue }));

      if (selectedJobOption && !matchesNormalizedValue(normalizedValue, selectedJobOption.job_title)) {
        setSelectedJobOption(null);
      }
    },
    [selectedJobOption]
  );

  const handleJobSelection = useCallback((_, nextValue) => {
    if (typeof nextValue === "string") {
      const normalizedValue = normalizeSequenceString(nextValue, 160);
      setSelectedJobOption(null);
      setJobInputValue(normalizedValue);
      setForm((prev) => ({ ...prev, selectedJobName: normalizedValue }));
      return;
    }

    if (!nextValue) {
      setSelectedJobOption(null);
      setSelectedJobFullDetails(null);
      setJobInputValue("");
      setForm((prev) => ({ ...prev, selectedJobName: "" }));
      return;
    }

    const nextOption = buildJobLookupOption(nextValue);
    const nextJobTitle = normalizeSequenceString(nextOption?.job_title, 160);

    setSelectedJobOption(nextOption);
    setJobInputValue(nextJobTitle);
    setForm((prev) => ({
      ...prev,
      selectedJobName: nextJobTitle,
      jobSource: normalizeSequenceString(nextOption?.source, 80) || prev.jobSource,
      companyName: normalizeSequenceString(nextOption?.company_name, 160) || prev.companyName,
    }));

    if (nextOption) {
      setJobLookupOptions((prev) =>
        mergeLookupOptions(prev, [nextOption], getJobLookupKey)
      );
    }

    if (nextOption?.company_name) {
      const nextCompanyOption = buildCompanyLookupOption({
        company_id: nextOption.company_id,
        company_name: nextOption.company_name,
        company_logo: nextOption.company_logo,
      });

      setSelectedCompanyOption(nextCompanyOption);
      setSelectedCompanyProfile(null);
      setCompanyInputValue(nextCompanyOption?.company_name || "");

      if (nextCompanyOption) {
        setCompanyLookupOptions((prev) =>
          mergeLookupOptions(prev, [nextCompanyOption], getCompanyLookupKey)
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!open || mode !== "new") return;

    let active = true;
    const query = normalizeSequenceString(companyInputValue || form.companyName, 160);
    const timerId = window.setTimeout(async () => {
      setCompanyLookupLoading(true);
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "25",
        });
        if (query) params.set("search", query);

        const response = await fetch(apiUrl(`/companies?${params.toString()}`), {
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(
            await readResponseError(response, `Failed to load companies (${response.status})`)
          );
        }

        const payload = await response.json().catch(() => ({}));
        const rows = (Array.isArray(payload) ? payload : payload?.rows || [])
          .map((item) => buildCompanyLookupOption(item))
          .filter(Boolean);

        if (!active) return;

        setCompanyLookupOptions((prev) =>
          mergeLookupOptions(
            prev,
            [
              buildCompanyLookupOption({
                company_id: selectedCompanyOption?.company_id,
                jobs_count: selectedCompanyOption?.jobs_count,
                company_name: form.companyName || companyInputValue,
                company_logo: selectedCompanyOption?.company_logo,
                company_website: selectedCompanyOption?.website,
                industry: selectedCompanyOption?.industry,
                location: selectedCompanyOption?.location,
                status: selectedCompanyOption?.status,
                source: selectedCompanyOption?.source,
              }),
              ...rows,
            ],
            getCompanyLookupKey
          )
        );

        const matchedOption = findLookupOptionByLabel(rows, query || form.companyName, "company_name");
        if (matchedOption && (!selectedCompanyOption || !selectedCompanyOption.company_id)) {
          setSelectedCompanyOption((prev) =>
            prev && prev.company_id ? prev : matchedOption
          );
        }
      } catch (error) {
        if (active) {
          console.error("Failed to load sequence companies:", error);
        }
      } finally {
        if (active) setCompanyLookupLoading(false);
      }
    }, query ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timerId);
    };
  }, [
    companyInputValue,
    form.companyName,
    mode,
    open,
    selectedCompanyOption,
  ]);

  useEffect(() => {
    if (!open || mode !== "new") return;

    let active = true;
    const query = normalizeSequenceString(jobInputValue, 160);
    const selectedCompanyId = Number(selectedCompanyOption?.company_id || 0);
    const selectedCompanyName = normalizeSequenceString(
      selectedCompanyOption?.company_name || form.companyName,
      160
    );
    if (!selectedCompanyName) {
      setJobLookupOptions([]);
      setJobLookupLoading(false);
      return undefined;
    }
    const timerId = window.setTimeout(async () => {
      setJobLookupLoading(true);
      try {
        const params = new URLSearchParams({
          page: "1",
          limit: "25",
        });
        if (query) params.set("search", query);
        if (Number.isFinite(selectedCompanyId) && selectedCompanyId > 0) {
          params.set("companyId", String(selectedCompanyId));
        } else if (selectedCompanyName) {
          // The jobs endpoint searches the linked company name as well as the
          // title, which supports legacy selections without a company ID.
          params.set("search", selectedCompanyName);
        }

        const response = await fetch(apiUrl(`/jobs?${params.toString()}`), {
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(
            await readResponseError(response, `Failed to load jobs (${response.status})`)
          );
        }

        const payload = await response.json().catch(() => ({}));
        const rows = (Array.isArray(payload) ? payload : payload?.rows || [])
          .map((item) => buildJobLookupOption(item))
          .filter(Boolean)
          .filter((job) => jobBelongsToCompany(job, selectedCompanyOption || { company_name: selectedCompanyName }));

        if (!active) return;

        setJobLookupOptions(rows);

        const matchedOption = findLookupOptionByLabel(rows, query || form.selectedJobName, "job_title");
        if (matchedOption && (!selectedJobOption || !selectedJobOption.id)) {
          setSelectedJobOption((prev) => (prev && prev.id ? prev : matchedOption));
        }
      } catch (error) {
        if (active) {
          console.error("Failed to load sequence jobs:", error);
        }
      } finally {
        if (active) setJobLookupLoading(false);
      }
    }, query ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timerId);
    };
  }, [
    form.companyName,
    form.selectedJobName,
    jobInputValue,
    mode,
    open,
    selectedCompanyOption,
    selectedJobOption,
  ]);

  useEffect(() => {
    if (!open || mode !== "new") return;

    const companyId = Number(selectedCompanyOption?.company_id || 0);
    if (!Number.isFinite(companyId) || companyId <= 0) {
      setSelectedCompanyProfile(null);
      return;
    }

    let active = true;

    const loadCompanyProfile = async () => {
      try {
        const response = await fetch(apiUrl(`/companies/${companyId}`), {
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(
            await readResponseError(
              response,
              `Failed to load company profile (${response.status})`
            )
          );
        }

        const payload = await response.json().catch(() => ({}));
        if (!active) return;
        const nextCompanyProfile =
          payload?.company && typeof payload.company === "object"
            ? payload.company
            : payload && typeof payload === "object"
              ? payload
              : null;
        setSelectedCompanyProfile(nextCompanyProfile);
      } catch (error) {
        if (!active) return;
        console.error("Failed to load sequence company profile:", error);
      }
    };

    loadCompanyProfile();

    return () => {
      active = false;
    };
  }, [mode, open, selectedCompanyOption?.company_id]);

  useEffect(() => {
    if (!open || mode !== "new") return;

    const companyName = normalizeSequenceString(form.companyName || companyInputValue, 160);
    if (!companyName) {
      setSelectedLeadMatch(null);
      setSelectedLeadInfo(null);
      return;
    }

    setSelectedLeadMatch(null);

    let active = true;

    const loadLeadInfo = async () => {
      try {
        const searchParams = new URLSearchParams({
          page: "1",
          limit: "10",
          search: companyName,
        });

        const leadsResponse = await fetch(apiUrl(`/leads?${searchParams.toString()}`), {
          headers: authHeaders(),
        });

        if (!leadsResponse.ok) {
          throw new Error(
            await readResponseError(leadsResponse, `Failed to load leads (${leadsResponse.status})`)
          );
        }

        const leadsPayload = await leadsResponse.json().catch(() => ({}));
        const leadRows = Array.isArray(leadsPayload?.rows) ? leadsPayload.rows : [];
        const normalizedCompanyName = normalizeLookupValue(companyName);
        const matchedLead =
          leadRows.find(
            (row) => normalizeLookupValue(row?.company_name || "") === normalizedCompanyName
          ) ||
          leadRows.find((row) =>
            normalizeLookupValue(row?.company_name || "").includes(normalizedCompanyName)
          ) ||
          null;

        if (!active) return;

        setSelectedLeadMatch(matchedLead);

        if (!matchedLead?.id) {
          return;
        }

        const infoResponse = await fetch(apiUrl(`/leads/${matchedLead.id}/info`), {
          headers: authHeaders(),
        });

        if (!infoResponse.ok) {
          throw new Error(
            await readResponseError(infoResponse, `Failed to load lead info (${infoResponse.status})`)
          );
        }

        const infoPayload = await infoResponse.json().catch(() => ({}));
        if (!active) return;
        setSelectedLeadInfo(infoPayload && typeof infoPayload === "object" ? infoPayload : null);
      } catch (error) {
        if (!active) return;
        console.error("Failed to load sequence lead company info:", error);
        setSelectedLeadMatch(null);
      }
    };

    loadLeadInfo();

    return () => {
      active = false;
    };
  }, [companyInputValue, form.companyName, mode, open]);

  useEffect(() => {
    if (!open || mode !== "new") return;

    const jobId = Number(selectedJobOption?.id || 0);
    if (!Number.isFinite(jobId) || jobId <= 0) {
      if (!normalizeSequenceString(form.selectedJobName, 160)) {
        setSelectedJobFullDetails(null);
      }
      return;
    }

    let active = true;

    const loadJobDetails = async () => {
      try {
        const response = await fetch(apiUrl(`/jobs/${jobId}`), {
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(
            await readResponseError(response, `Failed to load job details (${response.status})`)
          );
        }

        const payload = await response.json().catch(() => ({}));
        if (!active) return;
        setSelectedJobFullDetails(
          payload?.job && typeof payload.job === "object" ? payload.job : null
        );
      } catch (error) {
        if (!active) return;
        console.error("Failed to load sequence job details:", error);
      }
    };

    loadJobDetails();

    return () => {
      active = false;
    };
  }, [form.selectedJobName, mode, open, selectedJobOption?.id]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const load = async () => {
      setExistingLoading(true);
      try {
        const rows = await fetchSequences({ entityType: normalizedEntityType });
        if (active) setExistingSequences(rows.filter(sequence => getSequenceCampaignType(sequence) === normalizedInitialCampaignType));
      } catch (error) {
        if (active) {
          setExistingSequences([]);
          setToast({ open: true, type: "error", msg: error?.message || "Failed to load sequences" });
        }
      } finally {
        if (active) setExistingLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [open, normalizedEntityType, normalizedInitialCampaignType]);

  useEffect(() => {
    const apiOrigin = (() => {
      if (typeof window === "undefined") return "";
      try {
        return new URL(API_ROOT, window.location.origin).origin;
      } catch {
        return window.location.origin;
      }
    })();

    const handlePopupMessage = async (event) => {
      if (!event?.data || event.data.source !== "gmail-oauth") return;
      if (apiOrigin && event.origin !== apiOrigin) return;

      if (event.data.success) {
        await loadGmailStatus();
        setToast({
          open: true,
          type: "success",
          msg: "Gmail reconnected successfully.",
        });
        return;
      }

      setToast({
        open: true,
        type: "error",
        msg: event.data.error || "Failed to connect Gmail.",
      });
    };

    window.addEventListener("message", handlePopupMessage);
    return () => window.removeEventListener("message", handlePopupMessage);
  }, [loadGmailStatus]);

  const selectedExistingSequence = useMemo(
    () => existingSequences.find((sequence) => String(sequence.id) === String(selectedExistingId)) || null,
    [existingSequences, selectedExistingId]
  );
  const filteredSequences = useMemo(() => {
    const q = normalizeSequenceString(existingSearch).toLowerCase();
    if (!q) return existingSequences;
    return existingSequences.filter((sequence) =>
      [sequence.name, sequence.goal, sequence.description, sequence.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [existingSearch, existingSequences]);
  const activeStep = useMemo(
    () => steps.find((step) => step.id === activeStepId) || steps[0] || null,
    [steps, activeStepId]
  );
  useEffect(() => {
    const editor = richBodyEditorRef.current;
    const rawBody = String(activeStep?.body || "");
    const nextBody =
      rawBody && !/<[a-z][\s\S]*>/i.test(rawBody)
        ? plainTextToHtml(rawBody)
        : rawBody;
    if (!editor || editor.innerHTML === nextBody) {
      richBodyLastValueRef.current = nextBody;
      if (nextBody !== rawBody) {
        setSteps((prev) => prev.map((step) => (step.id === activeStepId && step.body === rawBody ? { ...step, body: nextBody } : step)));
      }
      return;
    }

    // Only external changes (switching steps, AI content, or loaded content)
    // update the DOM. Replacing HTML after a toolbar command clears selection.
    if (editor.innerHTML !== nextBody) {
      editor.innerHTML = nextBody;
    }
    richBodyLastValueRef.current = nextBody;
    if (nextBody !== rawBody) {
      setSteps((prev) => prev.map((step) => (step.id === activeStepId && step.body === rawBody ? { ...step, body: nextBody } : step)));
    }
  }, [activeStep?.body, activeStepId, screen]);
  useEffect(() => {
    richBodySelectionRef.current = null;
  }, [activeStepId]);
  const activeStepIndex = useMemo(
    () => Math.max(0, steps.findIndex((step) => step.id === activeStepId)),
    [steps, activeStepId]
  );
  const previousEmailSteps = useMemo(
    () => steps.slice(0, activeStepIndex).filter((step) => step.channel === "email"),
    [steps, activeStepIndex]
  );
  const previousEmailStep = previousEmailSteps[previousEmailSteps.length - 1] || null;
  const isFollowUpStep = activeStepIndex > 0;

  const recipientRows = useMemo(
    () =>
      sequenceItems.map((item, index) => {
        const status =
          normalizeSequenceString(item.metadata?.status || item.metadata?.candidateStatus || "Available") ||
          "Available";
        const skills =
          normalizeSequenceString(
            Array.isArray(item.metadata?.skills)
              ? item.metadata.skills.join(", ")
              : item.metadata?.skills || item.metadata?.skill || item.title || item.company
          ) || "-";
        const bestMatch = Number(item.metadata?.bestMatchPercentage ?? item.metadata?.matchPercentage);

        // For non-candidate entities, prefer item.title (person/contact name) over item.name (entity name)
        const isCandidate = String(item.entityType || normalizedEntityType || "").toLowerCase() === "candidate";
        const displayName = isCandidate
          ? (item.name || item.title || item.company || `${entityLabel} ${index + 1}`)
          : (item.title || item.name || item.company || `${entityLabel} ${index + 1}`);

        return {
          ...item,
          key: getSequenceItemKey(item, index),
          displayName,
          subtitle: item.title || item.company || item.email || "-",
          avatarUrl: normalizeSequenceString(
            item.metadata?.avatarUrl || item.avatarUrl || item.avatar_url,
            2048
          ),
          status,
          skills,
          bestMatch: Number.isFinite(bestMatch) ? bestMatch : Math.max(30, 90 - index * 10),
        };
      }),
    [sequenceItems, entityLabel, normalizedEntityType]
  );

  const filteredRecipientRows = useMemo(() => {
    const q = normalizeSequenceString(recipientSearch).toLowerCase();
    if (!q) return recipientRows;

    return recipientRows.filter((row) =>
      [row.displayName, row.subtitle, row.skills, row.company, row.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [recipientRows, recipientSearch]);

  const selectedRecipientKeySet = useMemo(
    () => new Set(selectedRecipientKeys),
    [selectedRecipientKeys]
  );

  const activeSequenceItems = useMemo(
    () => recipientRows.filter((row) => selectedRecipientKeySet.has(row.key)),
    [recipientRows, selectedRecipientKeySet]
  );
  const previewRecipients = useMemo(
    () => (activeSequenceItems.length ? activeSequenceItems : recipientRows),
    [activeSequenceItems, recipientRows]
  );

  const selectedCompanyDetails = useMemo(
    () =>
      buildCompanyLookupOption({
        company_id: selectedCompanyOption?.company_id || selectedCompanyProfile?.id,
        company_name:
          form.companyName ||
          selectedCompanyOption?.company_name ||
          selectedCompanyProfile?.company_name ||
          selectedCompanyProfile?.name,
        company_logo:
          selectedCompanyOption?.company_logo ||
          selectedCompanyProfile?.company_logo ||
          selectedCompanyProfile?.logo,
        company_website:
          selectedCompanyOption?.website ||
          selectedCompanyProfile?.company_website ||
          selectedCompanyProfile?.website,
        industry:
          selectedCompanyOption?.industry ||
          selectedCompanyProfile?.industry ||
          selectedCompanyProfile?.industry_sector,
        size_range:
          selectedCompanyOption?.size_range ||
          selectedCompanyProfile?.company_size ||
          selectedCompanyProfile?.size_range,
        location: selectedCompanyOption?.location || selectedCompanyProfile?.location,
        status: selectedCompanyOption?.status || selectedCompanyProfile?.status,
        source: selectedCompanyOption?.source || selectedCompanyProfile?.source,
      }),
    [
      form.companyName,
      selectedCompanyProfile?.company_logo,
      selectedCompanyProfile?.company_name,
      selectedCompanyProfile?.company_size,
      selectedCompanyProfile?.company_website,
      selectedCompanyProfile?.id,
      selectedCompanyProfile?.industry,
      selectedCompanyProfile?.industry_sector,
      selectedCompanyProfile?.location,
      selectedCompanyProfile?.logo,
      selectedCompanyProfile?.name,
      selectedCompanyProfile?.size_range,
      selectedCompanyProfile?.source,
      selectedCompanyProfile?.status,
      selectedCompanyProfile?.website,
      selectedCompanyOption?.company_id,
      selectedCompanyOption?.company_logo,
      selectedCompanyOption?.company_name,
      selectedCompanyOption?.industry,
      selectedCompanyOption?.location,
      selectedCompanyOption?.size_range,
      selectedCompanyOption?.source,
      selectedCompanyOption?.status,
      selectedCompanyOption?.website,
    ]
  );
  const selectedJobDetails = useMemo(
    () =>
      buildJobLookupOption({
        id: selectedJobOption?.id,
        job_title: form.selectedJobName || selectedJobOption?.job_title,
        company_id: selectedJobOption?.company_id,
        company_name:
          selectedJobOption?.company_name || selectedCompanyDetails?.company_name || form.companyName,
        company_logo: selectedJobOption?.company_logo,
        location: selectedJobOption?.location,
        employment: selectedJobOption?.employment,
        work_arrangement: selectedJobOption?.work_arrangement,
        status: selectedJobOption?.status,
        source: selectedJobOption?.source || form.jobSource,
        owner: selectedJobOption?.owner,
      }),
    [
      form.companyName,
      form.jobSource,
      form.selectedJobName,
      selectedCompanyDetails?.company_name,
      selectedJobOption?.company_id,
      selectedJobOption?.company_logo,
      selectedJobOption?.company_name,
      selectedJobOption?.employment,
      selectedJobOption?.id,
      selectedJobOption?.job_title,
      selectedJobOption?.location,
      selectedJobOption?.owner,
      selectedJobOption?.source,
      selectedJobOption?.status,
      selectedJobOption?.work_arrangement,
    ]
  );
  const companyLeadDetails = useMemo(
    () =>
      buildSequenceCompanyProfile({
        companyProfile: selectedCompanyProfile,
        leadInfo: {
          ...(selectedLeadInfo && typeof selectedLeadInfo === "object" ? selectedLeadInfo : {}),
          company_name:
            selectedLeadMatch?.company_name || selectedLeadInfo?.company_name || "",
        },
        companyDetails: {
          company: selectedCompanyDetails?.company_name || form.companyName,
          website: selectedCompanyDetails?.website,
          industry: selectedCompanyDetails?.industry,
          sizeRange: selectedCompanyDetails?.size_range,
          location: selectedCompanyDetails?.location,
          status: selectedCompanyDetails?.status,
          source: selectedJobDetails?.source || selectedCompanyDetails?.source || form.jobSource,
        },
        jobDetails: selectedJobFullDetails || selectedJobDetails,
      }),
    [
      form.companyName,
      form.jobSource,
      selectedCompanyDetails?.company_name,
      selectedCompanyDetails?.industry,
      selectedCompanyDetails?.location,
      selectedCompanyDetails?.size_range,
      selectedCompanyDetails?.source,
      selectedCompanyDetails?.status,
      selectedCompanyDetails?.website,
      selectedCompanyProfile,
      selectedJobDetails,
      selectedJobFullDetails,
      selectedLeadInfo,
      selectedLeadMatch?.company_name,
    ]
  );
  const sequenceCompanyDetails = useMemo(
    () => ({
      companyId: selectedCompanyDetails?.company_id || null,
      company: normalizeSequenceString(
        selectedCompanyDetails?.company_name || companyLeadDetails.company_name || form.companyName,
        160
      ),
      contact: normalizeSequenceString(form.contactName, 160),
      website: normalizeUrlValue(selectedCompanyDetails?.website || companyLeadDetails.company_website),
      industry: normalizeSequenceString(
        selectedCompanyDetails?.industry || companyLeadDetails.industry_sector,
        160
      ),
      sizeRange: normalizeSequenceString(
        selectedCompanyDetails?.size_range || companyLeadDetails.company_size,
        120
      ),
      location: normalizeSequenceString(
        selectedCompanyDetails?.location || companyLeadDetails.location,
        160
      ),
      status: normalizeSequenceString(
        selectedCompanyDetails?.status || companyLeadDetails.status,
        80
      ),
      source: normalizeSequenceString(
        selectedJobDetails?.source ||
          selectedCompanyDetails?.source ||
          companyLeadDetails.source ||
          form.jobSource,
        80
      ),
      jobId: selectedJobDetails?.id || null,
      job: normalizeSequenceString(selectedJobDetails?.job_title || form.selectedJobName, 160),
      jobLocation: normalizeSequenceString(selectedJobDetails?.location, 160),
      jobStatus: normalizeSequenceString(selectedJobDetails?.status, 80),
      employment: normalizeSequenceString(selectedJobDetails?.employment, 120),
      workArrangement: normalizeSequenceString(selectedJobDetails?.work_arrangement, 120),
      owner: normalizeSequenceString(selectedJobDetails?.owner, 160),
    }),
    [
      companyLeadDetails.company_name,
      companyLeadDetails.company_size,
      companyLeadDetails.company_website,
      companyLeadDetails.industry_sector,
      companyLeadDetails.location,
      companyLeadDetails.source,
      companyLeadDetails.status,
      form.companyName,
      form.contactName,
      form.jobSource,
      form.selectedJobName,
      selectedCompanyDetails?.company_id,
      selectedCompanyDetails?.company_name,
      selectedCompanyDetails?.industry,
      selectedCompanyDetails?.location,
      selectedCompanyDetails?.size_range,
      selectedCompanyDetails?.source,
      selectedCompanyDetails?.status,
      selectedCompanyDetails?.website,
      selectedJobDetails?.employment,
      selectedJobDetails?.id,
      selectedJobDetails?.job_title,
      selectedJobDetails?.location,
      selectedJobDetails?.owner,
      selectedJobDetails?.source,
      selectedJobDetails?.status,
      selectedJobDetails?.work_arrangement,
    ]
  );
  const jobEmailDetails = useMemo(
    () =>
      buildJobEmailDetails(
        {
          ...(selectedJobDetails && typeof selectedJobDetails === "object" ? selectedJobDetails : {}),
          ...(selectedJobFullDetails && typeof selectedJobFullDetails === "object" ? selectedJobFullDetails : {}),
          job_detail_url: buildInternalJobDetailUrl(
            selectedJobFullDetails?.id || selectedJobDetails?.id,
            "details"
          ),
        },
        sequenceCompanyDetails
      ),
    [selectedJobDetails, selectedJobFullDetails, sequenceCompanyDetails]
  );
  const emailCampaignDetailsText = useMemo(
    () =>
      buildEmailCampaignDetailsText({
        companyDetails: sequenceCompanyDetails,
        leadInfo: companyLeadDetails,
        jobDetails: jobEmailDetails,
      }),
    [companyLeadDetails, jobEmailDetails, sequenceCompanyDetails]
  );
  const hasStructuredCampaignContext = useMemo(
    () =>
      form.campaignType === "recruiting" &&
      Boolean(
        selectedCompanyDetails?.company_id ||
          selectedJobDetails?.id ||
          selectedLeadMatch?.id ||
          normalizeSequenceString(
            [
              companyLeadDetails.full_company_description,
              companyLeadDetails.mission_and_vision,
              companyLeadDetails.key_products_services,
              companyLeadDetails.industry_sector,
              companyLeadDetails.company_website,
              jobEmailDetails.jobDescription,
              jobEmailDetails.responsibilities,
              jobEmailDetails.qualifications,
              jobEmailDetails.jobTitle,
            ]
              .filter(Boolean)
              .join("\n"),
            8000
          )
      ),
    [
      companyLeadDetails.company_website,
      companyLeadDetails.full_company_description,
      companyLeadDetails.industry_sector,
      companyLeadDetails.key_products_services,
      companyLeadDetails.mission_and_vision,
      form.campaignType,
      jobEmailDetails.jobDescription,
      jobEmailDetails.jobTitle,
      jobEmailDetails.qualifications,
      jobEmailDetails.responsibilities,
      selectedCompanyDetails?.company_id,
      selectedJobDetails?.id,
      selectedLeadMatch?.id,
    ]
  );
  const shouldAttemptCompanyEnrichment = useMemo(
    () =>
      form.campaignType === "recruiting" &&
      Boolean(sequenceCompanyDetails.company) &&
      hasMissingStructuredCompanyFields(companyLeadDetails, jobEmailDetails),
    [
      companyLeadDetails,
      form.campaignType,
      jobEmailDetails,
      sequenceCompanyDetails.company,
    ]
  );
  const shouldAttemptJobEnrichment = useMemo(
    () =>
      form.campaignType === "recruiting" &&
      Boolean(selectedJobDetails?.id) &&
      listMissingStructuredEmailFields(companyLeadDetails, jobEmailDetails).some((field) =>
        [
          "job title",
          "role overview",
          "job link",
          "job responsibilities",
          "job qualifications",
          "job skills",
        ].includes(field)
      ),
    [companyLeadDetails, form.campaignType, jobEmailDetails, selectedJobDetails?.id]
  );
  const missingStructuredEmailFields = useMemo(
    () => listMissingStructuredEmailFields(companyLeadDetails, jobEmailDetails),
    [companyLeadDetails, jobEmailDetails]
  );
  const requiresExplicitAudienceSelection = normalizedEntityType === "candidate" || normalizedEntityType === "lead";
  const hasRequiredAudienceSelection = useMemo(() => {
    const hasCompany =
      Boolean(selectedCompanyDetails?.company_id) ||
      Boolean(normalizeSequenceString(form.companyName, 160));
    const hasJob = Boolean(selectedJobDetails?.id);

    if (normalizedEntityType === "candidate") {
      return hasCompany && hasJob;
    }

    if (normalizedEntityType === "lead") {
      return hasJob;
    }

    return true;
  }, [
    form.companyName,
    normalizedEntityType,
    selectedCompanyDetails?.company_id,
    selectedJobDetails?.id,
  ]);

  const persistJobEmailContext = useCallback(
    async (nextJob = {}) => {
      const jobId = Number(selectedJobFullDetails?.id || selectedJobDetails?.id || 0);
      if (!Number.isFinite(jobId) || jobId <= 0) return nextJob;

      const response = await fetch(apiUrl(`/jobs/${jobId}/sequence-email-context`), {
        method: "PATCH",
        headers: authHeaders(true),
        body: JSON.stringify(buildJobEmailContextPersistencePayload(nextJob)),
      });

      if (!response.ok) {
        throw new Error(
          await readResponseError(
            response,
            `Failed to save enriched job email data (${response.status})`
          )
        );
      }

      const payload = await response.json().catch(() => ({}));
      return payload?.job && typeof payload.job === "object" ? payload.job : nextJob;
    },
    [selectedJobDetails, selectedJobFullDetails]
  );

  const persistCompanyProfile = useCallback(
    async (nextCompanyProfile = {}) => {
      const normalizedCompanyProfile = buildSequenceCompanyProfile({
        companyProfile: nextCompanyProfile,
        leadInfo: companyLeadDetails,
        companyDetails: sequenceCompanyDetails,
        jobDetails: jobEmailDetails,
      });
      const companyName = normalizeSequenceString(
        sequenceCompanyDetails.company ||
          normalizedCompanyProfile.company_name ||
          jobEmailDetails.companyName,
        160
      );

      let persistedCompanyProfile = null;
      if (companyName) {
        const companyResponse = await fetch(apiUrl("/companies"), {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify(
            buildCompanyPersistencePayload({
              companyName,
              companyProfile: normalizedCompanyProfile,
              companyDetails: sequenceCompanyDetails,
              ownerName: form.ownerName || effectiveUser?.name || form.fromName,
            })
          ),
        });

        if (!companyResponse.ok) {
          throw new Error(
            await readResponseError(
              companyResponse,
              `Failed to save company profile (${companyResponse.status})`
            )
          );
        }

        const companyPayload = await companyResponse.json().catch(() => ({}));
        persistedCompanyProfile =
          companyPayload?.company && typeof companyPayload.company === "object"
            ? companyPayload.company
            : companyPayload && typeof companyPayload === "object"
              ? companyPayload
              : null;

        if (persistedCompanyProfile) {
          setSelectedCompanyProfile((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            ...persistedCompanyProfile,
          }));

          const nextCompanyOption = buildCompanyLookupOption({
            ...persistedCompanyProfile,
            company_id:
              persistedCompanyProfile?.id ??
              persistedCompanyProfile?.company_id ??
              selectedCompanyOption?.company_id,
          });

          if (nextCompanyOption) {
            setSelectedCompanyOption(nextCompanyOption);
            setCompanyLookupOptions((prev) =>
              mergeLookupOptions(prev, [nextCompanyOption], getCompanyLookupKey)
            );
          }
        }
      }

      if (selectedLeadMatch?.id) {
        const leadPayload = buildLeadCompanyPersistencePayload({
          companyProfile: persistedCompanyProfile || normalizedCompanyProfile,
          companyDetails: sequenceCompanyDetails,
        });
        const leadResponse = await fetch(apiUrl(`/leads/${selectedLeadMatch.id}/info`), {
          method: "PUT",
          headers: authHeaders(true),
          body: JSON.stringify(leadPayload),
        });

        if (!leadResponse.ok) {
          throw new Error(
            await readResponseError(
              leadResponse,
              `Failed to save lead company info (${leadResponse.status})`
            )
          );
        }

        setSelectedLeadInfo((prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          ...leadPayload,
          company_name:
            companyName ||
            normalizeSequenceString(
              prev?.company_name || selectedLeadMatch?.company_name,
              160
            ),
        }));
      }

      return buildSequenceCompanyProfile({
        companyProfile: persistedCompanyProfile || normalizedCompanyProfile,
        leadInfo: {
          ...(selectedLeadInfo && typeof selectedLeadInfo === "object" ? selectedLeadInfo : {}),
          ...buildLeadCompanyPersistencePayload({
            companyProfile: persistedCompanyProfile || normalizedCompanyProfile,
            companyDetails: sequenceCompanyDetails,
          }),
          company_name:
            companyName ||
            normalizeSequenceString(
              selectedLeadInfo?.company_name || selectedLeadMatch?.company_name,
              160
            ),
        },
        companyDetails: sequenceCompanyDetails,
        jobDetails: jobEmailDetails,
      });
    },
    [
      companyLeadDetails,
      effectiveUser?.name,
      form.fromName,
      form.ownerName,
      jobEmailDetails,
      selectedCompanyOption?.company_id,
      selectedLeadInfo,
      selectedLeadMatch?.company_name,
      selectedLeadMatch?.id,
      sequenceCompanyDetails,
    ]
  );

  const enrichCompanyProfileIfNeeded = useCallback(async (options = {}) => {
    const onProgress = typeof options?.onProgress === "function" ? options.onProgress : null;
    if (!shouldAttemptCompanyEnrichment) return companyLeadDetails;

    onProgress?.({
      message: "Collecting missing company details with AI...",
      companyProfile: companyLeadDetails,
      jobDetails: jobEmailDetails,
    });

    const enrichmentPayload = buildCompanyEnrichmentPayload({
      companyDetails: sequenceCompanyDetails,
      companyProfile: companyLeadDetails,
      jobDetails: jobEmailDetails,
    });

    if (!enrichmentPayload.company_name) {
      return companyLeadDetails;
    }

    let workingCompanyProfile = buildSequenceCompanyProfile({
      companyProfile: companyLeadDetails,
      leadInfo: companyLeadDetails,
      companyDetails: sequenceCompanyDetails,
      jobDetails: jobEmailDetails,
    });
    let patch = {};
    const companyFieldKeys = new Set([
      "location",
      "industry_sector",
      "founded_year",
      "work_policies",
      "full_company_description",
      "mission_and_vision",
      "key_products_services",
      "unique_selling_points",
      "employee_perks",
      "achievements_awards",
      "recruitment_tools",
      "job_boards",
      "company_website",
      "career_site",
      "company_link",
      "company_size",
      "ownership_type",
      "parent_company",
      "job_board_rating",
      "company_email",
      "company_phone",
      "elevator_pitch",
    ]);
    const applyCompanyPatch = (value = {}) => {
      const normalizedPatch = buildCompanyEnrichmentPatch(value);
      const meaningfulEntries = Object.entries(normalizedPatch).filter(([key, fieldValue]) => {
        if (!companyFieldKeys.has(key)) return false;
        if (Array.isArray(fieldValue)) {
          if (!fieldValue.length) return false;
          const existingValue = Array.isArray(workingCompanyProfile[key])
            ? workingCompanyProfile[key]
            : [];
          return (
            normalizeSequenceString(fieldValue.join(", "), 4000) !==
            normalizeSequenceString(existingValue.join(", "), 4000)
          );
        }
        if (!fieldValue) return false;
        return (
          normalizeSequenceString(fieldValue, 8000) !==
          normalizeSequenceString(workingCompanyProfile[key], 8000)
        );
      });

      if (!meaningfulEntries.length) return;

      const nextPatch = Object.fromEntries(meaningfulEntries);
      patch = { ...patch, ...nextPatch };
      workingCompanyProfile = buildSequenceCompanyProfile({
        companyProfile: { ...workingCompanyProfile, ...nextPatch },
        leadInfo: companyLeadDetails,
        companyDetails: sequenceCompanyDetails,
        jobDetails: jobEmailDetails,
      });
    };

    const response = await fetch(apiUrl("/candidates/enrich-company"), {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(enrichmentPayload),
    });

    if (!response.ok) {
      throw new Error(
        await readResponseError(
          response,
          `Failed to enrich company profile (${response.status})`
        )
      );
    }

    const payload = await response.json().catch(() => ({}));
    const parsed =
      payload?.parsed && typeof payload.parsed === "object"
        ? payload.parsed
        : payload?.data?.parsed && typeof payload.data.parsed === "object"
          ? payload.data.parsed
          : null;

    if (parsed) {
      applyCompanyPatch(parsed);
    }

    if (Object.keys(patch).length) {
      workingCompanyProfile = await persistCompanyProfile(workingCompanyProfile);
      patch = {};
      onProgress?.({
        message: "Company details saved to the database.",
        companyProfile: workingCompanyProfile,
        jobDetails: jobEmailDetails,
      });
    }

    const remainingCompanyMissing = listMissingStructuredEmailFields(
      workingCompanyProfile,
      jobEmailDetails
    ).filter((field) =>
      [
        "company description",
        "company mission and values",
        "company key products/services",
        "company website or career page",
      ].includes(field)
    );

    if (remainingCompanyMissing.length) {
      const token = getToken();
      if (token) {
        try {
          onProgress?.({
            message: "Collecting remaining company details with AI...",
            companyProfile: workingCompanyProfile,
            jobDetails: jobEmailDetails,
          });

          const contextText = buildEmailCampaignDetailsText({
            companyDetails: sequenceCompanyDetails,
            leadInfo: workingCompanyProfile,
            jobDetails: jobEmailDetails,
          });
          const prompt = [
            "You are enriching a company profile for a recruiting sequence.",
            "Use trustworthy public company information when needed. If a field cannot be supported with reasonable confidence, return an empty string or an empty array.",
            "Return valid JSON only with these keys: full_company_description, mission_and_vision, key_products_services, company_website, career_site, industry_sector, founded_year, company_size, ownership_type, parent_company, work_policies, recruitment_tools, job_boards, job_board_rating, company_email, company_phone, elevator_pitch, unique_selling_points, employee_perks, achievements_awards, location.",
            `Still missing: ${remainingCompanyMissing.join(", ")}`,
            `Current structured data:\n${contextText}`,
          ].join("\n\n");

          const formData = new FormData();
          formData.append("message", prompt);
          formData.append("mode", "compose");

          const fallbackResponse = await fetch(`${API_ROOT}/ai-agent/respond`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });

          if (fallbackResponse.ok) {
            const fallbackPayload = await fallbackResponse.json().catch(() => ({}));
            const rawAnswer =
              fallbackPayload?.answer ||
              fallbackPayload?.assistant ||
              fallbackPayload?.message ||
              "";
            const fallbackParsed = extractJsonObjectFromText(rawAnswer);
            if (fallbackParsed) {
              applyCompanyPatch(fallbackParsed);
            }
          }
        } catch (error) {
          console.error("AI company enrichment fallback failed:", error);
        }
      }
    }

    if (!Object.keys(patch).length) return workingCompanyProfile;

    const normalizedCompanyProfile = workingCompanyProfile;

    const persistedCompanyProfile = await persistCompanyProfile(normalizedCompanyProfile);
    onProgress?.({
      message: "Company details saved to the database.",
      companyProfile: persistedCompanyProfile,
      jobDetails: jobEmailDetails,
    });

    return persistedCompanyProfile;
  }, [
    companyLeadDetails,
    jobEmailDetails,
    persistCompanyProfile,
    sequenceCompanyDetails,
    shouldAttemptCompanyEnrichment,
  ]);

  const enrichJobDetailsIfNeeded = useCallback(
    async (companyProfileOverride = companyLeadDetails, options = {}) => {
      const onProgress = typeof options?.onProgress === "function" ? options.onProgress : null;
      if (!shouldAttemptJobEnrichment) {
        return jobEmailDetails;
      }

      const companyContext = buildSequenceCompanyProfile({
        companyProfile: companyProfileOverride,
        leadInfo: companyProfileOverride,
        companyDetails: sequenceCompanyDetails,
        jobDetails: jobEmailDetails,
      });

      let workingJob = {
        ...(selectedJobDetails && typeof selectedJobDetails === "object" ? selectedJobDetails : {}),
        ...(selectedJobFullDetails && typeof selectedJobFullDetails === "object" ? selectedJobFullDetails : {}),
      };
      let nextJobDetails = buildJobEmailDetails(workingJob, sequenceCompanyDetails);
      const initialMissingFields = listMissingStructuredEmailFields(
        companyContext,
        nextJobDetails
      );
      const initialJobMissing = initialMissingFields.filter((field) =>
        [
          "job title",
          "role overview",
          "job link",
          "job responsibilities",
          "job qualifications",
          "job skills",
        ].includes(field)
      );

      if (!initialJobMissing.length) {
        return nextJobDetails;
      }

      let patch = {};
      const applyPatch = (value = {}) => {
        const normalizedPatch = buildJobEnrichmentPatch(value);
        const meaningfulEntries = Object.entries(normalizedPatch).filter(([key, fieldValue]) => {
          if (Array.isArray(fieldValue)) return fieldValue.length > 0;
          if (!fieldValue) return false;
          if (["job_title", "location", "industry"].includes(key)) {
            const existingValue = normalizeSequenceString(workingJob[key], 255);
            return normalizeSequenceString(fieldValue, 255) !== existingValue;
          }
          return true;
        });

        if (!meaningfulEntries.length) return;

        const nextPatch = Object.fromEntries(meaningfulEntries);
        patch = { ...patch, ...nextPatch };
        workingJob = { ...workingJob, ...nextPatch };
        nextJobDetails = buildJobEmailDetails(workingJob, sequenceCompanyDetails);
      };

      const saveCollectedJobDetails = async () => {
        if (!Object.keys(patch).length) return;
        onProgress?.({
          message: "Saving collected job details to the database...",
          companyProfile: companyContext,
          jobDetails: nextJobDetails,
        });
        const persistedJob = await persistJobEmailContext(workingJob);
        workingJob = {
          ...workingJob,
          ...(persistedJob && typeof persistedJob === "object" ? persistedJob : {}),
        };
        nextJobDetails = buildJobEmailDetails(workingJob, sequenceCompanyDetails);
        setSelectedJobFullDetails(workingJob);
        patch = {};
        onProgress?.({
          message: "Job details saved to the database.",
          companyProfile: companyContext,
          jobDetails: nextJobDetails,
        });
      };

      const jobLink = normalizeUrlValue(
        nextJobDetails.jobLink || nextJobDetails.careerWebsite || nextJobDetails.companyWebsite
      );

      if (jobLink) {
        try {
          onProgress?.({
            message: "Collecting job details from the selected job posting...",
            companyProfile: companyContext,
            jobDetails: nextJobDetails,
          });
          const response = await fetch(apiUrl("/jobs/parse-link"), {
            method: "POST",
            headers: authHeaders(true),
            body: JSON.stringify({
              url: jobLink,
              company_id: selectedCompanyDetails?.company_id,
              company_name: companyContext.company_name || nextJobDetails.companyName,
              company_website: companyContext.company_website || nextJobDetails.companyWebsite,
              source: nextJobDetails.source || selectedJobDetails?.source,
            }),
          });

          if (response.ok) {
            const payload = await response.json().catch(() => ({}));
            if (payload?.parsed && typeof payload.parsed === "object") {
              applyPatch(payload.parsed);
            }
          }
        } catch (error) {
          console.error("Job link enrichment failed:", error);
        }
      }

      await saveCollectedJobDetails();

      const remainingAfterLink = listMissingStructuredEmailFields(companyContext, nextJobDetails).filter((field) =>
        [
          "job title",
          "role overview",
          "job link",
          "job responsibilities",
          "job qualifications",
          "job skills",
        ].includes(field)
      );

      if (remainingAfterLink.length) {
        const token = getToken();
        if (token) {
          try {
            onProgress?.({
              message: "Collecting remaining job details with AI...",
              companyProfile: companyContext,
              jobDetails: nextJobDetails,
            });
            const contextText = buildEmailCampaignDetailsText({
              companyDetails: sequenceCompanyDetails,
              leadInfo: companyContext,
              jobDetails: nextJobDetails,
            });
            const prompt = [
              "You are enriching a recruiting job record for a sequence email.",
              "Use only the company and job information provided below. If a field cannot be inferred with reasonable confidence, return an empty string or an empty array.",
              "Return valid JSON only with these keys: job_description, responsibilities, qualifications, hard_skills, soft_skills, salary_range, employment_type, work_arrangement, industry, company_size, location, company_website, company_career_website, post_link.",
              `Current structured data:\n${contextText}`,
            ].join("\n\n");

            const formData = new FormData();
            formData.append("message", prompt);
            formData.append("mode", "compose");

            const response = await fetch(`${API_ROOT}/ai-agent/respond`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: formData,
            });

            if (response.ok) {
              const payload = await response.json().catch(() => ({}));
              const rawAnswer = payload?.answer || payload?.assistant || payload?.message || "";
              const parsed = extractJsonObjectFromText(rawAnswer);
              if (parsed) {
                applyPatch(parsed);
              }
            }
          } catch (error) {
            console.error("AI job enrichment failed:", error);
          }
        }
      }

      if (Object.keys(patch).length) {
        await saveCollectedJobDetails();

        const finalMissingFields = listMissingStructuredEmailFields(companyContext, nextJobDetails).filter((field) =>
          [
            "job title",
            "role overview",
            "job link",
            "job responsibilities",
            "job qualifications",
            "job skills",
          ].includes(field)
        );

        if (finalMissingFields.length < initialJobMissing.length) {
          setToast({
            open: true,
            type: "success",
            msg: "AI filled the missing job email data from the selected company and job context.",
          });
        }
      }

      return nextJobDetails;
    },
    [
      companyLeadDetails,
      jobEmailDetails,
      persistJobEmailContext,
      selectedJobDetails,
      selectedJobFullDetails,
      sequenceCompanyDetails,
      selectedCompanyDetails?.company_id,
      shouldAttemptJobEnrichment,
      setToast,
    ]
  );

  const ensureAudienceEmailContext = useCallback(async (initialMissingFieldsOverride = null) => {
    if (normalizedEntityType === "candidate" && !normalizeSequenceString(form.companyName, 160)) {
      throw new Error("Select or enter a company before continuing.");
    }

    if (requiresExplicitAudienceSelection && !selectedJobDetails?.id) {
      throw new Error("Select a job from the database before continuing.");
    }

    const initialMissingFields = Array.isArray(initialMissingFieldsOverride)
      ? initialMissingFieldsOverride
      : listMissingStructuredEmailFields(companyLeadDetails, jobEmailDetails);
    let nextCompanyProfile = companyLeadDetails;
    let nextJobDetails = jobEmailDetails;
    const totalFields = initialMissingFields.length;
    const updateAudienceProgress = (message, companyProfile = nextCompanyProfile, jobDetails = nextJobDetails, status = "loading") => {
      const remainingFields = listMissingStructuredEmailFields(companyProfile, jobDetails);
      const collectedFields = initialMissingFields.filter((field) => !remainingFields.includes(field));

      setAudiencePreparation({
        status,
        message,
        totalFields,
        resolvedFields: collectedFields.length,
        remainingFields,
        collectedFields,
      });
    };

    updateAudienceProgress(
      totalFields
        ? `Reviewing ${totalFields} missing structured field${totalFields === 1 ? "" : "s"}...`
        : "Reviewing email context..."
    );

    if (form.campaignType === "recruiting") {
      nextCompanyProfile = await enrichCompanyProfileIfNeeded({
        onProgress: ({ message, companyProfile, jobDetails }) =>
          updateAudienceProgress(
            message,
            companyProfile || nextCompanyProfile,
            jobDetails || nextJobDetails
          ),
      });
      nextJobDetails = await enrichJobDetailsIfNeeded(nextCompanyProfile, {
        onProgress: ({ message, companyProfile, jobDetails }) =>
          updateAudienceProgress(
            message,
            companyProfile || nextCompanyProfile,
            jobDetails || nextJobDetails
          ),
      });
    }

    const nextMissingFields = listMissingStructuredEmailFields(
      nextCompanyProfile,
      nextJobDetails
    );
    const collectedFields = initialMissingFields.filter((field) => !nextMissingFields.includes(field));

    if (nextMissingFields.length) {
      updateAudienceProgress(
        `Still missing required structured data: ${nextMissingFields.join(", ")}.`,
        nextCompanyProfile,
        nextJobDetails,
        "error"
      );
      return {
        companyProfile: nextCompanyProfile,
        jobDetails: nextJobDetails,
        collectedFields,
        nextMissingFields,
      };
    }

    updateAudienceProgress(
      collectedFields.length
        ? `AI collected ${collectedFields.length} missing structured field${collectedFields.length === 1 ? "" : "s"}.`
        : "All required email data is ready.",
      nextCompanyProfile,
      nextJobDetails,
      "success"
    );

    return {
      companyProfile: nextCompanyProfile,
      jobDetails: nextJobDetails,
      collectedFields,
      nextMissingFields,
    };
  }, [
    companyLeadDetails,
    enrichCompanyProfileIfNeeded,
    enrichJobDetailsIfNeeded,
    form.campaignType,
    form.companyName,
    jobEmailDetails,
    normalizedEntityType,
    requiresExplicitAudienceSelection,
    selectedJobDetails?.id,
  ]);

  useEffect(() => {
    if (!open || !hasStructuredCampaignContext) return;

    const legacyBody = normalizeSequenceText(LEGACY_CANDIDATE_SEQUENCE_BODY);
    setSteps((prev) => {
      let changed = false;
      const next = prev.map((step) => {
        if (step.channel !== "email") return step;
        if (normalizeSequenceText(step.body || "") !== legacyBody) return step;
        changed = true;
        return {
          ...step,
          body: STRUCTURED_RECRUITING_SEQUENCE_BODY,
        };
      });

      return changed ? next : prev;
    });
  }, [hasStructuredCampaignContext, open]);

  const relatedCompanyLabel =
    normalizeSequenceString(selectedCompanyDetails?.company_name || form.companyName, 160) ||
    relatedSelections.find((item) => item.kind === "company")?.label ||
    recipientRows[0]?.company ||
    "";
  const relatedJobLabel =
    normalizeSequenceString(selectedJobDetails?.job_title || form.selectedJobName, 160) ||
    relatedSelections.find((item) => item.kind === "job")?.label ||
    recipientRows[0]?.title ||
    "";
  const campaignHeaderTitle =
    [relatedCompanyLabel, relatedJobLabel].filter(Boolean).length
      ? `Campaign - ${[relatedCompanyLabel, relatedJobLabel].filter(Boolean).join(" - ")}`
      : form.name || "Campaign";
  const campaignInfoParts = [
    form.campaignType === "sales" ? "Sales" : "Recruiting",
    relatedCompanyLabel,
    relatedJobLabel ? "Job" : "",
    new Date().toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  ].filter(Boolean);
  const relatedToOptions = useMemo(() => {
    const seen = new Set();
    const labels = [];
    const push = (value) => {
      const normalized = normalizeSequenceString(value, 120);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      labels.push(normalized);
    };

    push(relatedJobLabel);
    relatedSelections.forEach((item) => push(item.label));
    push("Job Lead");

    return labels;
  }, [relatedJobLabel, relatedSelections]);
  const ownershipOptions = useMemo(() => {
    const seen = new Set();
    const labels = [];
    const push = (value) => {
      const normalized = normalizeSequenceString(value, 120);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      labels.push(normalized);
    };

    push(form.ownerName);
    push(defaults.fromName);
    push(effectiveUser?.name);
    sequenceItems.forEach((item) => push(item.owner));

    return labels.length ? labels : ["Owner"];
  }, [defaults.fromName, effectiveUser?.name, form.ownerName, sequenceItems]);
  const contactOptions = useMemo(() => {
    const seen = new Set();
    const labels = [];
    const push = (value) => {
      const normalized = normalizeSequenceString(value, 160);
      if (!normalized) return;
      const key = normalized.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      labels.push(normalized);
    };

    push(form.contactName);
    push(form.ownerName);
    sequenceItems.forEach((item) => {
      const contactLabel =
        item.name && item.title
          ? `${item.name} (${item.title})`
          : item.name || item.owner || item.email || "";
      push(contactLabel);
    });

    return labels.length ? labels : [form.ownerName || "Owner"];
  }, [form.contactName, form.ownerName, sequenceItems]);
  const contactLookupOptions = platformContactOptions;
  useEffect(() => {
    setPlatformContactOptions([]);
    setContactLookupLoading(false);
    const companyId = Number(selectedCompanyOption?.company_id || 0);
    if (!open || !Number.isFinite(companyId) || companyId <= 0) return undefined;

    const controller = new AbortController();
    setContactLookupLoading(true);
    fetchCompanyContactOptions({ companyId, signal: controller.signal })
      .then((rows) => {
        if (!controller.signal.aborted) setPlatformContactOptions(rows);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setPlatformContactOptions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setContactLookupLoading(false);
      });
    return () => controller.abort();
  }, [open, selectedCompanyOption?.company_id]);
  const senderOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    const push = (name, email) => {
      const normalizedName = normalizeSequenceString(name, 120);
      const normalizedEmail = normalizeSequenceString(email, 160);
      if (!normalizedName && !normalizedEmail) return;
      const key = `${normalizedName.toLowerCase()}::${normalizedEmail.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        key,
        name: normalizedName,
        email: normalizedEmail,
        label:
          normalizedName && normalizedEmail
            ? `${normalizedName} <${normalizedEmail}>`
            : normalizedName || normalizedEmail,
      });
    };

    push(activeStep?.senderName, activeStep?.senderEmail);
    push(form.fromName, form.fromEmail);
    push(defaults.fromName, defaults.fromEmail);
    push(effectiveUser?.name, effectiveUser?.email);

    return options.length
      ? options
      : [
          {
            key: "sender::",
            name: "",
            email: "",
            label: "Select sender",
          },
        ];
  }, [
    activeStep?.senderEmail,
    activeStep?.senderName,
    defaults.fromEmail,
    defaults.fromName,
    form.fromEmail,
    form.fromName,
    effectiveUser?.email,
    effectiveUser?.name,
  ]);
  const activeSenderKey = useMemo(() => {
    const currentKey = `${normalizeSequenceString(activeStep?.senderName, 120).toLowerCase()}::${normalizeSequenceString(
      activeStep?.senderEmail,
      160
    ).toLowerCase()}`;
    return senderOptions.some((option) => option.key === currentKey) ? currentKey : senderOptions[0]?.key || "";
  }, [activeStep?.senderEmail, activeStep?.senderName, senderOptions]);
  const activeDelayPreset = useMemo(() => {
    if (!activeStep) return "immediately";
    if (activeStep.scheduleType === "specific-date") return "specific-date";
    if (activeStep.scheduleType === "next-business-day") return "next-business-day";
    if (activeStep.scheduleType === "immediately") return "immediately";
    if (Number(activeStep.delayValue) === 0) return "immediately";
    return "next-business-day";
  }, [activeStep]);
  const activeScheduledTime = activeStep?.scheduledTime || DEFAULT_STEP_TIME;
  const activeScheduledDate = activeStep?.scheduledDate || "";
  const activeTimezoneLabel = activeStep?.timezoneLabel || DEFAULT_STEP_TIMEZONE;
  const visibleRecipientKeys = useMemo(
    () => filteredRecipientRows.map((row) => row.key),
    [filteredRecipientRows]
  );
  const allVisibleRecipientsSelected = useMemo(
    () =>
      visibleRecipientKeys.length > 0 &&
      visibleRecipientKeys.every((key) => selectedRecipientKeySet.has(key)),
    [visibleRecipientKeys, selectedRecipientKeySet]
  );
  const someVisibleRecipientsSelected = useMemo(
    () =>
      !allVisibleRecipientsSelected &&
      visibleRecipientKeys.some((key) => selectedRecipientKeySet.has(key)),
    [allVisibleRecipientsSelected, visibleRecipientKeys, selectedRecipientKeySet]
  );

  const canCreate = Boolean(normalizeSequenceString(form.name) && steps.length);
  const canAddToExisting = Boolean(selectedExistingSequence && activeSequenceItems.length);
  const nextDisabled =
    saving ||
    preparingAudience ||
    (screen === "existing" && !canAddToExisting) ||
    (screen === "review" && mode === "new" && !canCreate) ||
    (screen === "audience" &&
      requiresExplicitAudienceSelection &&
      !hasRequiredAudienceSelection);
  const dialogWidth =
    screen === "start"
      ? 430
      : screen === "details" && mode === "new"
        ? 690
      : screen === "recipients" && mode === "new"
        ? 720
      : screen === "steps" && mode === "new"
        ? 720
      : screen === "audience" && mode === "new"
        ? 720
      : screen === "review" && mode === "new"
        ? 1060
      : screen === "preview" && mode === "new"
        ? 1120
      : ["existing", "steps", "audience", "review"].includes(screen)
        ? 1040
        : 620;
  const isCampaignSetupScreen = screen === "start" || (screen === "details" && mode === "new");
  const isRecipientsScreen = screen === "recipients" && mode === "new";
  const isStepSetupScreen = screen === "steps" && mode === "new";
  const isAudienceSetupScreen = screen === "audience" && mode === "new";
  const isReviewComposerScreen = screen === "review" && mode === "new";
  const isPreviewComposerScreen = screen === "preview" && mode === "new";
  const isCampaignInfoScreen =
    isRecipientsScreen ||
    isAudienceSetupScreen ||
    isReviewComposerScreen ||
    isPreviewComposerScreen;
  const isWizardFormScreen =
    isCampaignSetupScreen ||
    isRecipientsScreen ||
    isStepSetupScreen ||
    isAudienceSetupScreen ||
    isReviewComposerScreen ||
    isPreviewComposerScreen;

  useEffect(() => {
    if (!open || mode !== "new") return;

    // Only clean up old injected details blocks on body sync — do NOT inject new ones
    const previousDetailsText = previousEmailDetailsTextRef.current;
    if (!previousDetailsText) {
      previousEmailDetailsTextRef.current = emailCampaignDetailsText;
      return;
    }
    setSteps((prev) =>
      prev.map((step) => {
        if (step.channel !== "email") return step;
        // Remove previously injected details block if present
        let nextBody = String(step.body || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
        if (previousDetailsText && nextBody.includes(previousDetailsText)) {
          nextBody = nextBody.replace(previousDetailsText, "").replace(/\n{3,}/g, "\n\n").trim();
        }
        return nextBody === step.body ? step : { ...step, body: nextBody };
      })
    );
    previousEmailDetailsTextRef.current = emailCampaignDetailsText;
  }, [emailCampaignDetailsText, mode, open]);

  const close = () => {
    if (!saving) onClose?.();
  };

  const setFormField = (key) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateActiveStep = useCallback((updater) => {
    setSteps((prev) =>
      prev.map((step) =>
        step.id === activeStepId
          ? {
              ...step,
              ...(typeof updater === "function" ? updater(step) : updater),
            }
          : step
      )
    );
  }, [activeStepId]);

  const setStepField = (key) => (event) => {
    const value = event?.target?.value ?? event;
    updateActiveStep({
      [key]: key === "delayValue" ? Math.max(0, Number(value) || 0) : value,
    });
  };

  const plainTextToHtml = (value) =>
    String(value || "")
      .split(/\r?\n/)
      .map((line) => `<div>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") || "<br>"}</div>`)
      .join("");

  const enableRichTextEditor = () => {
    const currentBody = String(activeStep?.body || "");
    if (!/<[a-z][\s\S]*>/i.test(currentBody)) {
      updateActiveStep({ body: plainTextToHtml(currentBody) });
    }
  };

  const rememberRichTextSelection = () => {
    const editor = richBodyEditorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection?.rangeCount || !editor.contains(selection.anchorNode)) return;
    richBodySelectionRef.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreRichTextSelection = () => {
    const selection = window.getSelection?.();
    const range = richBodySelectionRef.current;
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const runRichTextCommand = (command, value = null) => {
    const applyCommand = () => {
      const editor = richBodyEditorRef.current;
      if (!editor) return;
      editor.focus({ preventScroll: true });
      restoreRichTextSelection();
      // execCommand preserves the restored selection and, unlike the former
      // manual wrapper, correctly toggles formats on and off.
      document.execCommand(command, false, value);
      const nextBody = editor.innerHTML;
      richBodyLastValueRef.current = nextBody;
      updateActiveStep({ body: nextBody });
      rememberRichTextSelection();
    };

    if (richBodyEditorRef.current) {
      applyCommand();
      return;
    }

    enableRichTextEditor();
    requestAnimationFrame(applyCommand);
  };

  const insertRichTextLink = () => {
    const url = window.prompt("Enter the link URL", "https://");
    if (url) runRichTextCommand("createLink", url);
  };

  const addStep = () => {
    const id = `step-${Date.now()}`;
    setSteps((prev) => {
      const sourceStep =
        prev.find((step) => step.id === activeStepId) ||
        prev[prev.length - 1] ||
        buildInitialCampaignSteps(normalizedEntityType, defaults)[0];

      return [
        ...prev,
        {
          ...createFollowUpStep(sourceStep, prev.length, {
            fromName: form.fromName,
            fromEmail: form.fromEmail,
            entityType: normalizedEntityType,
          }),
          id,
        },
      ];
    });
    setActiveStepId(id);
    setPendingAutoComposeStepId(id);
  };

  const setActiveStepSender = (event) => {
    const nextKey = event?.target?.value ?? "";
    const nextSender = senderOptions.find((option) => option.key === nextKey);
    if (!nextSender) return;

    updateActiveStep({ senderName: nextSender.name, senderEmail: nextSender.email });
  };

  const setActiveStepDelayPreset = (event) => {
    const nextValue = event?.target?.value ?? "immediately";
    const delayMap = {
      immediately: {
        delayValue: 0,
        delayUnit: "minutes",
        scheduleType: "immediately",
      },
      "next-business-day": {
        delayValue: 1,
        delayUnit: "days",
        scheduleType: "next-business-day",
      },
      "specific-date": {
        delayValue: 0,
        delayUnit: "minutes",
        scheduleType: "specific-date",
      },
    };
    const nextDelay = delayMap[nextValue] || delayMap.immediately;

    updateActiveStep((step) => ({
      delayValue: nextDelay.delayValue,
      delayUnit: nextDelay.delayUnit,
      scheduleType: nextDelay.scheduleType,
      scheduledDate: step.scheduledDate || "",
      scheduledTime: step.scheduledTime || DEFAULT_STEP_TIME,
      timezoneLabel: step.timezoneLabel || DEFAULT_STEP_TIMEZONE,
    }));
  };

  const setActiveStepScheduledTime = (event) => {
    const value = event?.target?.value ?? event;
    updateActiveStep({ scheduledTime: value });
  };

  const setActiveStepScheduledDate = (event) => {
    const value = event?.target?.value ?? event;
    updateActiveStep({ scheduledDate: value });
  };

  const setActiveStepTimezone = (event) => {
    const value = event?.target?.value ?? event;
    updateActiveStep({ timezoneLabel: value });
  };

  const removeStep = (id) => {
    setSteps((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((step) => step.id !== id);
      if (!next.find((step) => step.id === activeStepId)) setActiveStepId(next[0]?.id || "");
      return next;
    });
  };

  /* ---- Variables menu helpers ---- */
  const AVAILABLE_VARIABLES = [
    { group: "Recipient", tokens: ["first_name", "last_name", "full_name", "email", "title", "location", "phone", "linkedin_url"] },
    { group: "Company", tokens: ["company_name", "company", "company_website", "company_industry", "company_location", "company_status", "company_size", "company_description", "company_founded_year", "company_ownership", "company_career_website", "company_mission_vision", "company_key_products"] },
    { group: "Job", tokens: ["job_title", "job_location", "job_source", "job_status", "job_employment", "job_work_arrangement", "job_description", "job_role_overview", "job_post_link", "job_detail_link", "job_responsibilities", "job_qualifications", "job_skills", "job_salary", "job_industry"] },
    { group: "Sender", tokens: ["sender_name", "sender_email", "owner_name"] },
    { group: "Other", tokens: ["sequence_name", "contact_name", "to_email"] },
  ];

  const insertVariableAtCursor = (token) => {
    const insertion = `{{${token}}}`;
    const editor = richBodyEditorRef.current;
    if (editor) {
      editor.focus({ preventScroll: true });
      restoreRichTextSelection();
      document.execCommand("insertText", false, insertion);
      const nextBody = editor.innerHTML;
      richBodyLastValueRef.current = nextBody;
      updateActiveStep({ body: nextBody });
      rememberRichTextSelection();
    } else {
      updateActiveStep((previous) => ({ body: `${previous.body || ""}${insertion}` }));
    }
    setVariablesAnchorEl(null);
  };

  const handleAddCustomVariable = () => {
    const name = customVarName.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const value = customVarValue.trim();
    if (!name || !value) return;
    setCustomVariables((prev) => {
      if (prev.some((v) => v.name === name)) return prev.map((v) => (v.name === name ? { ...v, value } : v));
      return [...prev, { name, value }];
    });
    insertVariableAtCursor(name);
    setCustomVarName("");
    setCustomVarValue("");
    setCustomVarDialogOpen(false);
  };

  /* ---- AI Agent compose helper ---- */
  const handleAiCompose = useCallback(async () => {
    if (aiComposing) return;
    setAiComposing(true);
    try {
      const token = getToken();
      let promptCompanyProfile = companyLeadDetails;

      if (shouldAttemptCompanyEnrichment) {
        try {
          promptCompanyProfile = await enrichCompanyProfileIfNeeded();
        } catch (enrichmentError) {
          console.error("Company enrichment failed during AI compose:", enrichmentError);
          setToast({
            open: true,
            type: "warning",
            msg:
              enrichmentError?.message ||
              "Company enrichment failed, so AI compose used the existing stored company data.",
          });
        }
      }

      const promptCompanyDetails = {
        ...sequenceCompanyDetails,
        website: normalizeUrlValue(
          sequenceCompanyDetails.website || promptCompanyProfile.company_website
        ),
        industry: normalizeSequenceString(
          sequenceCompanyDetails.industry || promptCompanyProfile.industry_sector,
          160
        ),
        sizeRange: normalizeSequenceString(
          sequenceCompanyDetails.sizeRange || promptCompanyProfile.company_size,
          120
        ),
        location: normalizeSequenceString(
          sequenceCompanyDetails.location || promptCompanyProfile.location,
          160
        ),
      };
      const promptJobDetails = {
        ...jobEmailDetails,
        companyWebsite: normalizeUrlValue(
          jobEmailDetails.companyWebsite || promptCompanyProfile.company_website
        ),
        careerWebsite: normalizeUrlValue(
          jobEmailDetails.careerWebsite || promptCompanyProfile.career_site
        ),
        companySize: normalizeSequenceString(
          jobEmailDetails.companySize || promptCompanyProfile.company_size,
          120
        ),
        industry: normalizeSequenceString(
          jobEmailDetails.industry || promptCompanyProfile.industry_sector,
          160
        ),
      };
      const promptDetailsText = buildEmailCampaignDetailsText({
        companyDetails: promptCompanyDetails,
        leadInfo: promptCompanyProfile,
        jobDetails: promptJobDetails,
      });
      const contextParts = [];
      if (form.name) contextParts.push(`Sequence: ${form.name}`);
      if (form.goal) contextParts.push(`Goal: ${form.goal}`);
      if (activeStep?.subject) contextParts.push(`Email subject: ${activeStep.subject}`);
      if (promptCompanyDetails?.company) contextParts.push(`Company: ${promptCompanyDetails.company}`);
      if (promptCompanyDetails?.industry) contextParts.push(`Industry: ${promptCompanyDetails.industry}`);
      if (promptCompanyDetails?.website) contextParts.push(`Website: ${promptCompanyDetails.website}`);
      if (promptJobDetails?.jobTitle) contextParts.push(`Job title: ${promptJobDetails.jobTitle}`);
      if (promptJobDetails?.location) contextParts.push(`Job location: ${promptJobDetails.location}`);
      if (promptJobDetails?.employmentType) contextParts.push(`Employment type: ${promptJobDetails.employmentType}`);
      if (promptJobDetails?.workArrangement || promptCompanyDetails?.workArrangement) {
        contextParts.push(
          `Work arrangement: ${promptJobDetails?.workArrangement || promptCompanyDetails?.workArrangement}`
        );
      }
      if (isFollowUpStep) {
        contextParts.push(`Sequence step: ${activeStepIndex + 1}`);
        contextParts.push("This draft should be a follow-up email, not a first-touch email.");
        contextParts.push("The message must feel materially different from the earlier email.");
      }
      const recipientLabel = getSequenceAiRecipientLabel(normalizedEntityType);
      const hasStructuredSource =
        hasStructuredCampaignContext ||
        Boolean(
          promptCompanyDetails?.company ||
            promptCompanyProfile?.full_company_description ||
            promptCompanyProfile?.mission_and_vision ||
            promptJobDetails?.jobDescription ||
            promptJobDetails?.responsibilities ||
            promptJobDetails?.qualifications
        );
      const shouldComposeStructuredIntro = hasStructuredSource && !isFollowUpStep;
      const previousStepContextText = previousEmailSteps
        .slice(-2)
        .map((step, index) => {
          const stepOrder = steps.findIndex((item) => item.id === step?.id) + 1;
          const subject = normalizeSequenceString(step?.subject, 255);
          const body = truncateAiComposeContext(step?.body, 1200);
          const lines = [`Earlier step ${stepOrder > 0 ? stepOrder : index + 1}:`];
          if (subject) lines.push(`Subject: ${subject}`);
          if (body) lines.push(`Body:\n${body}`);
          return lines.join("\n");
        })
        .filter(Boolean)
        .join("\n\n");
      const currentDraftBody = truncateAiComposeContext(activeStep?.body, 1200);
      const previousStepBody = truncateAiComposeContext(previousEmailStep?.body, 1200);
      const currentDraftDiffersFromPrevious =
        currentDraftBody &&
        (!previousStepBody || normalizeSequenceString(currentDraftBody, 2000) !== normalizeSequenceString(previousStepBody, 2000));

      const prompt = [
        isFollowUpStep
          ? `You are a professional recruiter writing step ${activeStepIndex + 1} of an email sequence to a ${recipientLabel}.`
          : shouldComposeStructuredIntro
          ? `You are a professional recruiter writing a structured plain-text recruiting brief for a sequence email body.`
          : `You are a professional recruiter writing a cold outreach email to a ${recipientLabel}.`,
        contextParts.length ? `Context:\n${contextParts.join("\n")}` : "",
        promptDetailsText ? `Stored company and job information:\n${promptDetailsText}` : "",
        isFollowUpStep && previousStepContextText
          ? `Previous sequence emails:\n${previousStepContextText}`
          : "",
        isFollowUpStep && currentDraftDiffersFromPrevious
          ? `Current draft to improve:\n${currentDraftBody}`
          : "",
        shouldComposeStructuredIntro
          ? `Match the screenshot-style layout closely. Write a short opening company overview paragraph, then a blank line, then the heading "Who We Are", then role sections with the exact labels "Responsibilities:", "Qualifications:", and "Technical Expertise:" whenever the stored data supports them. The "Role Overview:" paragraph must stay within 300 characters before the link, and if a job link is available append "Read more: <url>" on the same line. Use concise bullet points under the structured sections. If there is strong supporting company information, you may add "Why Join Us?" as the final section. Keep everything in plain text.`
          : isFollowUpStep
            ? getSequenceAiFollowUpInstructions(normalizedEntityType)
            : `Write a short, warm, and human-sounding email body (2-3 short paragraphs). Do NOT include the subject line. Use {{first_name}} for the recipient greeting and {{sender_name}} for the sign-off.`,
        shouldComposeStructuredIntro
          ? `Do not write a greeting, sign-off, CTA, or conversational cold-email copy in the structured version. Use only the company and job information already stored or enriched from the database. Treat "-" values as missing, omit empty fields, and do not invent facts. Output ONLY the email body text, nothing else.`
          : isFollowUpStep
            ? `Keep it conversational, concise, and non-pushy. Do not repeat the full company or job brief from earlier steps. Do not reuse the same opening lines, headings, bullet structure, or phrasing from the earlier emails. A recipient reading both emails should immediately feel this is a new follow-up touch. Use {{first_name}} for the greeting when it fits and {{sender_name}} for the sign-off. No bullet points. No AI-sounding language. Output ONLY the email body text, nothing else.`
            : `Keep it conversational, not salesy. No bullet points. No AI-sounding language. Output ONLY the email body text, nothing else.`,
      ].filter(Boolean).join("\n\n");

      const formData = new FormData();
      formData.append("message", prompt);
      formData.append("mode", "compose");

      const res = await fetch(`${API_ROOT}/ai-agent/respond`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("AI request failed");
      const data = await res.json();
      const aiBody = (data.answer || data.assistant || data.message || "").trim();
      if (aiBody) updateActiveStep({ body: aiBody });
    } catch (err) {
      console.error("AI compose failed:", err);
      setToast({
        open: true,
        type: "error",
        msg: err?.message || "AI compose failed.",
      });
    } finally {
      setAiComposing(false);
    }
  }, [
    activeStep?.body,
    activeStep?.subject,
    activeStepIndex,
    aiComposing,
    companyLeadDetails,
    enrichCompanyProfileIfNeeded,
    form.goal,
    form.name,
    hasStructuredCampaignContext,
    isFollowUpStep,
    jobEmailDetails,
    normalizedEntityType,
    previousEmailStep?.body,
    previousEmailSteps,
    sequenceCompanyDetails,
    shouldAttemptCompanyEnrichment,
    steps,
    updateActiveStep,
  ]);

  useEffect(() => {
    if (!open || !pendingAutoComposeStepId) return;
    if (pendingAutoComposeStepId !== activeStep?.id) return;
    if (!isFollowUpStep || aiComposing) return;

    setPendingAutoComposeStepId("");
    handleAiCompose();
  }, [
    activeStep?.id,
    aiComposing,
    handleAiCompose,
    isFollowUpStep,
    open,
    pendingAutoComposeStepId,
  ]);

  const removeRelatedSelection = (id) =>
    setRelatedSelections((prev) => prev.filter((item) => item.id !== id));

  const addRelatedSelection = (label, kind = "custom") => {
    const normalized = normalizeSequenceString(label, 120);
    if (!normalized) return;

    setRelatedSelections((prev) => {
      const nextId = `${kind}:${normalized.toLowerCase()}`;
      if (prev.some((item) => item.id === nextId)) return prev;
      return [...prev, { id: nextId, label: normalized, kind }];
    });
  };

  const handleRelatedSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    addRelatedSelection(relatedSearch, "custom");
    setRelatedSearch("");
  };

  const toggleRecipientSelection = (rowKey) => {
    setSelectedRecipientKeys((prev) =>
      prev.includes(rowKey) ? prev.filter((key) => key !== rowKey) : [...prev, rowKey]
    );
  };

  const selectAllRecipients = () => {
    setSelectedRecipientKeys((prev) => Array.from(new Set([...prev, ...visibleRecipientKeys])));
  };

  const toggleVisibleRecipients = () => {
    if (!visibleRecipientKeys.length) return;

    setSelectedRecipientKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleRecipientsSelected) {
        visibleRecipientKeys.forEach((key) => next.delete(key));
      } else {
        visibleRecipientKeys.forEach((key) => next.add(key));
      }
      return Array.from(next);
    });
  };

  const deleteSelectedRecipients = () => {
    if (!selectedRecipientKeys.length) return;

    setSequenceItems((prev) =>
      prev.filter((item, index) => !selectedRecipientKeySet.has(getSequenceItemKey(item, index)))
    );
    setSelectedRecipientKeys([]);
  };

  const goBack = () => {
    if (screen === "existing" || screen === "details") setScreen("start");
    else if (screen === "recipients") setScreen("details");
    else if (screen === "steps") setScreen("recipients");
    else if (screen === "audience") setScreen("steps");
    else if (screen === "preview") setScreen("review");
    else if (screen === "review") setScreen(mode === "existing" ? "existing" : "audience");
  };

  const goNext = async () => {
    if (screen === "existing" && canAddToExisting) setScreen("review");
    else if (screen === "details") setScreen("recipients");
    else if (screen === "recipients") {
      setSequenceItems(activeSequenceItems);
      setSelectedRecipientKeys(activeSequenceItems.map((item, index) => getSequenceItemKey(item, index)));
      setScreen("steps");
    }
    else if (screen === "steps") setScreen("audience");
    else if (screen === "audience") {
      const initialMissingFields = listMissingStructuredEmailFields(
        companyLeadDetails,
        jobEmailDetails
      );
      try {
        setPreparingAudience(true);
        setAudiencePreparation({
          status: "loading",
          message: initialMissingFields.length
            ? `Reviewing ${initialMissingFields.length} missing structured field${initialMissingFields.length === 1 ? "" : "s"}...`
            : "Reviewing email context...",
          totalFields: initialMissingFields.length,
          resolvedFields: 0,
          remainingFields: initialMissingFields,
          collectedFields: [],
        });
        const result = await ensureAudienceEmailContext(initialMissingFields);
        if (result?.collectedFields?.length) {
          setToast({
            open: true,
            type: "success",
            msg: `AI collected ${result.collectedFields.length} missing structured field${result.collectedFields.length === 1 ? "" : "s"} and saved them to the database.`,
          });
        }
        setScreen("review");
      } catch (error) {
        setAudiencePreparation((prev) => ({
          ...prev,
          status: "error",
          message:
            error?.message || "Complete the company and job details before continuing.",
        }));
      } finally {
        setPreparingAudience(false);
      }
    }
  };

  const enrollments = useMemo(
    () =>
      activeSequenceItems.map((item) => ({
        entityType: item.entityType,
        entityId: item.entityId,
        name: item.name || item.title || item.company || entityLabel,
        title: item.title,
        company: item.company,
        email: item.email,
        phone: item.phone,
        linkedinUrl: item.linkedinUrl,
        location: item.location,
        owner: item.owner,
        metadata: item.metadata,
      })),
    [activeSequenceItems, entityLabel]
  );
  const previewRecipientIndex = useMemo(
    () => previewRecipients.findIndex((item) => item.key === previewRecipientKey),
    [previewRecipientKey, previewRecipients]
  );
  const previewRecipient = useMemo(
    () =>
      previewRecipients[
        previewRecipientIndex >= 0 ? previewRecipientIndex : 0
      ] || null,
    [previewRecipientIndex, previewRecipients]
  );
  useEffect(() => {
    if (!previewRecipients.length) {
      if (previewRecipientKey) setPreviewRecipientKey("");
      return;
    }

    if (!previewRecipients.some((item) => item.key === previewRecipientKey)) {
      setPreviewRecipientKey(previewRecipients[0]?.key || "");
    }
  }, [previewRecipientKey, previewRecipients]);
  const gmailHasSendScope = useMemo(
    () => hasGmailScope(gmailStatus.connection?.scope, GMAIL_SEND_SCOPE),
    [gmailStatus.connection?.scope]
  );
  const gmailReadyForSend = gmailStatus.connected && gmailHasSendScope;
  const canSendCampaign = Boolean(canCreate && enrollments.length && gmailReadyForSend);
  const previewTemplateContext = useMemo(
    () =>
      buildPreviewTemplateContext(
        {
          name: form.name,
          entityType: normalizedEntityType,
          ownerName: form.ownerName || effectiveUser?.name || form.fromName,
          fromName: form.fromName,
          fromEmail: form.fromEmail,
          companyName: form.companyName,
          selectedJobName: form.selectedJobName,
          companyDetails: sequenceCompanyDetails,
          leadDetails: companyLeadDetails,
          jobDetails: jobEmailDetails,
        },
        previewRecipient || {},
        {
          senderName: activeStep?.senderName || form.fromName,
          senderEmail: activeStep?.senderEmail || form.fromEmail,
        },
        Object.fromEntries(customVariables.map((v) => [v.name, v.value]))
      ),
    [
      activeStep?.senderEmail,
      activeStep?.senderName,
      form.fromEmail,
      form.fromName,
      form.name,
      form.ownerName,
      form.companyName,
      form.selectedJobName,
      normalizedEntityType,
      previewRecipient,
      sequenceCompanyDetails,
      companyLeadDetails,
      jobEmailDetails,
      effectiveUser?.name,
      customVariables,
    ]
  );
  const previewSubject = useMemo(
    () =>
      normalizeSequenceString(
        applyPreviewTemplateTokens(activeStep?.subject || "", previewTemplateContext)
      ),
    [activeStep?.subject, previewTemplateContext]
  );
  const previewBody = useMemo(
    () =>
      normalizeSequenceText(
        applyPreviewTemplateTokens(activeStep?.body || "", previewTemplateContext),
        20000
      ),
    [activeStep?.body, previewTemplateContext]
  );
  const previewHtml = useMemo(() => {
    if (!previewBody) return "";

    // Plain-text sequences retain their line breaks, while rich-text sequences
    // are rendered as email HTML after sanitization.
    const markup = /<\/?[a-z][\s\S]*>/i.test(previewBody)
      ? previewBody
      : previewBody
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br />");
    return DOMPurify.sanitize(markup);
  }, [previewBody]);

  const saveNewSequence = async (
    statusOverride = form.status,
    options = {}
  ) => {
    if (!canCreate) return;
    const requestedStatus = normalizeSequenceString(statusOverride).toLowerCase() || "draft";
    const existingStatus = normalizeSequenceString(form.status).toLowerCase();
    // Save buttons use "draft" for a new sequence. When editing a sent,
    // active campaign, retain its live status instead of silently demoting it.
    const effectiveStatus =
      draftId && requestedStatus === "draft" && existingStatus === "active"
        ? "active"
        : requestedStatus;
    const action = normalizeSequenceString(options.action).toLowerCase();
    const successPrefixOverride = normalizeSequenceString(options.successPrefix);
    setSaveAction(action);
    setSaving(true);
    try {
      const payload = {
        sequence: {
          name: normalizeSequenceString(form.name),
          goal: normalizeSequenceString(form.goal),
          description: normalizeSequenceText(form.description),
          status: effectiveStatus,
          entityType: normalizedEntityType,
          ownerName: normalizeSequenceString(form.ownerName || effectiveUser?.name || form.fromName),
          fromName: normalizeSequenceString(form.fromName),
          fromEmail: normalizeSequenceString(form.fromEmail),
          companyName: normalizeSequenceString(form.companyName),
          selectedJobName: normalizeSequenceString(form.selectedJobName),
          settings: {
            source: "bulk-add-to-sequence",
            provider: "gmail",
            mailboxProvider: "gmail",
            workflow: {
              screen,
              activeStepId: normalizeSequenceString(activeStepId),
            },
            campaignType: form.campaignType,
            privacy: normalizeSequenceString(form.privacy),
            relatedTo: relatedSelections.map((item) => ({
              label: item.label,
              kind: item.kind,
            })),
            relatedSelection: normalizeSequenceString(form.relatedTo),
            companyDetails: sequenceCompanyDetails,
            leadDetails: companyLeadDetails,
            jobDetails: jobEmailDetails,
            replyRules: {
              primary: normalizeSequenceString(form.primaryReplyAction),
              secondary: normalizeSequenceString(form.secondaryReplyAction),
            },
          },
        },
        steps: steps.map((step) => ({
          channel: step.channel,
          title: normalizeSequenceString(step.title),
          subject: normalizeSequenceString(step.subject),
          body: normalizeSequenceText(step.body),
          delayValue: Math.max(0, Number(step.delayValue) || 0),
          delayUnit: step.delayUnit || "days",
          senderName: normalizeSequenceString(step.senderName || form.fromName),
          senderEmail: normalizeSequenceString(step.senderEmail || form.fromEmail),
          metadata: {
            scheduleType: step.scheduleType || "immediately",
            scheduledDate: step.scheduledDate || "",
            scheduledTime: step.scheduledTime || DEFAULT_STEP_TIME,
            timezoneLabel: step.timezoneLabel || DEFAULT_STEP_TIMEZONE,
            bodyFormat: "Rich text",
          },
          })),
        enrollments,
      };
      const result = draftId
        ? await updateSequence(draftId, payload)
        : await createSequence(payload);
      if (!draftId && result?.sequence?.id) {
        setDraftId(result.sequence.id);
      }
      const processingErrors = Array.isArray(result?.processing?.errors)
        ? result.processing.errors.filter(Boolean)
        : [];
      const sentCount = Number(result?.processing?.sent || 0);
      const scheduledCount = Number(result?.processing?.scheduled || 0);
      const isUpdate = Boolean(draftId);
      const successPrefix =
        successPrefixOverride ||
        (effectiveStatus === "draft"
          ? isUpdate ? "Draft updated" : "Sequence draft saved"
          : isUpdate ? "Sequence updated" : "Sequence created");
      const sendCreatedButPending = action === "send" && sentCount <= 0 && scheduledCount <= 0;
      const successMessage = sendCreatedButPending
        ? "Campaign created, but no Gmail sequence emails were sent yet."
        :
        sentCount > 0
          ? `${successPrefix}. ${sentCount} Gmail sequence email${sentCount === 1 ? "" : "s"} sent.`
          : scheduledCount > 0
            ? `Campaign scheduled for ${scheduledCount} recipient${scheduledCount === 1 ? "" : "s"}.`
            : successPrefix;
      const shouldKeepOpenForSend =
        action === "send" && (processingErrors.length > 0 || sendCreatedButPending);
      setToast({
        open: true,
        type: processingErrors.length || sendCreatedButPending ? "warning" : "success",
        msg: processingErrors.length ? `${successMessage} ${processingErrors[0]}` : successMessage,
      });
      onCreated?.(result?.sequence || result);
      if (!shouldKeepOpenForSend) {
        onClose?.();
      }
    } catch (error) {
      setToast({ open: true, type: "error", msg: error?.message || (draftId ? "Failed to update sequence" : "Failed to create sequence") });
    } finally {
      setSaving(false);
      setSaveAction("");
    }
  };

  const saveExistingSequence = async () => {
    if (!canAddToExisting) return;
    setSaving(true);
    try {
      const result = await addSequenceEnrollments(selectedExistingSequence.id, enrollments);
      const processingErrors = Array.isArray(result?.processing?.errors)
        ? result.processing.errors.filter(Boolean)
        : [];
      const sentCount = Number(result?.processing?.sent || 0);
      const scheduledCount = Number(result?.processing?.scheduled || 0);
      const baseMessage = `${result?.added || enrollments.length} ${entityLabelPlural.toLowerCase()} added to sequence`;
      const sendCreatedButPending = sentCount <= 0 && scheduledCount <= 0;
      const successMessage = sendCreatedButPending
        ? `${baseMessage}. No Gmail sequence emails were sent yet.`
        :
        sentCount > 0
          ? `${baseMessage}. ${sentCount} Gmail sequence email${sentCount === 1 ? "" : "s"} sent.`
          : `${baseMessage}. Emails scheduled for ${scheduledCount} recipients.`;
      const shouldKeepOpenForSend = processingErrors.length > 0 || sendCreatedButPending;
      setToast({
        open: true,
        type: processingErrors.length || sendCreatedButPending ? "warning" : "success",
        msg: processingErrors.length ? `${successMessage} ${processingErrors[0]}` : successMessage,
      });
      onCreated?.(result?.sequence || result);
      if (!shouldKeepOpenForSend) {
        onClose?.();
      }
    } catch (error) {
      setToast({ open: true, type: "error", msg: error?.message || "Failed to add to sequence" });
    } finally {
      setSaving(false);
    }
  };

  const handleConnectGmail = useCallback(async () => {
    let popup = null;
    try {
      setGmailLoading(true);

      popup = window.open(
        "",
        "gmail-oauth",
        "width=640,height=760,menubar=no,toolbar=no,status=no"
      );

      if (!popup) {
        throw new Error("Allow popups to reconnect Gmail from this window.");
      }

      renderGmailPopupStatus(popup, "Reconnect Gmail", "Connecting to Gmail...");

      const returnPath =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : "/messages-sequences";
      const response = await fetch(
        apiUrl(
          `/gmail/auth-url?origin=${encodeURIComponent(
            window.location.origin
          )}&return_path=${encodeURIComponent(returnPath)}`
        ),
        {
          headers: authHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          await readResponseError(
            response,
            `Failed to start Gmail connection (${response.status})`
          )
        );
      }

      const payload = await response.json().catch(() => ({}));
      const authUrl = payload?.auth_url || "";
      if (!authUrl) {
        throw new Error("Failed to create Gmail authorization URL.");
      }

      popup.location.href = authUrl;
    } catch (error) {
      renderGmailPopupStatus(
        popup,
        "Gmail Connection Error",
        error?.message || "Failed to connect Gmail."
      );
      setToast({
        open: true,
        type: "error",
        msg: error?.message || "Failed to connect Gmail.",
      });
      try {
        popup?.close?.();
      } catch {}
    } finally {
      setGmailLoading(false);
    }
  }, []);

  const sendTestEmailPreview = async () => {
    const testEmail = normalizeSequenceString(form.testEmail);
    if (!testEmail) {
      setToast({
        open: true,
        type: "error",
        msg: "Enter a test email address first.",
      });
      return;
    }

    if (!activeStep) {
      setToast({
        open: true,
        type: "error",
        msg: "Select a sequence step before sending a test email.",
      });
      return;
    }

    if (!previewRecipient) {
      setToast({
        open: true,
        type: "error",
        msg: "Select at least one recipient to build the test email preview.",
      });
      return;
    }

    if (!gmailReadyForSend) {
      setToast({
        open: true,
        type: "error",
        msg: gmailStatus.connected
          ? "Reconnect Gmail to grant Gmail send permission before sending a test email."
          : "Connect Gmail before sending a test email.",
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const result = await sendSequenceTestEmail({
        testEmail,
        sequence: {
          name: normalizeSequenceString(form.name),
          ownerName: normalizeSequenceString(form.ownerName || effectiveUser?.name || form.fromName),
          fromName: normalizeSequenceString(form.fromName),
          fromEmail: normalizeSequenceString(form.fromEmail),
          companyName: normalizeSequenceString(form.companyName),
          selectedJobName: normalizeSequenceString(form.selectedJobName),
          settings: {
            companyDetails: sequenceCompanyDetails,
            leadDetails: companyLeadDetails,
            jobDetails: jobEmailDetails,
          },
        },
        step: {
          title: normalizeSequenceString(activeStep.title),
          subject: normalizeSequenceString(activeStep.subject),
          body: normalizeSequenceText(activeStep.body),
          senderName: normalizeSequenceString(activeStep.senderName || form.fromName),
          senderEmail: normalizeSequenceString(activeStep.senderEmail || form.fromEmail),
          metadata: { bodyFormat: "Rich text" },
        },
        enrollment: {
          entityType: previewRecipient.entityType,
          entityId: previewRecipient.entityId,
          name: previewRecipient.name,
          title: previewRecipient.title,
          company: previewRecipient.company,
          email: previewRecipient.email,
          phone: previewRecipient.phone,
          linkedinUrl: previewRecipient.linkedinUrl,
          location: previewRecipient.location,
          owner: previewRecipient.owner,
          metadata: previewRecipient.metadata,
        },
      });

      setToast({
        open: true,
        type: "success",
        msg: `Test email sent to ${result?.to_email || testEmail}.`,
      });
    } catch (error) {
      if (
        /reconnect gmail|gmail is not connected|grant gmail send permission/i.test(
          String(error?.message || "")
        )
      ) {
        await loadGmailStatus();
      }
      setToast({
        open: true,
        type: "error",
        msg: error?.message || "Failed to send test email",
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const previewEmail = () => {
    if (!previewRecipients.length) {
      setToast({
        open: true,
        type: "error",
        msg: `Select at least one ${entityLabel.toLowerCase()} to preview this email.`,
      });
      return;
    }

    setScreen("preview");
  };

  const movePreviewRecipient = (direction = 0) => {
    if (!previewRecipients.length) return;

    const currentIndex = previewRecipientIndex >= 0 ? previewRecipientIndex : 0;
    const nextIndex = Math.max(
      0,
      Math.min(previewRecipients.length - 1, currentIndex + Number(direction || 0))
    );
    const nextRecipient = previewRecipients[nextIndex];
    if (nextRecipient?.key) setPreviewRecipientKey(nextRecipient.key);
  };

  const sendCampaign = async () => {
    if (!enrollments.length) {
      setToast({
        open: true,
        type: "error",
        msg: `Select at least one ${entityLabel.toLowerCase()} before sending this campaign.`,
      });
      return;
    }

    const missingEmails = enrollments.filter(item => !normalizeSequenceString(item.email));
    if (missingEmails.length) {
      setToast({ open: true, type: "error",
        msg: `Add an email address for ${missingEmails.map(item => item.name).join(", ")} before sending this campaign.` });
      setScreen("recipients");
      return;
    }

    if (!gmailReadyForSend) {
      setToast({
        open: true,
        type: "error",
        msg: gmailStatus.connected
          ? "Reconnect Gmail to grant Gmail send permission before sending this campaign."
          : "Connect Gmail before sending this campaign.",
      });
      return;
    }

    await saveNewSequence("active", {
      action: "send",
      successPrefix: "Campaign sent",
    });
  };

  const renderStart = () => (
    <Stack spacing={1.45}>
      <StartFlowCard
        title="Start from Scratch"
        subtitle="Start from scratch, create the Campaign yourself."
        icon={<EditNoteRoundedIcon sx={{ fontSize: 16 }} />}
        onClick={() => {
          setMode("new");
          setScreen("details");
        }}
      />
      <StartFlowCard
        title="Clone a Campaign"
        subtitle="Clone an existing Campaign."
        icon={<ContentCopyRoundedIcon sx={{ fontSize: 15 }} />}
        onClick={() => {
          setMode("existing");
          setScreen("existing");
        }}
      />
    </Stack>
  );

  const renderExisting = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" }, gap: 2 }}>
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
        <Typography sx={{ color: BLUE, fontWeight: 800, mb: 1.2 }}>Existing {normalizedInitialCampaignType === "sales" ? "Sales" : "Recruiting"} Sequences</Typography>
        <TextField fullWidth size="small" value={existingSearch} onChange={(e) => setExistingSearch(e.target.value)} placeholder="Search sequence name" sx={{ ...fieldSx, mb: 1.5 }} />
        <Stack spacing={1}>
          {existingLoading ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>Loading sequences...</Typography>
          ) : null}
          {filteredSequences.map((sequence) => (
            <Box
              key={sequence.id}
              onClick={() => setSelectedExistingId(sequence.id)}
              sx={{
                border: `1px solid ${String(sequence.id) === String(selectedExistingId) ? "#A78BFA" : BORDER}`,
                borderRadius: 2,
                p: 1.25,
                backgroundColor: String(sequence.id) === String(selectedExistingId) ? "#FAF5FF" : "#fff",
                cursor: "pointer",
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>{sequence.name}</Typography>
              <Typography sx={{ fontSize: 12, color: MUTED }}>
                {sequence.goal || sequence.description || "No goal yet"}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Chip size="small" label={`${sequence.step_count || 0} steps`} />
                <Chip size="small" label={`${sequence.enrollment_count || 0} enrolled`} />
              </Stack>
            </Box>
          ))}
          {!existingLoading && !filteredSequences.length ? (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>No sequences found.</Typography>
          ) : null}
        </Stack>
      </Box>
      <Stack spacing={2}>
        <ItemList items={sequenceItems} entityLabel={entityLabelPlural} compact />
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
          <Typography sx={{ color: BLUE, fontWeight: 800, mb: 1 }}>Selected Sequence</Typography>
          <Typography sx={{ fontWeight: 800 }}>{selectedExistingSequence?.name || "-"}</Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>
            {selectedExistingSequence?.description || selectedExistingSequence?.goal || "Choose a sequence to continue."}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  const renderDetails = () => (
    <Stack spacing={3.1}>
      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 15, fontWeight: 900, mb: 1.15 }}>
          Related to
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search DB"
          value={relatedSearch}
          onChange={(event) => setRelatedSearch(event.target.value)}
          onKeyDown={handleRelatedSearchKeyDown}
          sx={{
            ...fieldSx,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              minHeight: 40,
              borderRadius: "10px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 25 }} />
              </InputAdornment>
            ),
          }}
        />

        {!!relatedSelections.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.4, rowGap: 1 }}>
            {relatedSelections.map((item) => (
              <Chip
                key={item.id}
                label={item.label}
                onDelete={() => removeRelatedSelection(item.id)}
                sx={getRelatedChipSx(item.kind)}
              />
            ))}
          </Stack>
        ) : (
          <Typography sx={{ mt: 1.25, fontSize: 11.5, color: MUTED }}>
            Press Enter to add a related company, job, lead, or team member.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 15, fontWeight: 900, mb: 1.55 }}>
          Campaign Type
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <CampaignTypeCard
            label="Sales"
            selected={form.campaignType === "sales"}
            onClick={() => setForm((prev) => ({ ...prev, campaignType: "sales" }))}
          />
          <CampaignTypeCard
            label="Recruiting"
            selected={form.campaignType === "recruiting"}
            onClick={() => setForm((prev) => ({ ...prev, campaignType: "recruiting" }))}
          />
        </Stack>
      </Box>
    </Stack>
  );

  const renderRecipients = () => (
    <Stack spacing={2.3}>
      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 15, fontWeight: 900, mb: 1.1 }}>
          Recipients
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search DB"
          value={recipientSearch}
          onChange={(event) => setRecipientSearch(event.target.value)}
          sx={{
            ...fieldSx,
            "& .MuiOutlinedInput-root": {
              ...fieldSx["& .MuiOutlinedInput-root"],
              minHeight: 38,
              borderRadius: "8px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 22 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          border: "1px solid #D78BFF",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.6,
            py: 1.15,
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Typography sx={{ color: PURPLE, fontSize: 12.5, fontWeight: 800 }}>
            Recommended Recipients
          </Typography>
          <Typography sx={{ color: "#98A2B3", fontSize: 11.5 }}>
            Sequencing {recipientRows.length} {entityLabelPlural}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 1 }}>
          <Box
            sx={{
              gridTemplateColumns: "28px minmax(0, 2.3fr) 0.9fr 1fr 1fr",
              gap: 1.2,
              alignItems: "center",
              color: "#667085",
              fontSize: 11,
              fontWeight: 700,
              display: "none",
              pb: 0.8,
            }}
          >
            <Checkbox
              size="small"
              checked={allVisibleRecipientsSelected}
              indeterminate={someVisibleRecipientsSelected}
              onChange={toggleVisibleRecipients}
              sx={{ p: 0 }}
            />
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Name ↓</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Status ↓</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Skills ↓</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>% of Best Matches ↓</Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "28px minmax(0, 2.3fr) 0.9fr 1fr 1fr",
              gap: 1.2,
              alignItems: "center",
              color: "#667085",
              fontSize: 11,
              fontWeight: 700,
              pb: 0.8,
            }}
          >
            <Checkbox
              size="small"
              checked={allVisibleRecipientsSelected}
              indeterminate={someVisibleRecipientsSelected}
              onChange={toggleVisibleRecipients}
              sx={{ p: 0 }}
            />
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{`Name \u2193`}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{`Status \u2193`}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{`Skills \u2193`}</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{`% of Best Matches \u2193`}</Typography>
          </Box>

          <Stack spacing={0} sx={{ maxHeight: 328, overflowY: "auto", pr: 0.25 }}>
            {filteredRecipientRows.map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "28px minmax(0, 2.3fr) 0.9fr 1fr 1fr",
                  gap: 1.2,
                  alignItems: "center",
                  py: 1.05,
                  minHeight: 56,
                  borderTop: "1px solid #F2F4F7",
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedRecipientKeySet.has(row.key)}
                  onChange={() => toggleRecipientSelection(row.key)}
                  sx={{ p: 0 }}
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                  <Avatar
                    src={row.avatarUrl || undefined}
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 12,
                      bgcolor: "#F4EBFF",
                      color: "#6941C6",
                    }}
                  >
                    {row.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} noWrap>
                      {row.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: MUTED }} noWrap>
                      {row.subtitle}
                    </Typography>
                  </Box>
                </Box>

                <Chip
                  size="small"
                  label={row.status}
                  sx={{
                    width: "fit-content",
                    height: 20,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#12B76A",
                    backgroundColor: "#ECFDF3",
                  }}
                />

                <Typography sx={{ fontSize: 11.5, color: "#475467" }} noWrap>
                  {row.skills}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 999,
                      backgroundColor: "#EAECF0",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(0, Math.min(100, row.bestMatch))}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "linear-gradient(90deg,#C4B5FD,#7C3AED)",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#475467" }}>
                    {row.bestMatch}%
                  </Typography>
                </Box>
              </Box>
            ))}

            {!filteredRecipientRows.length ? (
              <Box sx={{ py: 3 }}>
                <Typography sx={{ fontSize: 12.5, color: MUTED, textAlign: "center" }}>
                  No recipients match this search.
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1.6,
            py: 1.1,
            borderTop: "1px solid #EAECF0",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#475467" }}>
            Bulk actions:
            {" "}
            <Box component="span" sx={{ color: PURPLE, cursor: "pointer", ml: 0.5 }} onClick={selectAllRecipients}>
              Select All
            </Box>
            {" | "}
            <Box component="span" sx={{ color: PURPLE, cursor: "pointer" }} onClick={deleteSelectedRecipients}>
              Delete
            </Box>
          </Typography>

          <Typography sx={{ fontSize: 12, color: PURPLE, fontWeight: 700 }}>
            Show Advanced
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Related to
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={relatedToOptions.includes(form.relatedTo) ? form.relatedTo : relatedToOptions[0] || "Job Lead"}
          onChange={setFormField("relatedTo")}
          sx={fieldSx}
        >
          {relatedToOptions.map((label) => (
            <MenuItem key={label} value={label}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Replies
        </Typography>
        <Stack spacing={1.4}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.55 }}>
              When the primary candidate replies
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.primaryReplyAction}
              onChange={setFormField("primaryReplyAction")}
              sx={fieldSx}
            >
              <MenuItem value="Finish sequence">Finish sequence</MenuItem>
              <MenuItem value="Pause sequence">Pause sequence</MenuItem>
              <MenuItem value="Notify owner">Notify owner</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.55 }}>
              When the non-primary candidate replies
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.secondaryReplyAction}
              onChange={setFormField("secondaryReplyAction")}
              sx={fieldSx}
            >
              <MenuItem value="Finish sequence">Finish sequence</MenuItem>
              <MenuItem value="Pause sequence">Pause sequence</MenuItem>
              <MenuItem value="Notify owner">Notify owner</MenuItem>
            </TextField>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );

  const renderSteps = () => (
    <Stack spacing={2.25}>
      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1.15 }}>
          Details
        </Typography>
        <Stack spacing={1.35}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Sequence Title
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={form.name}
              onChange={setFormField("name")}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Ownership
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={ownershipOptions.includes(form.ownerName) ? form.ownerName : ownershipOptions[0]}
              onChange={setFormField("ownerName")}
              sx={fieldSx}
            >
              {ownershipOptions.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Privacy
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.privacy}
              onChange={setFormField("privacy")}
              sx={fieldSx}
            >
              <MenuItem value="Private to Owner">Private to Owner</MenuItem>
              <MenuItem value="Shared with Team">Shared with Team</MenuItem>
              <MenuItem value="Public">Public</MenuItem>
            </TextField>
          </Box>
        </Stack>
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Related to
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={relatedToOptions.includes(form.relatedTo) ? form.relatedTo : relatedToOptions[0] || "Job Lead"}
          onChange={setFormField("relatedTo")}
          sx={fieldSx}
        >
          {relatedToOptions.map((label) => (
            <MenuItem key={label} value={label}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Replies
        </Typography>
        <Stack spacing={1.4}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.55 }}>
              When the primary candidate replies
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.primaryReplyAction}
              onChange={setFormField("primaryReplyAction")}
              sx={fieldSx}
            >
              <MenuItem value="Finish sequence">Finish sequence</MenuItem>
              <MenuItem value="Pause sequence">Pause sequence</MenuItem>
              <MenuItem value="Notify owner">Notify owner</MenuItem>
            </TextField>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: MUTED, mb: 0.55 }}>
              When the non-primary candidate replies
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={form.secondaryReplyAction}
              onChange={setFormField("secondaryReplyAction")}
              sx={fieldSx}
            >
              <MenuItem value="Finish sequence">Finish sequence</MenuItem>
              <MenuItem value="Pause sequence">Pause sequence</MenuItem>
              <MenuItem value="Notify owner">Notify owner</MenuItem>
            </TextField>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );

  const renderAudience = () => (
    <Stack spacing={2.25}>
      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Company Detail
        </Typography>
        <Stack spacing={1.35}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Company
            </Typography>
            <Autocomplete
              freeSolo
              options={companyLookupOptions}
              filterOptions={(options) => options}
              loading={companyLookupLoading}
              value={selectedCompanyOption}
              inputValue={companyInputValue}
              onInputChange={handleCompanyInputChange}
              onChange={handleCompanySelection}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.company_name || ""
              }
              isOptionEqualToValue={(option, value) =>
                getCompanyLookupKey(option) === getCompanyLookupKey(value)
              }
              noOptionsText={companyLookupLoading ? "Loading..." : "No companies found"}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1.15 }}
                >
                  <Avatar
                    src={option.company_logo || undefined}
                    sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "#F4EBFF", color: PURPLE }}
                  >
                    {option.company_name?.charAt(0)?.toUpperCase() || "C"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} noWrap>
                      <CompanyOptionLabel company={option} />
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: MUTED }} noWrap>
                      {[option.industry, option.location].filter(Boolean).join(" | ") || option.website || "Company"}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search company"
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {companyLookupLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedCompanyDetails ? (
              <Typography sx={{ mt: 0.75, fontSize: 10.5, color: "#98A2B3" }}>
                {[selectedCompanyDetails.industry, selectedCompanyDetails.location, selectedCompanyDetails.website]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Contact
            </Typography>
            <Autocomplete
              freeSolo={false}
              options={contactLookupOptions}
              loading={contactLookupLoading}
              value={contactLookupOptions.find((option) => option.name === form.contactName) || null}
              inputValue={form.contactName}
              onInputChange={(_, value) => setForm((previous) => ({ ...previous, contactName: value || "" }))}
              onChange={(_, option) => setForm((previous) => ({
                ...previous,
                contactName: typeof option === "string" ? option : option?.name || "",
              }))}
              getOptionLabel={(option) => typeof option === "string" ? option : option?.name || ""}
              isOptionEqualToValue={(option, value) => String(option?.id || option?.name) === String(value?.id || value?.name)}
              noOptionsText={contactLookupLoading ? "Searching contacts..." : "No contacts found"}
              renderOption={(props, option) => <Box component="li" {...props}><Box><Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{option.name}</Typography>{option.email || option.role ? <Typography sx={{ fontSize: 10.5, color: MUTED }}>{[option.role, option.email].filter(Boolean).join(" | ")}</Typography> : null}</Box></Box>}
              renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Search contacts" autoComplete="off" sx={fieldSx} InputProps={{ ...params.InputProps, endAdornment: <>{contactLookupLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
            />
          </Box>
        </Stack>
      </Box>

      <Box>
        <Typography data-popup-section sx={{ color: BLUE, fontSize: 14.5, fontWeight: 900, mb: 1 }}>
          Job Detail
        </Typography>
        <Stack spacing={1.35}>
          <Box>
            <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.55 }}>
              Select Job
            </Typography>
            <Autocomplete
              freeSolo={false}
              options={jobLookupOptions}
              filterOptions={(options) => options}
              loading={jobLookupLoading}
              value={selectedJobOption}
              inputValue={jobInputValue}
              onInputChange={handleJobInputChange}
              onChange={handleJobSelection}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.job_title || ""
              }
              isOptionEqualToValue={(option, value) =>
                getJobLookupKey(option) === getJobLookupKey(value)
              }
              noOptionsText={jobLookupLoading ? "Loading..." : "No jobs found"}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", gap: 1.15 }}
                >
                  <Avatar
                    src={option.company_logo || undefined}
                    sx={{ width: 28, height: 28, fontSize: 11, bgcolor: "#EEF2FF", color: BLUE }}
                  >
                    {option.job_title?.charAt(0)?.toUpperCase() || "J"}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} noWrap>
                      {option.job_title}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: MUTED }} noWrap>
                      {[option.company_name, option.location, option.status].filter(Boolean).join(" | ") || "Job"}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search jobs in database"
                  size="small"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {jobLookupLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedJobDetails ? (
              <Typography sx={{ mt: 0.75, fontSize: 10.5, color: "#98A2B3" }}>
                {[selectedJobDetails.company_name, selectedJobDetails.location, selectedJobDetails.status]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Typography sx={{ mt: 1.1, fontSize: 10.5, color: "#98A2B3" }}>
          {`Created: ${new Date().toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })} - by ${form.ownerName || "Owner"} | Contact: ${
            (contactOptions.includes(form.contactName) ? form.contactName : contactOptions[0]) || "Owner"
          } | Source: ${selectedJobDetails?.source || form.jobSource || "Job Board"}`}
        </Typography>
        {!hasRequiredAudienceSelection ? (
          <Typography sx={{ mt: 0.8, fontSize: 10.5, color: "#B42318" }}>
            {normalizedEntityType === "candidate"
              ? "Select a company and a job before continuing."
              : normalizedEntityType === "lead"
                ? "Select a job from the database before continuing."
                : "Complete the required audience selections before continuing."}
          </Typography>
        ) : null}
        {hasRequiredAudienceSelection && missingStructuredEmailFields.length ? (
          <>
            <Typography sx={{ mt: 0.8, fontSize: 10.5, color: "#B54708" }}>
              {`Missing email data: ${missingStructuredEmailFields.join(", ")}.`}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 10.5, color: "#98A2B3" }}>
              AI will try to fill the missing job data from the selected company and job record when you continue.
            </Typography>
          </>
        ) : null}
        {(preparingAudience || audiencePreparation.status !== "idle") ? (
          <Box
            sx={{
              mt: 1.25,
              p: 1.25,
              borderRadius: 2,
              border: "1px solid #E4E7EC",
              backgroundColor: "#F8FAFC",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
              {preparingAudience ? (
                <CircularProgress size={15} sx={{ color: PURPLE }} />
              ) : audiencePreparation.status === "success" ? (
                <CheckRoundedIcon sx={{ fontSize: 16, color: "#039855" }} />
              ) : audiencePreparation.status === "error" ? (
                <CloseRoundedIcon sx={{ fontSize: 16, color: "#D92D20" }} />
              ) : null}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color:
                    audiencePreparation.status === "error"
                      ? "#B42318"
                      : audiencePreparation.status === "success"
                        ? "#027A48"
                        : BLUE,
                }}
              >
                {audiencePreparation.message || "Collecting missing email data..."}
              </Typography>
            </Stack>

            {audiencePreparation.totalFields > 0 ? (
              <>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(
                    0,
                    Math.min(
                      100,
                      (audiencePreparation.resolvedFields / audiencePreparation.totalFields) * 100
                    )
                  )}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    backgroundColor: "#EAECF0",
                    mb: 0.7,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      background:
                        audiencePreparation.status === "error"
                          ? "#F04438"
                          : "linear-gradient(135deg,#8B5CF6,#9333EA)",
                    },
                  }}
                />
                <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                  {`${audiencePreparation.resolvedFields} of ${audiencePreparation.totalFields} missing structured field${audiencePreparation.totalFields === 1 ? "" : "s"} collected.`}
                </Typography>
              </>
            ) : null}

            {audiencePreparation.collectedFields.length ? (
              <Typography sx={{ mt: 0.55, fontSize: 10.5, color: "#027A48" }}>
                {`Collected: ${audiencePreparation.collectedFields.join(", ")}.`}
              </Typography>
            ) : null}

            {audiencePreparation.remainingFields.length ? (
              <Typography sx={{ mt: 0.4, fontSize: 10.5, color: audiencePreparation.status === "error" ? "#B42318" : MUTED }}>
                {`Remaining: ${audiencePreparation.remainingFields.join(", ")}.`}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Stack>
  );

  const renderReview = () => (
    mode === "existing" ? (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
            <Typography sx={{ color: BLUE, fontWeight: 800, mb: 1.2 }}>Sequence Ready</Typography>
            <Typography sx={{ fontWeight: 800 }}>{selectedExistingSequence?.name || "-"}</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              {selectedExistingSequence?.description || selectedExistingSequence?.goal || "This sequence will receive the selected enrollments."}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <Chip size="small" label={`${sequenceItems.length} enrollments`} />
            </Stack>
          </Box>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
            <Typography sx={{ color: BLUE, fontWeight: 800, mb: 1.2 }}>Steps</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Existing sequence steps will remain unchanged. Only the selected {entityLabelPlural.toLowerCase()} will be enrolled.
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={2}>
          <ItemList items={sequenceItems} entityLabel={entityLabelPlural} compact />
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 2, backgroundColor: "#fff" }}>
            <Typography sx={{ color: BLUE, fontWeight: 800, mb: 1 }}>Sender</Typography>
            <Typography sx={{ fontWeight: 700 }}>{form.fromName || "-"}</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>{form.fromEmail || "-"}</Typography>
          </Box>
        </Stack>
      </Box>
    ) : (
    <Box>
      <Stack spacing={1.7}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 1.2,
            pb: 0.95,
            borderBottom: "1px solid #EAECF0",
          }}
        >
          <Stack direction="row" spacing={2.1} alignItems="flex-end">
            {steps.map((step, index) => {
              const isActive = step.id === activeStep?.id;

              return (
                <ButtonBase
                  key={step.id}
                  onClick={() => setActiveStepId(step.id)}
                  sx={{
                    pb: 0.8,
                    borderBottom: `2px solid ${isActive ? PURPLE : "transparent"}`,
                    color: BLUE,
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    lineHeight: 1,
                  }}
                >
                  {`Step ${index + 1}`}
                </ButtonBase>
              );
            })}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 0.8 }}>
              <ButtonBase
                onClick={addStep}
                sx={{
                  color: PURPLE,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                Add Step
              </ButtonBase>
              <Typography sx={{ color: "#98A2B3", fontSize: 14, lineHeight: 1 }}>
                &gt;
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              justifySelf: "end",
              minWidth: 110,
              pl: 2.25,
              borderLeft: "1px solid #EAECF0",
            }}
          >
            <ButtonBase
              onClick={() => removeStep(activeStep?.id)}
              disabled={steps.length <= 1}
              sx={{
                color: steps.length <= 1 ? "#D0D5DD" : "#B692F6",
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              Delete Step
            </ButtonBase>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1.1}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          sx={{ pb: 1.1, borderBottom: "1px solid #F2F4F7" }}
        >
          <Typography sx={{ fontSize: 12, color: "#344054" }}>Send this email:</Typography>
          <TextField
            select
            size="small"
            value={activeDelayPreset}
            onChange={setActiveStepDelayPreset}
            sx={{ ...fieldSx, minWidth: 168 }}
          >
            <MenuItem value="immediately">Immediately</MenuItem>
            <MenuItem value="next-business-day">On the next business day</MenuItem>
            <MenuItem value="specific-date">Select a date</MenuItem>
          </TextField>
          {["next-business-day", "specific-date"].includes(activeDelayPreset) ? (
            <>
              {activeDelayPreset === "specific-date" ? (
                <TextField
                  size="small"
                  type="date"
                  label="Send date"
                  value={activeScheduledDate}
                  onChange={setActiveStepScheduledDate}
                  sx={{ ...fieldSx, width: 172 }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ "aria-label": "Send date" }}
                />
              ) : null}
              <Typography sx={{ fontSize: 12, color: "#344054" }}>at</Typography>
              <TextField
                select
                size="small"
                value={activeScheduledTime}
                onChange={setActiveStepScheduledTime}
                sx={{ ...fieldSx, width: 142 }}
                inputProps={{ "aria-label": "Send time" }}
              >
                {SCHEDULE_TIME_SLOTS.map((timeSlot) => (
                  <MenuItem key={timeSlot} value={timeSlot}>
                    {timeSlot}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={activeTimezoneLabel}
                onChange={setActiveStepTimezone}
                sx={{ ...fieldSx, minWidth: 280, flex: 1 }}
              >
                <MenuItem value={DEFAULT_STEP_TIMEZONE}>{DEFAULT_STEP_TIMEZONE}</MenuItem>
              </TextField>
            </>
          ) : null}
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "655px 338px" }, gap: 1.6 }}>
          <Stack spacing={1.25}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
              <Typography sx={{ minWidth: 32, fontSize: 12, color: BLUE, fontWeight: 700 }}>
                From
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={activeSenderKey}
                onChange={setActiveStepSender}
                sx={fieldSx}
              >
                {senderOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
              <Typography sx={{ minWidth: 48, fontSize: 12, color: BLUE, fontWeight: 700 }}>
                Subject
              </Typography>
              <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr auto", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={activeStep?.subject || ""}
                  onChange={setStepField("subject")}
                  sx={fieldSx}
                />
                <Typography sx={{ alignSelf: "center", fontSize: 11.5, color: PURPLE, whiteSpace: "nowrap" }}>
                  Cc Bcc
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.2 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  className={withPillButtonClassName()}
                  variant="contained"
                  size="small"
                  onClick={handleAiCompose}
                  disabled={aiComposing}
                  startIcon={aiComposing ? <CircularProgress size={14} /> : <BoltOutlinedIcon sx={{ fontSize: 15 }} />}
                  sx={[pillButtonFilledSx, pillButtonInteractiveSx, {
                    minWidth: 88,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    fontSize: 11,
                    fontWeight: 400,
                  }]}
                >
                  {aiComposing ? "Composing…" : "AI Agent"}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setVariablesAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 88,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    background: "#fff",
                    fontSize: 11,
                    fontWeight: 700,
                    "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" },
                  }}
                >
                  Variables
                </Button>
                <Menu
                  anchorEl={variablesAnchorEl}
                  open={Boolean(variablesAnchorEl)}
                  onClose={() => setVariablesAnchorEl(null)}
                  PaperProps={{ sx: { maxHeight: 380, minWidth: 220 } }}
                >
                  {AVAILABLE_VARIABLES.map((group) => [
                    <ListSubheader key={group.group} sx={{ fontSize: 11, fontWeight: 700, lineHeight: "28px", color: BLUE }}>{group.group}</ListSubheader>,
                    ...group.tokens.map((token) => (
                      <MenuItem key={token} onClick={() => insertVariableAtCursor(token)} sx={{ fontSize: 12 }}>
                        <ListItemText primary={`{{${token}}}`} primaryTypographyProps={{ fontSize: 12 }} />
                      </MenuItem>
                    )),
                  ])}
                  {customVariables.length > 0 && [
                    <ListSubheader key="custom" sx={{ fontSize: 11, fontWeight: 700, lineHeight: "28px", color: BLUE }}>Custom</ListSubheader>,
                    ...customVariables.map((v) => (
                      <MenuItem key={v.name} onClick={() => insertVariableAtCursor(v.name)} sx={{ fontSize: 12 }}>
                        <ListItemText primary={`{{${v.name}}}`} primaryTypographyProps={{ fontSize: 12 }} />
                      </MenuItem>
                    )),
                  ]}
                  <Divider />
                  <MenuItem onClick={() => { setVariablesAnchorEl(null); setCustomVarDialogOpen(true); }} sx={{ fontSize: 12, color: PURPLE, fontWeight: 700 }}>
                    + Create Custom Variable
                  </MenuItem>
                </Menu>
              </Stack>
              <Typography sx={{ color: PURPLE, fontSize: 18, lineHeight: 1, display: "none" }}>
                ⋮
              </Typography>
              <Typography sx={{ color: PURPLE, fontSize: 18, lineHeight: 1, display: "none" }}>
                ...
              </Typography>
              <MoreVertRoundedIcon sx={{ color: PURPLE, fontSize: 18 }} />
            </Stack>

            <Box>
              <Typography sx={{ color: BLUE, fontSize: 12, fontWeight: 700, mb: 0.8 }}>
                Email Body
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 0.9 }}>
                <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                  {[
                    ["Bold", <FormatBoldRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("bold")],
                    ["Italic", <FormatItalicRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("italic")],
                    ["Underline", <FormatUnderlinedRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("underline")],
                    ["Heading", <TitleRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("formatBlock", "h2")],
                    ["Numbered list", <FormatListNumberedRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("insertOrderedList")],
                    ["Bulleted list", <FormatListBulletedRoundedIcon sx={{ fontSize: 17 }} />, () => runRichTextCommand("insertUnorderedList")],
                    ["Insert link", <LinkRoundedIcon sx={{ fontSize: 17 }} />, insertRichTextLink],
                  ].map(([label, icon, onClick]) => (
                    <Tooltip title={label} key={label}>
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        aria-label={label}
                        onMouseDown={(event) => {
                          rememberRichTextSelection();
                          event.preventDefault();
                          onClick();
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onClick();
                          }
                        }}
                        sx={{ width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#667085" }}
                      >
                        {icon}
                      </Box>
                    </Tooltip>
                  ))}
                  <Tooltip title="Text alignment">
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label="Text alignment"
                      onMouseDown={(event) => {
                        rememberRichTextSelection();
                        event.preventDefault();
                        setTextAlignmentAnchorEl(event.currentTarget);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setTextAlignmentAnchorEl(event.currentTarget);
                        }
                      }}
                      sx={{ width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#667085" }}
                    >
                      <FormatAlignLeftRoundedIcon sx={{ fontSize: 17 }} />
                    </Box>
                  </Tooltip>
                  <Menu
                    anchorEl={textAlignmentAnchorEl}
                    open={Boolean(textAlignmentAnchorEl)}
                    onClose={() => setTextAlignmentAnchorEl(null)}
                    slotProps={{ paper: { sx: { py: 0.5, minWidth: 168 } } }}
                  >
                    {[
                      ["Left", "justifyLeft", <FormatAlignLeftRoundedIcon fontSize="small" />],
                      ["Center", "justifyCenter", <FormatAlignCenterRoundedIcon fontSize="small" />],
                      ["Right", "justifyRight", <FormatAlignRightRoundedIcon fontSize="small" />],
                      ["Justify", "justifyFull", <FormatAlignJustifyRoundedIcon fontSize="small" />],
                    ].map(([label, command, icon]) => (
                      <MenuItem
                        key={command}
                        onMouseDown={(event) => {
                          rememberRichTextSelection();
                          event.preventDefault();
                        }}
                        onClick={() => {
                          setTextAlignmentAnchorEl(null);
                          runRichTextCommand(command);
                        }}
                        sx={{ gap: 1, fontSize: 13 }}
                      >
                        {icon}
                        {label}
                      </MenuItem>
                    ))}
                  </Menu>
                  <Tooltip title="Text colour">
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label="Text colour"
                      onMouseDown={(event) => {
                        rememberRichTextSelection();
                        event.preventDefault();
                        setTextColorAnchorEl(event.currentTarget);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setTextColorAnchorEl(event.currentTarget);
                        }
                      }}
                      sx={{ width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#667085" }}
                    >
                      <FormatColorTextRoundedIcon sx={{ fontSize: 17 }} />
                    </Box>
                  </Tooltip>
                  <Menu
                    anchorEl={textColorAnchorEl}
                    open={Boolean(textColorAnchorEl)}
                    onClose={() => setTextColorAnchorEl(null)}
                    slotProps={{ paper: { sx: { p: 0.75, display: "flex", gap: 0.5 } } }}
                  >
                    {RICH_TEXT_COLORS.map((color) => (
                      <MenuItem
                        key={color}
                        aria-label={`Use ${color} text colour`}
                        onMouseDown={(event) => {
                          rememberRichTextSelection();
                          event.preventDefault();
                        }}
                        onClick={() => { setTextColorAnchorEl(null); runRichTextCommand("foreColor", color); }}
                        sx={{ minWidth: 0, p: 0.25 }}
                      >
                        <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: color }} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
              </Box>
              <Box
                ref={richBodyEditorRef}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-label="Email body"
                onMouseUp={rememberRichTextSelection}
                onKeyUp={rememberRichTextSelection}
                onSelect={rememberRichTextSelection}
                onInput={(event) => {
                  const nextBody = event.currentTarget.innerHTML;
                  richBodyLastValueRef.current = nextBody;
                  updateActiveStep({ body: nextBody });
                }}
                sx={{
                  border: "1px solid #98A9C6",
                  borderRadius: 1.25,
                  minHeight: 192,
                  px: 1.5,
                  py: 1.15,
                  color: "#344054",
                  fontSize: 15,
                  lineHeight: 1.42,
                  whiteSpace: "pre-wrap",
                  overflowY: "auto",
                  "&:focus": { outline: `2px solid ${PURPLE}`, outlineOffset: -1 },
                  "& h2": { fontSize: 20, lineHeight: 1.3, my: 0.75 },
                  "& img": { maxWidth: "100%", height: "auto" },
                }}
              />
            </Box>

            <Typography sx={{ color: PURPLE, fontSize: 11.5, fontWeight: 700 }}>
              Preview
            </Typography>

            <Box>
              <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.75 }}>
                Want to see how this email looks in your inbox?
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
                <TextField
                  size="small"
                  value={form.testEmail || ""}
                  onChange={setFormField("testEmail")}
                  sx={fieldSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {!gmailReadyForSend ? (
                    <Button
                      variant="outlined"
                      onClick={handleConnectGmail}
                      disabled={gmailLoading}
                      sx={{
                        minWidth: 126,
                        height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                        borderRadius: 999,
                        textTransform: "none",
                        borderColor: "#A855F7",
                        color: PURPLE,
                        fontSize: 10.5,
                        fontWeight: 700,
                      }}
                    >
                      {gmailLoading
                        ? "Opening Gmail..."
                        : gmailStatus.connected
                          ? "Reconnect Gmail"
                          : "Connect Gmail"}
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    onClick={sendTestEmailPreview}
                    disabled={
                      sendingTestEmail ||
                      gmailLoading ||
                      !gmailReadyForSend ||
                      !normalizeSequenceString(form.testEmail)
                    }
                    endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      minWidth: 104,
                      height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                      borderRadius: 999,
                      textTransform: "none",
                      fontSize: 10.5,
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                      "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" },
                    }}
                  >
                    {sendingTestEmail ? "Sending..." : "Send Test Email"}
                  </Button>
                </Stack>
              </Box>
              {!gmailReadyForSend ? (
                <Typography sx={{ mt: 0.6, fontSize: 10.5, color: "#B42318" }}>
                  {gmailStatus.connected
                    ? "This Gmail account is connected without send permission. Reconnect Gmail once to enable test emails."
                    : "Connect Gmail to enable test emails from this sequence preview."}
                </Typography>
              ) : null}
              <Typography sx={{ mt: 0.6, fontSize: 10, color: "#98A2B3" }}>
                Test emails have "Test" in the subject line. You can send up to 10 per day.
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1.15} sx={{ alignSelf: "start" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Candidate(s)"
              value={recipientSearch}
              onChange={(event) => setRecipientSearch(event.target.value)}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: PURPLE, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                border: "1px solid #D78BFF",
                borderRadius: "10px",
                overflow: "hidden",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1.05 }}>
                <Typography sx={{ color: PURPLE, fontSize: 12.5, fontWeight: 800 }}>
                  Recommended
                </Typography>
                <Typography sx={{ color: "#98A2B3", fontSize: 11.5 }}>
                  Sequencing {recipientRows.length} Candidates
                </Typography>
              </Box>

              <Box sx={{ px: 1.5, pb: 1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "28px minmax(0, 2.05fr) 0.9fr 1fr",
                    gap: 1,
                    alignItems: "center",
                    color: "#667085",
                    fontSize: 10.5,
                    fontWeight: 700,
                    pb: 0.75,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allVisibleRecipientsSelected}
                    indeterminate={someVisibleRecipientsSelected}
                    onChange={toggleVisibleRecipients}
                    sx={{ p: 0 }}
                  />
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>{`Name \u2193`}</Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>{`Status \u2193`}</Typography>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>{`% of Best Matches \u2193`}</Typography>
                </Box>

                <Stack spacing={0} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.25 }}>
                  {filteredRecipientRows.map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "28px minmax(0, 2.05fr) 0.9fr 1fr",
                        gap: 1,
                        alignItems: "center",
                        py: 0.95,
                        minHeight: 52,
                        borderTop: "1px solid #F2F4F7",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedRecipientKeySet.has(row.key)}
                        onChange={() => toggleRecipientSelection(row.key)}
                        sx={{ p: 0 }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Avatar
                          src={row.avatarUrl || undefined}
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 11,
                            bgcolor: "#F4EBFF",
                            color: "#6941C6",
                          }}
                        >
                          {row.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700 }} noWrap>
                            {row.displayName}
                          </Typography>
                          <Typography sx={{ fontSize: 10.5, color: MUTED }} noWrap>
                            {row.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        size="small"
                        label={row.status}
                        sx={{
                          width: "fit-content",
                          height: 18,
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: "#12B76A",
                          backgroundColor: "#ECFDF3",
                        }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        <Box
                          sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: "#EAECF0",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.max(0, Math.min(100, row.bestMatch))}%`,
                              height: "100%",
                              borderRadius: 999,
                              background: "linear-gradient(90deg,#C4B5FD,#7C3AED)",
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475467" }}>
                          {row.bestMatch}%
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pt: 1.05,
                    mt: 0.6,
                    borderTop: "1px solid #EAECF0",
                  }}
                >
                  <Typography sx={{ fontSize: 11.5, color: "#475467" }}>
                    Bulk actions:
                    {" "}
                    <Box component="span" sx={{ color: PURPLE, cursor: "pointer", ml: 0.5 }} onClick={selectAllRecipients}>
                      Select All
                    </Box>
                    {" | "}
                    <Box component="span" sx={{ color: PURPLE, cursor: "pointer" }} onClick={deleteSelectedRecipients}>
                      Delete
                    </Box>
                  </Typography>

                  <Typography sx={{ fontSize: 11.5, color: PURPLE, fontWeight: 700 }}>
                    Show Advanced
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
    )
  );

  const renderPreviewEmail = () => (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 1.2,
          pb: 0.95,
          borderBottom: "1px solid #EAECF0",
        }}
      >
        <Stack direction="row" spacing={2.1} alignItems="flex-end" flexWrap="wrap" useFlexGap>
          {steps.map((step, index) => {
            const isActive = step.id === activeStep?.id;

            return (
              <ButtonBase
                key={step.id}
                onClick={() => setActiveStepId(step.id)}
                sx={{
                  pb: 0.8,
                  borderBottom: `2px solid ${isActive ? PURPLE : "transparent"}`,
                  color: BLUE,
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: 1,
                }}
              >
                {`Step ${index + 1}`}
              </ButtonBase>
            );
          })}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pb: 0.8 }}>
            <ButtonBase
              onClick={addStep}
              sx={{
                color: PURPLE,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              Add Step
            </ButtonBase>
            <Typography sx={{ color: "#98A2B3", fontSize: 14, lineHeight: 1 }}>
              &gt;
            </Typography>
          </Stack>
        </Stack>
        <ButtonBase
          onClick={() => removeStep(activeStep?.id)}
          disabled={steps.length <= 1}
          sx={{
            justifySelf: "end",
            color: steps.length <= 1 ? "#D0D5DD" : "#B692F6",
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          Delete Step
        </ButtonBase>
      </Box>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.2}
        alignItems={{ xs: "stretch", lg: "center" }}
      >
        <Typography sx={{ minWidth: 68, fontSize: 12, color: BLUE, fontWeight: 700 }}>
          Recipients
        </Typography>
        <TextField
          select
          size="small"
          value={previewRecipient?.key || ""}
          onChange={(event) => setPreviewRecipientKey(event.target.value)}
          sx={{ ...fieldSx, minWidth: { xs: "100%", lg: 310 }, maxWidth: { lg: 420 } }}
        >
          {previewRecipients.map((row) => (
            <MenuItem key={row.key} value={row.key}>
              {row.email ? `${row.displayName} <${row.email}>` : row.displayName}
            </MenuItem>
          ))}
        </TextField>

        <Box
          sx={{
            ml: { lg: "auto" },
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", lg: "flex-end" },
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#475467" }}>
            {previewRecipients.length
              ? `${Math.max(previewRecipientIndex, 0) + 1} of ${previewRecipients.length}`
              : "0 of 0"}
          </Typography>
          <Stack direction="row" spacing={0.8}>
            <IconButton
              onClick={() => movePreviewRecipient(-1)}
              disabled={previewRecipientIndex <= 0}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #E4E7EC",
                borderRadius: "10px",
                color: "#98A2B3",
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={() => movePreviewRecipient(1)}
              disabled={
                !previewRecipients.length ||
                previewRecipientIndex < 0 ||
                previewRecipientIndex >= previewRecipients.length - 1
              }
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #D6BBFB",
                borderRadius: "10px",
                color: PURPLE,
              }}
            >
              <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>
      </Stack>

      <Typography data-popup-section sx={{ color: BLUE, fontSize: 24, fontWeight: 900, lineHeight: 1.1 }}>
        Email Preview
      </Typography>

      <Stack spacing={1.2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
          <Typography sx={{ minWidth: 32, fontSize: 12, color: BLUE, fontWeight: 700 }}>
            From
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={activeSenderKey}
            onChange={setActiveStepSender}
            sx={fieldSx}
          >
            {senderOptions.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
          <Typography sx={{ minWidth: 48, fontSize: 12, color: BLUE, fontWeight: 700 }}>
            Subject
          </Typography>
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1fr auto", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={previewSubject}
              InputProps={{ readOnly: true }}
              sx={fieldSx}
            />
            <Typography sx={{ alignSelf: "center", fontSize: 11.5, color: PURPLE, whiteSpace: "nowrap" }}>
              Cc Bcc
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            minHeight: 270,
            border: "1px solid #D0D5DD",
            borderRadius: "10px",
            backgroundColor: "#fff",
            px: { xs: 1.7, sm: 2.2 },
            py: { xs: 1.8, sm: 2.2 },
          }}
        >
          <Box
            component="div"
            sx={{
              color: "#344054",
              fontSize: 13,
              lineHeight: 1.85,
              whiteSpace: "normal",
              "& p:first-of-type": { mt: 0 },
              "& p:last-child": { mb: 0 },
              "& a": { color: BLUE, fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{
              __html: previewHtml || "No preview content available yet.",
            }}
          />
        </Box>
      </Stack>

      <Typography sx={{ fontSize: 11, color: "#98A2B3" }}>
        Preview
      </Typography>

      <Box>
        <Typography sx={{ fontSize: 11.5, color: "#344054", mb: 0.75 }}>
          Want to see how this email looks in your inbox?
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
          <TextField
            size="small"
            value={form.testEmail || ""}
            onChange={setFormField("testEmail")}
            sx={fieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {!gmailReadyForSend ? (
              <Button
                variant="outlined"
                onClick={handleConnectGmail}
                disabled={gmailLoading}
                sx={{
                  minWidth: 126,
                  height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                  borderRadius: 999,
                  textTransform: "none",
                  borderColor: "#A855F7",
                  color: PURPLE,
                  fontSize: 10.5,
                  fontWeight: 700,
                }}
              >
                {gmailLoading
                  ? "Opening Gmail..."
                  : gmailStatus.connected
                    ? "Reconnect Gmail"
                    : "Connect Gmail"}
              </Button>
            ) : null}
            <Button
              variant="contained"
              onClick={sendTestEmailPreview}
              disabled={
                sendingTestEmail ||
                gmailLoading ||
                !gmailReadyForSend ||
                !normalizeSequenceString(form.testEmail)
              }
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{
                minWidth: 132,
                height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                borderRadius: 999,
                textTransform: "none",
                fontSize: 10.5,
                fontWeight: 700,
                background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" },
              }}
            >
              {sendingTestEmail ? "Sending..." : "Send Test Email"}
            </Button>
          </Stack>
        </Box>
        {!gmailReadyForSend ? (
          <Typography sx={{ mt: 0.6, fontSize: 10.5, color: "#B42318" }}>
            {gmailStatus.connected
              ? "This Gmail account is connected without send permission. Reconnect Gmail once to enable test emails."
              : "Connect Gmail to enable test emails from this sequence preview."}
          </Typography>
        ) : null}
        <Typography sx={{ mt: 0.6, fontSize: 10, color: "#98A2B3" }}>
          Test emails have "Test" in the subject line. You can send up to 10 per day.
        </Typography>
      </Box>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={() => setScreen("review")}
          endIcon={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
          sx={{
            minWidth: 126,
            height: SEQUENCE_ACTION_BUTTON_HEIGHT,
            borderRadius: 999,
            textTransform: "none",
            borderColor: "#A855F7",
            color: PURPLE,
            fontSize: 10.5,
            fontWeight: 700,
          }}
        >
          Close Preview
        </Button>
      </Stack>
    </Stack>
  );

  const currentScreen = () => {
    if (screen === "start") return renderStart();
    if (screen === "existing") return renderExisting();
    if (screen === "details") return renderDetails();
    if (screen === "recipients") return renderRecipients();
    if (screen === "steps") return renderSteps();
    if (screen === "audience") return renderAudience();
    if (screen === "preview") return renderPreviewEmail();
    return renderReview();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "calc(100% - 24px)", md: dialogWidth },
            maxWidth: `${dialogWidth}px`,
            borderRadius: 3,
            boxShadow: "0 28px 70px rgba(15, 23, 42, 0.18)",
            "& .MuiButton-root": {
              fontWeight: 400,
            },
            "& .MuiButton-contained:not(.Mui-disabled):hover": {
              color: `${PURPLE} !important`,
              background: "#fff !important",
              border: "1px solid #A855F7 !important",
              boxShadow: "none !important",
            },
            "& .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-startIcon, & .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-endIcon": {
              color: `${PURPLE} !important`,
            },
            "& .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-startIcon svg, & .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-endIcon svg": {
              color: `${PURPLE} !important`,
              fill: "currentColor !important",
            },
            "& .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-startIcon svg path, & .MuiButton-contained:not(.Mui-disabled):hover .MuiButton-endIcon svg path": {
              fill: "currentColor !important",
            },
            "& .sequence-outline-action": {
              borderColor: "#A855F7 !important",
            },
            "& .sequence-outline-action:not(.Mui-disabled):hover": {
              color: `${PURPLE} !important`,
              backgroundColor: "transparent !important",
              borderColor: "#A855F7 !important",
              boxShadow: "none !important",
            },
            "& .MuiButton-outlined:not(.Mui-disabled):hover": {
              color: `${PURPLE} !important`,
              backgroundColor: "transparent !important",
              borderColor: "#A855F7 !important",
              boxShadow: "none !important",
            },
          },
        }}
      >
        <DialogTitle data-popup-header
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            pb: isWizardFormScreen ? 1.25 : 1,
            pt: isWizardFormScreen ? 2 : 2.25,
            px: isWizardFormScreen ? 2.7 : 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {isWizardFormScreen ? (
              <Box sx={{ color: PURPLE, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <AltRouteRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#F3E8FF",
                  color: PURPLE,
                }}
              >
                <AltRouteRoundedIcon sx={{ fontSize: 20 }} />
              </Avatar>
            )}
            <Box>
              <Typography data-popup-title sx={{ fontSize: isWizardFormScreen ? 16 : 18, fontWeight: 900 }}>
                {screen === "start"
                  ? "Create New Campaign"
                  : screen === "details" && mode === "new"
                    ? "Create a New Campaign"
                    : isStepSetupScreen
                      ? "Start from Scratch"
                    : isCampaignInfoScreen
                      ? campaignHeaderTitle
                    : mode === "existing"
                      ? "Add to Existing Sequence"
                      : "Create Sequence"}
              </Typography>
              {isCampaignInfoScreen ? (
                <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.35 }}>
                  <Box component="span" sx={{ color: BLUE, fontWeight: 700 }}>
                    Info:
                  </Box>
                  {" "}
                  {campaignInfoParts.join(" | ")}
                </Typography>
              ) : !isWizardFormScreen ? (
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {sequenceItems.length} selected {entityLabelPlural.toLowerCase()}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton data-popup-close
            onClick={close}
            disabled={saving}
            sx={{
              width: 24,
              height: 24,
              bgcolor: "#A855F7",
              color: "#fff",
              "&:hover": { bgcolor: "#9333EA" },
              "&.Mui-disabled": { bgcolor: "#D8B4FE", color: "#fff" },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: isWizardFormScreen ? 0.5 : 0.75,
            pb: 4,
            px: 4,
          }}
        >
          {screen !== "start" && !(screen === "details" && mode === "new") && !isRecipientsScreen && !isStepSetupScreen && !isAudienceSetupScreen && !isReviewComposerScreen && !isPreviewComposerScreen ? (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
              {(mode === "existing" ? ["existing", "review"] : ["details", "steps", "audience", "review"]).map((value) => {
                const isActive = value === screen;
                return (
                  <Chip
                    key={value}
                    size="small"
                    label={screenLabels[value] || value}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 700,
                      color: isActive ? "#fff" : BLUE,
                      backgroundColor: isActive ? PURPLE : "#EEF2FF",
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
          {currentScreen()}
          {screen === "details" && mode === "new" ? (
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              spacing={1.4}
              sx={{ mt: 4.5 }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{
                  alignSelf: { xs: "stretch", md: "flex-start" },
                  minWidth: 112,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  color: "#fff",
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Back
              </Button>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4}>
                <Button
                  variant="outlined"
                  className="sequence-outline-action"
                  onClick={close}
                  sx={{
                    minWidth: 136,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Delete Sequence
                </Button>
                <Button
                  variant="outlined"
                  className="sequence-outline-action"
                  onClick={() => saveNewSequence("draft")}
                  disabled={saving || !canCreate}
                  sx={{
                    minWidth: 128,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Save Sequence
                </Button>
                <Button
                  variant="contained"
                  onClick={goNext}
                  disabled={nextDisabled}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    minWidth: 114,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                    "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                  }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          ) : isRecipientsScreen ? (
            <Box
              sx={{
                mt: 2.6,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                gap: 1.4,
              }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{
                  justifySelf: { md: "start" },
                  minWidth: 72,
                  height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                  borderRadius: 999,
                  textTransform: "none",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Back
              </Button>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.4}
                sx={{ justifySelf: { md: "center" } }}
              >
                <Button
                  variant="outlined"
                  onClick={close}
                  sx={{
                    minWidth: 104,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  Delete Sequence
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => saveNewSequence("draft")}
                  disabled={saving || !canCreate}
                  sx={{
                    minWidth: 128,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Save Sequence
                </Button>
              </Stack>
              <Button
                variant="contained"
                onClick={goNext}
                disabled={nextDisabled}
                endIcon={preparingAudience ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <ArrowForwardRoundedIcon />}
                sx={{
                  justifySelf: { md: "end" },
                  minWidth: 114,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                {preparingAudience ? "Collecting..." : "Next"}
              </Button>
            </Box>
          ) : isStepSetupScreen ? (
            <Box
              sx={{
                mt: 2.6,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                gap: 1.4,
              }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{
                  justifySelf: { md: "start" },
                  minWidth: 112,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  color: "#fff",
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Back
              </Button>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.4}
                sx={{ justifySelf: { md: "center" } }}
              >
                <Button
                  variant="outlined"
                  onClick={close}
                  sx={{
                    minWidth: 136,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Delete Sequence
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => saveNewSequence("draft")}
                  disabled={saving || !canCreate}
                  sx={{
                    minWidth: 128,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Save Sequence
                </Button>
              </Stack>
              <Button
                variant="contained"
                onClick={goNext}
                disabled={nextDisabled}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  justifySelf: { md: "end" },
                  minWidth: 114,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Next
              </Button>
            </Box>
          ) : isAudienceSetupScreen ? (
            <Box
              sx={{
                mt: 2.6,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                gap: 1.4,
              }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{
                  justifySelf: { md: "start" },
                  minWidth: 112,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  color: "#fff",
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Back
              </Button>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.4}
                sx={{ justifySelf: { md: "center" } }}
              >
                <Button
                  variant="outlined"
                  onClick={close}
                  sx={{
                    minWidth: 136,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Delete Sequence
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => saveNewSequence("draft")}
                  disabled={saving || !canCreate}
                  sx={{
                    minWidth: 128,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Save Sequence
                </Button>
              </Stack>
              <Button
                variant="contained"
                onClick={goNext}
                disabled={nextDisabled}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  justifySelf: { md: "end" },
                  minWidth: 114,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Next
              </Button>
            </Box>
          ) : isReviewComposerScreen ? (
            <Box
              sx={{
                mt: 2.5,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
                alignItems: "center",
                gap: 1.4,
              }}
            >
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                sx={{
                  justifySelf: { md: "start" },
                  minWidth: 112,
                  height: 40,
                  borderRadius: 999,
                  textTransform: "none",
                  color: "#fff",
                  fontWeight: 700,
                  background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                  "&:hover": SEQUENCE_OUTLINE_HOVER_SX,
                }}
              >
                Back
              </Button>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.4}
                sx={{ justifySelf: { md: "center" } }}
              >
                <Button
                  variant="outlined"
                  onClick={close}
                  sx={{
                    minWidth: 136,
                    height: 40,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontWeight: 700,
                  }}
                >
                  Delete Sequence
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => saveNewSequence("draft", { action: "save" })}
                  disabled={saving || !canCreate}
                  sx={{
                    minWidth: 96,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  {saving && saveAction === "save" ? "Saving..." : "Save Sequence"}
                </Button>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.2}
                sx={{ justifySelf: { md: "end" } }}
              >
                <Button
                  variant="outlined"
                  onClick={previewEmail}
                  disabled={saving}
                  sx={{
                    minWidth: 112,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    borderRadius: 999,
                    textTransform: "none",
                    borderColor: "#A855F7",
                    color: PURPLE,
                    fontSize: 10.5,
                    fontWeight: 700,
                  }}
                >
                  Preview Email
                </Button>
                <Button
                  variant="contained"
                  onClick={sendCampaign}
                  disabled={saving || !canSendCampaign}
                  endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    minWidth: 122,
                    height: SEQUENCE_ACTION_BUTTON_HEIGHT,
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: 10.5,
                    fontWeight: 700,
                    background: "linear-gradient(135deg,#8B5CF6,#9333EA)",
                    "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" },
                  }}
                >
                  {saving && saveAction === "send" ? "Sending..." : "Send Campaign"}
                </Button>
              </Stack>
              {!gmailReadyForSend ? (
                <Typography
                  sx={{
                    gridColumn: { xs: "1", md: "3" },
                    justifySelf: { md: "end" },
                    fontSize: 10.5,
                    color: "#B42318",
                  }}
                >
                  {gmailStatus.connected
                    ? "Reconnect Gmail to enable campaign sends."
                    : "Connect Gmail to enable campaign sends."}
                </Typography>
              ) : null}
            </Box>
          ) : isPreviewComposerScreen ? null : screen !== "start" ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, pt: 1.25 }}>
              <Button
                onClick={goBack}
                startIcon={<ArrowBackRoundedIcon />}
                disabled={saving}
                sx={{ textTransform: "none", color: PURPLE, fontWeight: 700 }}
              >
                Back
              </Button>
              <Stack direction="row" spacing={1}>
                {mode === "new" && screen === "review" ? (
                  <Button
                    variant="outlined"
                    onClick={() => saveNewSequence("draft")}
                    disabled={saving || !canCreate}
                    startIcon={<SaveOutlinedIcon />}
                    sx={{ textTransform: "none", borderRadius: 999, borderColor: "#D1D5DB", color: MUTED }}
                  >
                    Save Draft
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  onClick={mode === "existing" && screen === "review" ? saveExistingSequence : mode === "new" && screen === "review" ? () => saveNewSequence(form.status) : goNext}
                  disabled={nextDisabled}
                  startIcon={
                    screen === "review" ? <SequenceActionIcon size={16} /> : null
                  }
                  endIcon={screen === "review" ? null : <ArrowForwardRoundedIcon />}
                  sx={{ textTransform: "none", borderRadius: 999, px: 2.5, background: "linear-gradient(135deg,#8B5CF6,#9333EA)", "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" } }}
                >
                  {saving ? "Saving..." : mode === "existing" && screen === "review" ? "Add to Sequence" : mode === "new" && screen === "review" ? "Create Sequence" : "Next"}
                </Button>
              </Stack>
            </Stack>
          ) : null}
          {screen !== "start" && !(screen === "details" && mode === "new") && !isRecipientsScreen && !isStepSetupScreen && !isAudienceSetupScreen && !isReviewComposerScreen && !isPreviewComposerScreen ? <Divider sx={{ mt: 2 }} /> : null}
        </DialogContent>
      </Dialog>
      {/* Custom Variable Creation Dialog */}
      <Dialog open={customVarDialogOpen} onClose={() => setCustomVarDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle data-popup-header sx={{ fontSize: 14, fontWeight: 700, color: BLUE }}>Create Custom Variable</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 4,
          }}
        >
          <TextField
            label="Variable name"
            size="small"
            value={customVarName}
            onChange={(e) => setCustomVarName(e.target.value)}
            placeholder="e.g. custom_greeting"
            helperText="Letters, numbers, and underscores only"
          />
          <TextField
            label="Default value"
            size="small"
            value={customVarValue}
            onChange={(e) => setCustomVarValue(e.target.value)}
            placeholder="e.g. Hello there"
          />
          <Button
            variant="contained"
            onClick={handleAddCustomVariable}
            disabled={!customVarName.trim() || !customVarValue.trim()}
            sx={{ alignSelf: "flex-end", textTransform: "none", background: "linear-gradient(135deg,#8B5CF6,#9333EA)", "&:hover": { background: "linear-gradient(135deg,#7C3AED,#8B5CF6)" } }}
          >
            Add & Insert
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar open={toast.open} autoHideDuration={3200} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
